/*! Astrolabe Schematics v1.0.0 | Noble Base | schematic motion web components
 * Source: https://claude.ai/code/artifact/dfa597b5-81fe-469f-bdc7-e900c830c2b8
 * FIG. 01 tuned from: https://claude.ai/artifact/59Vw6VgJbRwYeVCNizQSpd
 */
(function () {
'use strict';
if (typeof window === 'undefined' || !window.customElements || window.AstrolabeSchematics) return;
/* src/core/10-math.js */
/* ------------------------------------------------------------------
 * Math and geometry helpers shared by every figure.
 * Screen convention: x right, y down, angles in radians measured
 * clockwise from +x (so the SVG coordinate system needs no flips).
 * ------------------------------------------------------------------ */

const TAU = Math.PI * 2;
const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

const clamp = (v, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
const fract = (x) => x - Math.floor(x);
const mod = (x, m) => ((x % m) + m) % m;
/** Progress of p inside [a, b], clamped to 0..1. */
const seg = (p, a, b) => clamp((p - a) / (b - a));

const ease = {
  linear: (t) => t,
  in: (t) => t * t * t,
  out: (t) => 1 - Math.pow(1 - t, 3),
  inOut: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  inOutQuad: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  outExpo: (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t)),
  inOutSine: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
};

/** Rise over [a,b], hold, fall over [c,d]. Used for build-and-fade loops. */
const envelope = (p, a, b, c, d) => (p < c ? ease.inOut(seg(p, a, b)) : 1 - ease.inOut(seg(p, c, d)));

/** Deterministic PRNG (mulberry32) so star fields are identical everywhere. */
function rng(seed) {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ---------- Orbital mechanics ---------- */

/** Solve Kepler's equation M = E - e sin E for the eccentric anomaly. */
function kepler(M, e) {
  let E = e < 0.8 ? M : Math.PI;
  for (let i = 0; i < 16; i++) {
    const d = (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
    E -= d;
    if (Math.abs(d) < 1e-10) break;
  }
  return E;
}

/**
 * Position on a Keplerian ellipse, focus at the origin, counter-clockwise
 * on screen (as seen from the north). `w` rotates the line of apsides.
 */
function orbitXY(a, e, M, w = 0) {
  const E = kepler(M, e);
  const x = a * (Math.cos(E) - e);
  const y = -a * Math.sqrt(1 - e * e) * Math.sin(E);
  const c = Math.cos(w), s = Math.sin(w);
  return [x * c - y * s, x * s + y * c];
}

/* ---------- Stereographic projection (astrolabe plates) ---------- */

/** Radius of a declination circle, projected from the south pole. */
const stereoR = (Req, decDeg) => Req * Math.tan(((90 - decDeg) * D2R) / 2);

/**
 * Almucantar (circle of equal altitude h) for latitude phi.
 * Returns centre offset `c` along the meridian (positive = toward the
 * zenith side, which is drawn upward) and radius `r`.
 */
function almucantar(Req, phi, h) {
  const ys = stereoR(Req, phi + h - 90);
  const yn = h < phi ? -stereoR(Req, 90 - phi + h) : stereoR(Req, phi + 90 - h);
  return { c: (ys + yn) / 2, r: Math.abs(ys - yn) / 2 };
}

/** Azimuth circle through zenith and nadir, making angle A with the meridian. */
function azimuthCircle(Req, phi, Adeg) {
  const yz = stereoR(Req, phi);
  const yn = -stereoR(Req, -phi);
  const ym = (yz + yn) / 2;
  const d = (yz - yn) / 2;
  const xc = Math.abs(Adeg - 90) < 1e-6 ? 0 : d / Math.tan(Adeg * D2R);
  return { x: xc, c: ym, r: Math.hypot(xc, d) };
}

/* ---------- 3D (orthographic) ---------- */

const m3 = {
  mul(a, b) {
    const r = new Array(9);
    for (let i = 0; i < 3; i++)
      for (let j = 0; j < 3; j++)
        r[i * 3 + j] = a[i * 3] * b[j] + a[i * 3 + 1] * b[3 + j] + a[i * 3 + 2] * b[6 + j];
    return r;
  },
  x(a) { const c = Math.cos(a), s = Math.sin(a); return [1, 0, 0, 0, c, -s, 0, s, c]; },
  y(a) { const c = Math.cos(a), s = Math.sin(a); return [c, 0, s, 0, 1, 0, -s, 0, c]; },
  z(a) { const c = Math.cos(a), s = Math.sin(a); return [c, -s, 0, s, c, 0, 0, 0, 1]; },
  apply(m, v) {
    return [
      m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
      m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
      m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
    ];
  },
};

/**
 * Sample a circle in 3D. `u`, `v` are orthonormal basis vectors of the
 * circle's plane, `c` its centre. Returns [x, y, z] points.
 */
function circle3(c, u, v, r, n = 96, a0 = 0, a1 = TAU) {
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    const ca = Math.cos(a) * r, sa = Math.sin(a) * r;
    pts.push([c[0] + u[0] * ca + v[0] * sa, c[1] + u[1] * ca + v[1] * sa, c[2] + u[2] * ca + v[2] * sa]);
  }
  return pts;
}

/**
 * Split a projected polyline into runs that are in front of / behind a
 * surface, so hidden portions can be drawn dashed. `front(p)` decides.
 */
function splitRuns(pts, front) {
  const runs = [];
  let cur = null;
  for (let i = 0; i < pts.length; i++) {
    const f = front(pts[i]);
    if (!cur || cur.front !== f) {
      const prev = cur && cur.pts[cur.pts.length - 1];
      cur = { front: f, pts: prev ? [prev] : [] };
      runs.push(cur);
    }
    cur.pts.push(pts[i]);
  }
  return runs;
}

/* ---------- Plane geometry ---------- */

/** External (umbra) and internal (penumbra) tangent lines of two circles. */
function tangents(c1, r1, c2, r2) {
  const dx = c2[0] - c1[0], dy = c2[1] - c1[1];
  const d = Math.hypot(dx, dy);
  const out = { ext: [], int: [] };
  const base = Math.atan2(dy, dx);
  for (const kind of ['ext', 'int']) {
    const rr = kind === 'ext' ? r1 - r2 : r1 + r2;
    if (d <= Math.abs(rr)) continue;
    const a = Math.acos(rr / d);
    for (const sgn of [1, -1]) {
      const ang = base + sgn * a;
      const n = [Math.cos(ang), Math.sin(ang)];
      const k = kind === 'ext' ? 1 : -1;
      out[kind].push([
        [c1[0] + n[0] * r1, c1[1] + n[1] * r1],
        [c2[0] + k * n[0] * r2, c2[1] + k * n[1] * r2],
      ]);
    }
  }
  return out;
}

/** Intersection points of two circles, or null. */
function ccx(c1, r1, c2, r2) {
  const dx = c2[0] - c1[0], dy = c2[1] - c1[1], d = Math.hypot(dx, dy);
  if (!d || d > r1 + r2 || d < Math.abs(r1 - r2)) return null;
  const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
  const hh = Math.sqrt(Math.max(0, r1 * r1 - a * a));
  const mx = c1[0] + (a * dx) / d, my = c1[1] + (a * dy) / d;
  return [[mx + (hh * dy) / d, my - (hh * dx) / d], [mx - (hh * dy) / d, my + (hh * dx) / d]];
}

/** The arc [a0, a1] of circle (c, r) that lies inside circle (C, R); null if none. */
function arcInside(c, r, C, R) {
  const pts = ccx(c, r, C, R);
  if (!pts) {
    return Math.hypot(c[0] - C[0], c[1] - C[1]) + r <= R + 1e-6 ? [-Math.PI / 2, 1.5 * Math.PI] : null;
  }
  let a0 = Math.atan2(pts[0][1] - c[1], pts[0][0] - c[0]);
  let a1 = Math.atan2(pts[1][1] - c[1], pts[1][0] - c[0]);
  if (a1 < a0) a1 += TAU;
  const m = [c[0] + r * Math.cos((a0 + a1) / 2), c[1] + r * Math.sin((a0 + a1) / 2)];
  if (Math.hypot(m[0] - C[0], m[1] - C[1]) > R) { const t = a0; a0 = a1; a1 = t + TAU; }
  return [a0, a1];
}

/** Marching squares: iso-lines of a sampled scalar field, as segment lists. */
function isolines(field, nx, ny, x0, y0, dx, dy, level) {
  const segs = [];
  const v = (i, j) => field[j * nx + i];
  const interp = (a, b, va, vb) => a + ((level - va) / (vb - va)) * (b - a);
  for (let j = 0; j < ny - 1; j++) {
    for (let i = 0; i < nx - 1; i++) {
      const a = v(i, j), b = v(i + 1, j), c = v(i + 1, j + 1), d = v(i, j + 1);
      let idx = 0;
      if (a > level) idx |= 1;
      if (b > level) idx |= 2;
      if (c > level) idx |= 4;
      if (d > level) idx |= 8;
      if (idx === 0 || idx === 15) continue;
      const X = x0 + i * dx, Y = y0 + j * dy;
      const top = [interp(X, X + dx, a, b), Y];
      const right = [X + dx, interp(Y, Y + dy, b, c)];
      const bottom = [interp(X, X + dx, d, c), Y + dy];
      const left = [X, interp(Y, Y + dy, a, d)];
      const add = (p, q) => segs.push([p, q]);
      switch (idx) {
        case 1: case 14: add(left, top); break;
        case 2: case 13: add(top, right); break;
        case 3: case 12: add(left, right); break;
        case 4: case 11: add(right, bottom); break;
        case 6: case 9: add(top, bottom); break;
        case 7: case 8: add(left, bottom); break;
        case 5: add(left, top); add(right, bottom); break;
        case 10: add(top, right); add(left, bottom); break;
      }
    }
  }
  return joinSegments(segs);
}

/** Join loose segments into polylines (for smooth, drawable contours). */
function joinSegments(segs) {
  const key = (p) => Math.round(p[0] * 1000) + ',' + Math.round(p[1] * 1000);
  const map = new Map();
  segs.forEach((s, i) => {
    for (const end of [0, 1]) {
      const k = key(s[end]);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(i);
    }
  });
  const used = new Uint8Array(segs.length);
  const lines = [];
  for (let i = 0; i < segs.length; i++) {
    if (used[i]) continue;
    used[i] = 1;
    const line = [segs[i][0], segs[i][1]];
    for (const dir of [1, 0]) {
      let grow = true;
      while (grow) {
        grow = false;
        const endPt = dir ? line[line.length - 1] : line[0];
        const cands = map.get(key(endPt)) || [];
        for (const c of cands) {
          if (used[c]) continue;
          used[c] = 1;
          const s = segs[c];
          const next = key(s[0]) === key(endPt) ? s[1] : s[0];
          if (dir) line.push(next); else line.unshift(next);
          grow = true;
          break;
        }
      }
    }
    lines.push(line);
  }
  return lines;
}

/** Total length of a polyline and a partial copy up to fraction t. */
function polyLength(pts) {
  let L = 0;
  for (let i = 1; i < pts.length; i++) L += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  return L;
}
function polyPartial(pts, t) {
  if (t >= 1) return pts;
  const L = polyLength(pts) * t;
  const out = [pts[0]];
  let acc = 0;
  for (let i = 1; i < pts.length; i++) {
    const l = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    if (acc + l >= L) {
      const k = l ? (L - acc) / l : 0;
      out.push([lerp(pts[i - 1][0], pts[i][0], k), lerp(pts[i - 1][1], pts[i][1], k)]);
      return out;
    }
    acc += l;
    out.push(pts[i]);
  }
  return out;
}

/** Convert right ascension (hours) and declination (degrees) to a unit vector. */
function radec(raH, decD) {
  const a = raH * 15 * D2R, d = decD * D2R;
  return [Math.cos(d) * Math.cos(a), Math.cos(d) * Math.sin(a), Math.sin(d)];
}

/* src/core/20-kit.js */
/* ------------------------------------------------------------------
 * Drawing kit. Path builders are pure functions; `makeKit` binds the
 * drafting conventions (pens, lettering, leaders, dimensions) to one
 * build of one figure at one rendered size.
 * ------------------------------------------------------------------ */

const NS = 'http://www.w3.org/2000/svg';
const n2 = (x) => Math.round(x * 100) / 100;

function h(tag, attrs, parent) {
  const e = document.createElementNS(NS, tag);
  if (attrs) {
    for (const k in attrs) {
      const v = attrs[k];
      if (v == null || v === false) continue;
      e.setAttribute(k, v === true ? '' : v);
    }
  }
  if (parent) parent.appendChild(e);
  return e;
}

const P = {
  line: (x1, y1, x2, y2) => `M${n2(x1)} ${n2(y1)}L${n2(x2)} ${n2(y2)}`,
  poly(pts, close) {
    if (!pts.length) return '';
    let d = `M${n2(pts[0][0])} ${n2(pts[0][1])}`;
    for (let i = 1; i < pts.length; i++) d += `L${n2(pts[i][0])} ${n2(pts[i][1])}`;
    return close ? d + 'Z' : d;
  },
  circle(cx, cy, r) {
    return `M${n2(cx - r)} ${n2(cy)}a${n2(r)} ${n2(r)} 0 1 0 ${n2(2 * r)} 0a${n2(r)} ${n2(r)} 0 1 0 ${n2(-2 * r)} 0`;
  },
  ellipse(cx, cy, rx, ry) {
    return `M${n2(cx - rx)} ${n2(cy)}a${n2(rx)} ${n2(ry)} 0 1 0 ${n2(2 * rx)} 0a${n2(rx)} ${n2(ry)} 0 1 0 ${n2(-2 * rx)} 0`;
  },
  /** Circular arc from a0 to a1 (radians, clockwise on screen). */
  arc(cx, cy, r, a0, a1) {
    const d = a1 - a0;
    if (Math.abs(d) >= TAU - 1e-6) return P.circle(cx, cy, r);
    if (Math.abs(d) < 1e-6) return '';
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    return `M${n2(x0)} ${n2(y0)}A${n2(r)} ${n2(r)} 0 ${Math.abs(d) > Math.PI ? 1 : 0} ${d > 0 ? 1 : 0} ${n2(x1)} ${n2(y1)}`;
  },
  /** Elliptical arc (axis-aligned), sampled, for tilted views. */
  earc(cx, cy, rx, ry, a0, a1, n = 48) {
    const pts = [];
    for (let i = 0; i <= n; i++) {
      const a = a0 + ((a1 - a0) * i) / n;
      pts.push([cx + rx * Math.cos(a), cy + ry * Math.sin(a)]);
    }
    return P.poly(pts);
  },
  /** Radial tick marks between radii r1..r2, n divisions over [a0, a1]. */
  ticks(cx, cy, r1, r2, a0, a1, n, every = 1, r2major = r2) {
    let d = '';
    const full = Math.abs(a1 - a0) >= TAU - 1e-6;
    const count = full ? n : n + 1;
    for (let i = 0; i < count; i++) {
      const a = a0 + ((a1 - a0) * i) / n;
      const R2 = i % every === 0 ? r2major : r2;
      const c = Math.cos(a), s = Math.sin(a);
      d += `M${n2(cx + r1 * c)} ${n2(cy + r1 * s)}L${n2(cx + R2 * c)} ${n2(cy + R2 * s)}`;
    }
    return d;
  },
  rect: (x, y, w, hh) => `M${n2(x)} ${n2(y)}h${n2(w)}v${n2(hh)}h${n2(-w)}Z`,
};

const polar = (cx, cy, r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];

/** Class rules outrank presentation attributes, so pattern fills go inline. */
function fillStyle(el, attrs) {
  if (attrs && attrs.fill && attrs.fill !== '#000' && attrs.fill !== '#fff') {
    el.removeAttribute('fill');
    el.style.fill = attrs.fill;
  }
  return el;
}

/* ---------- label knockout geometry ---------- */
const tfMul = (A, B) => [A[0] * B[0] + A[2] * B[1], A[1] * B[0] + A[3] * B[1], A[0] * B[2] + A[2] * B[3],
  A[1] * B[2] + A[3] * B[3], A[0] * B[4] + A[2] * B[5] + A[4], A[1] * B[4] + A[3] * B[5] + A[5]];
function parseTf(str) {
  let M = [1, 0, 0, 1, 0, 0];
  if (!str) return M;
  const re = /(matrix|translate|scale|rotate)\s*\(([^)]*)\)/g;
  let m;
  while ((m = re.exec(str))) {
    const v = m[2].split(/[\s,]+/).filter(Boolean).map(Number);
    let T;
    if (m[1] === 'translate') T = [1, 0, 0, 1, v[0] || 0, v[1] || 0];
    else if (m[1] === 'scale') T = [v[0], 0, 0, v[1] == null ? v[0] : v[1], 0, 0];
    else if (m[1] === 'rotate') {
      const a = (v[0] || 0) * D2R, c = Math.cos(a), sn = Math.sin(a), cx = v[1] || 0, cy = v[2] || 0;
      T = [c, sn, -sn, c, cx - c * cx + sn * cy, cy - sn * cx - c * cy];
    } else T = v;
    M = tfMul(M, T);
  }
  return M;
}
/** Opacity of a label after its own and its twin ancestors' fades. */
function inkOpacity(el, stop) {
  let op = 1;
  for (let n = el; n && n !== stop; n = n.parentNode) {
    const st = n.style;
    if (st.visibility === 'hidden' || st.display === 'none') return 0;
    if (st.opacity !== '') op *= parseFloat(st.opacity);
  }
  return op;
}
/** Estimated glyph box of a label, mapped through its transforms. */
function labelHole(it, stop) {
  const el = it.el, fs = it.fs, str = el.textContent;
  const ls = /\btt\b/.test(it.cls) ? 0.1 : /\bts\b/.test(it.cls) ? 0.07 : /\btn\b/.test(it.cls) ? 0.01 : 0.08;
  let w = 0;
  for (const ch of str) {
    w += (/[A-Z0-9#%]/.test(ch) ? 0.66 : /[a-z]/.test(ch) ? 0.53 : ch === ' ' ? 0.3 : /[.,:·′'°]/.test(ch) ? 0.32 : 0.62) + ls;
  }
  w *= fs;
  const x = parseFloat(el.getAttribute('x')) || 0, y = parseFloat(el.getAttribute('y')) || 0;
  const anchor = el.getAttribute('text-anchor');
  const pad = fs * 0.32;
  const x0 = (anchor === 'middle' ? x - w / 2 : anchor === 'end' ? x - w : x) - pad;
  const x1 = x0 + w + 2 * pad;
  const y0 = y - fs * 0.86, y1 = y + fs * 0.34;
  const chain = [];
  for (let n = el; n && n !== stop; n = n.parentNode) chain.push(n.getAttribute('transform'));
  let M = [1, 0, 0, 1, 0, 0];
  for (let i = chain.length - 1; i >= 0; i--) M = tfMul(M, parseTf(chain[i]));
  const pt = (px, py) => [M[0] * px + M[2] * py + M[4], M[1] * px + M[3] * py + M[5]];
  return P.poly([pt(x0, y0), pt(x1, y0), pt(x1, y1), pt(x0, y1)], true);
}

/**
 * Bind the kit to one build. `o` carries: pu (user units per pen unit),
 * tu (user units per lettering unit), detail, labels.
 */
function makeKit(svg, o) {
  let uid = 0;
  const id = (p) => `${p}${++uid}`;
  const defs = h('defs', null, svg);
  const root = h('g', { class: 'root' }, svg);
  const pu = o.pu, tu = o.tu;

  /* Lettering lives in a twin tree above the linework, and a mask cuts
     the linework under every label. The knockout needs no paper color,
     so labels stay legible on any ground and in every export. */
  const letters = h('g', { class: 'letters' }, svg);
  const twins = new Map();
  const texts = [];
  let holes = null;
  const twinOf = (p) => {
    if (!p || p === root) return letters;
    if (p === letters || letters.contains(p)) return p;
    let t = twins.get(p);
    if (!t) {
      t = h('g', null, twinOf(p.parentNode));
      twins.set(p, t);
    }
    return t;
  };
  const ensureMask = () => {
    if (holes) return;
    const m = h('mask', { id: 'lm', maskUnits: 'userSpaceOnUse', x: -800, y: -800, width: 2100, height: 2000 }, defs);
    h('rect', { x: -800, y: -800, width: 2100, height: 2000, fill: '#fff' }, m);
    holes = h('g', null, m);
    root.setAttribute('mask', 'url(#lm)');
  };

  const arrow = (mid, cls) => {
    const m = h('marker', {
      id: mid, viewBox: '0 0 10 10', refX: 9.2, refY: 5,
      markerWidth: n2(6.6 * pu), markerHeight: n2(6.6 * pu),
      markerUnits: 'userSpaceOnUse', orient: 'auto-start-reverse',
    }, defs);
    h('path', { d: 'M0 1.2L10 5L0 8.8Z', class: cls }, m);
  };
  arrow('ar', 'fi');
  arrow('aa', 'fa');

  const k = {
    svg, defs, root, pu, tu,
    detail: o.detail,
    full: o.detail === 'full',
    mark: o.detail === 'mark',
    L: o.detail === 'full' && o.labels,
    uid: id,

    g(parent, cls, attrs) {
      return h('g', Object.assign(cls ? { class: cls } : {}, attrs || {}), parent || root);
    },
    path(parent, d, cls, attrs) {
      return fillStyle(h('path', Object.assign({ d, class: cls || 'm' }, attrs || {}), parent || root), attrs);
    },
    /** A solid path that can be drawn on with k.draw(). */
    dpath(parent, d, cls, attrs) {
      return fillStyle(h('path', Object.assign({ d, class: cls || 'm', pathLength: 1 }, attrs || {}), parent || root), attrs);
    },
    set(el, d) { el.setAttribute('d', d); return el; },
    draw(el, t) {
      if (t <= 0.0005) { el.style.visibility = 'hidden'; return; }
      el.style.visibility = '';
      el.style.strokeDasharray = t >= 0.9995 ? '' : `${t} 1`;
    },
    fade(el, a) {
      el.style.opacity = a >= 0.999 ? '' : String(n2(Math.max(0, a)));
      el.style.visibility = a <= 0.001 ? 'hidden' : '';
    },
    move(el, x, y, rot, s) {
      let tr = `translate(${n2(x)} ${n2(y)})`;
      if (rot) tr += ` rotate(${n2(rot * R2D)})`;
      if (s != null && s !== 1) tr += ` scale(${n2(s)})`;
      el.setAttribute('transform', tr);
    },
    rotate(el, a, cx = 0, cy = 0) {
      el.setAttribute('transform', `rotate(${n2(a * R2D)} ${n2(cx)} ${n2(cy)})`);
    },

    dot(parent, x, y, r = 1.7, cls = 'fi') {
      return h('circle', { cx: n2(x), cy: n2(y), r: n2(r * pu), class: cls }, parent || root);
    },
    ring(parent, x, y, r = 3, cls = 'f') {
      return h('circle', { cx: n2(x), cy: n2(y), r: n2(r * pu), class: cls }, parent || root);
    },
    /** Circle whose radius is in drawing units (a body, not a mark). */
    body(parent, x, y, r, cls = 'm') {
      return h('circle', { cx: n2(x), cy: n2(y), r: n2(r), class: cls }, parent || root);
    },
    /** Star glyph sized by visual magnitude. */
    star(parent, x, y, mag = 2, cls = 'fi') {
      const r = Math.max(0.55, 2.5 - 0.42 * mag) * pu;
      return h('circle', { cx: n2(x), cy: n2(y), r: n2(r), class: cls }, parent || root);
    },
    /** Centre mark: the drafted cross used on every pivot. */
    centre(parent, x, y, size = 6, cls = 'f') {
      const s = size * pu;
      return h('path', { d: P.line(x - s, y, x + s, y) + P.line(x, y - s, x, y + s), class: cls }, parent || root);
    },

    /** Lettering. Uppercase unless `tn` is in cls. Vertically centred on y. */
    text(parent, x, y, str, cls = 't', align = 'start') {
      const size = /\btt\b/.test(cls) ? 10.5 : /\bts\b/.test(cls) ? 7.6 : 9;
      ensureMask();
      const e = h('text', {
        x: n2(x), y: n2(y + size * tu * 0.35), class: cls,
        'text-anchor': align === 'middle' ? 'middle' : align === 'end' ? 'end' : null,
      }, twinOf(parent || root));
      e.textContent = /\btn\b/.test(cls) ? str : String(str).toUpperCase();
      texts.push({ el: e, hole: h('path', { d: '', fill: '#000' }, holes), fs: size * tu, cls });
      return e;
    },
    /** Empty a group (and its lettering twin) before redrawing into it. */
    clear(g) {
      g.textContent = '';
      const t = twins.get(g);
      if (t) t.textContent = '';
    },
    /** Called after every frame: sync twins, then recut the label holes. */
    afterFrame() {
      if (!holes) return;
      for (const [og, tg] of twins) {
        const tr = og.getAttribute('transform');
        if (tr) tg.setAttribute('transform', tr); else tg.removeAttribute('transform');
        if (tg.style.cssText !== og.style.cssText) tg.style.cssText = og.style.cssText;
      }
      for (let i = texts.length - 1; i >= 0; i--) {
        const it = texts[i];
        if (!it.el.parentNode) { it.hole.remove(); texts.splice(i, 1); continue; }
        const op = inkOpacity(it.el, letters);
        if (op <= 0.01 || !it.el.textContent) { it.hole.setAttribute('d', ''); continue; }
        it.hole.setAttribute('d', labelHole(it, letters));
        it.hole.setAttribute('fill-opacity', String(n2(Math.min(1, op * 1.4))));
      }
    },
    retext(el, str) { el.textContent = str; },

    /**
     * Leader line: dot on the part, straight run to the knee, a short
     * shoulder, then the label. Returns the group so callers can fade it.
     */
    leader(parent, a, knee, label, opt = {}) {
      const g = h('g', { class: 'ld' }, parent || root);
      const side = opt.side || (knee[0] >= a[0] ? 'r' : 'l');
      const sh = (opt.shoulder == null ? 9 : opt.shoulder) * tu;
      const ex = side === 'r' ? knee[0] + sh : knee[0] - sh;
      h('path', { d: P.poly([a, knee, [ex, knee[1]]]), class: opt.cls || 'dm' }, g);
      if (opt.dot !== false) h('circle', { cx: n2(a[0]), cy: n2(a[1]), r: n2(1.35 * pu), class: opt.dotCls || 'fi' }, g);
      if (label != null) {
        const tx = side === 'r' ? ex + 3 * tu : ex - 3 * tu;
        g.label = k.text(g, tx, knee[1], label, opt.textCls || 't', side === 'r' ? 'start' : 'end');
      }
      return g;
    },

    /** Linear dimension between p1 and p2, offset perpendicular by `off`. */
    dim(parent, p1, p2, off, label, opt = {}) {
      const g = h('g', { class: 'dg' }, parent || root);
      const dx = p2[0] - p1[0], dy = p2[1] - p1[1];
      const L = Math.hypot(dx, dy) || 1;
      const nx = -dy / L, ny = dx / L;
      const q1 = [p1[0] + nx * off, p1[1] + ny * off];
      const q2 = [p2[0] + nx * off, p2[1] + ny * off];
      const over = 3 * pu * Math.sign(off || 1), gap = 2 * pu * Math.sign(off || 1);
      if (opt.ext !== false) {
        h('path', {
          d: P.line(p1[0] + nx * gap, p1[1] + ny * gap, q1[0] + nx * over, q1[1] + ny * over) +
             P.line(p2[0] + nx * gap, p2[1] + ny * gap, q2[0] + nx * over, q2[1] + ny * over),
          class: 'dm',
        }, g);
      }
      h('path', { d: P.line(q1[0], q1[1], q2[0], q2[1]), class: 'dm', 'marker-start': 'url(#ar)', 'marker-end': 'url(#ar)' }, g);
      if (label != null) {
        let ang = Math.atan2(dy, dx) * R2D;
        if (ang > 90) ang -= 180;
        if (ang < -90) ang += 180;
        const mx = (q1[0] + q2[0]) / 2, my = (q1[1] + q2[1]) / 2;
        const up = 6 * tu * (opt.below ? -1 : 1);
        const rad = ang * D2R;
        const tx = mx + Math.sin(rad) * up, ty = my - Math.cos(rad) * up;
        const t = k.text(g, tx, ty, label, opt.textCls || 't', 'middle');
        if (Math.abs(ang) > 0.5) t.setAttribute('transform', `rotate(${n2(ang)} ${n2(tx)} ${n2(ty)})`);
        g.label = t;
      }
      return g;
    },

    /** Angular dimension: arc with arrowheads and a label at mid-angle. */
    dimAngle(parent, cx, cy, r, a0, a1, label, opt = {}) {
      const g = h('g', { class: 'dg' }, parent || root);
      h('path', { d: P.arc(cx, cy, r, a0, a1), class: opt.cls || 'dm', 'marker-start': opt.noStart ? null : 'url(#ar)', 'marker-end': 'url(#ar)' }, g);
      if (label != null) {
        const am = (a0 + a1) / 2;
        const rr = r + (opt.inside ? -8 : 8) * tu;
        const [tx, ty] = polar(cx, cy, rr, am);
        const c = Math.cos(am);
        g.label = k.text(g, tx, ty, label, opt.textCls || 't', Math.abs(c) < 0.35 ? 'middle' : c > 0 ? 'start' : 'end');
      }
      return g;
    },

    /** Clip path from a path string; returns the url() reference. */
    clip(d) {
      const cid = id('cp');
      const cp = h('clipPath', { id: cid }, defs);
      cp.path = h('path', { d }, cp);
      return { ref: `url(#${cid})`, el: cp.path };
    },
    /** Mask (white keeps, black removes). Returns { ref, g } to draw into. */
    mask(bounds) {
      const mid = id('mk');
      const m = h('mask', { id: mid, maskUnits: 'userSpaceOnUse', x: bounds[0], y: bounds[1], width: bounds[2], height: bounds[3] }, defs);
      h('rect', { x: bounds[0], y: bounds[1], width: bounds[2], height: bounds[3], fill: '#fff' }, m);
      return { ref: `url(#${mid})`, g: m };
    },
    /** Section hatching as a pattern fill. */
    hatch(angleDeg = 45, gap = 4, cls = 'f') {
      const hid = id('ht');
      const pat = h('pattern', {
        id: hid, patternUnits: 'userSpaceOnUse', width: n2(gap * pu), height: n2(gap * pu),
        patternTransform: `rotate(${angleDeg})`,
      }, defs);
      h('path', { d: P.line((gap * pu) / 2, 0, (gap * pu) / 2, gap * pu), class: cls }, pat);
      return `url(#${hid})`;
    },
  };
  return k;
}

/* src/core/30-element.js */
/* ------------------------------------------------------------------
 * <astrolabe-*> base element: one shared clock, size-aware level of
 * detail, themeable ink, reduced-motion stills, and exporters.
 * ------------------------------------------------------------------ */

const VERSION = '1.0.0';
const SHEET = [0, 0, 480, 360];
const FRAMED = [-16, -16, 512, 432];

const BASE_CSS = `
.o,.m,.f,.h,.hh,.c,.hd,.dm,.dt{fill:none;stroke:var(--ink);stroke-linecap:round;stroke-linejoin:round}
.o{stroke-width:calc(var(--pu)*1.55px)}
.m{stroke-width:calc(var(--pu)*1.05px)}
.f{stroke-width:calc(var(--pu)*.72px)}
.h{stroke-width:calc(var(--pu)*.5px);opacity:.46}
.hh{stroke-width:calc(var(--pu)*.5px);opacity:.26}
.c{stroke-width:calc(var(--pu)*.55px);stroke-dasharray:calc(var(--pu)*10px) calc(var(--pu)*2.6px) calc(var(--pu)*1.2px) calc(var(--pu)*2.6px);opacity:.6}
.hd{stroke-width:calc(var(--pu)*.72px);stroke-dasharray:calc(var(--pu)*3.4px) calc(var(--pu)*2.4px);opacity:.55}
.dt{stroke-width:calc(var(--pu)*1.05px);stroke-dasharray:0 calc(var(--pu)*3.6px)}
.dm{stroke-width:calc(var(--pu)*.55px);opacity:.8}
.a{stroke:var(--acc)}
.fi{fill:var(--ink);stroke:none}
.fa{fill:var(--acc);stroke:none}
.fp{fill:var(--paper)}
.fs{fill:var(--ink);fill-opacity:.07}
.fsa{fill:var(--acc);fill-opacity:.13}
.t{fill:var(--ink);stroke:none;font-family:var(--schematic-font,ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif);font-size:calc(var(--tu)*9px);font-weight:500;letter-spacing:.08em;font-variant-numeric:tabular-nums}
.ts{font-size:calc(var(--tu)*7.6px);letter-spacing:.07em}
.tt{font-size:calc(var(--tu)*10.5px);font-weight:600;letter-spacing:.1em}
.tn{letter-spacing:.01em}
.ta{fill:var(--acc)}
.q{opacity:.55}
/* marks: one pen for every figure, so a 40px state reads as one family */
.mk .o,.mk .m,.mk .f,.mk .h,.mk .hh,.mk .c,.mk .hd,.mk .dm,.mk .dt{stroke-width:calc(var(--pu)*1.3px)}
.mk .h,.mk .hh{opacity:.5}
.nsg{--pu:var(--ps)}
.nsg path,.nsg circle,.nsg ellipse{vector-effect:non-scaling-stroke}
`;

const HOST_CSS = `
:host{display:block;position:relative;width:100%;contain:layout paint;-webkit-user-select:none;user-select:none}
:host([hidden]){display:none}
svg{display:block;width:100%;height:100%;overflow:visible;--ink:var(--schematic-ink,currentColor);--acc:var(--schematic-accent,#0466F4);--paper:var(--schematic-paper,transparent);--pu:1;--tu:1;--ps:1}
`;

/* ---------- One clock for every instance on the page ---------- */
const Clock = {
  items: new Set(),
  raf: 0,
  last: 0,
  add(x) {
    this.items.add(x);
    this.start();
  },
  remove(x) { this.items.delete(x); },
  start() {
    if (!this.raf && this.items.size && !document.hidden) {
      this.last = performance.now();
      this.raf = requestAnimationFrame(Clock.tick);
    }
  },
  tick(now) {
    if (document.hidden) { Clock.raf = 0; return; }
    const dt = Math.min(0.1, Math.max(0, (now - Clock.last) / 1000));
    Clock.last = now;
    for (const x of Clock.items) x._tick(dt);
    Clock.raf = Clock.items.size ? requestAnimationFrame(Clock.tick) : 0;
  },
};
document.addEventListener('visibilitychange', () => Clock.start());

const REDUCED = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
const LIVE = new Set();
if (REDUCED.addEventListener) REDUCED.addEventListener('change', () => LIVE.forEach((el) => el._motionChanged()));

const ro = typeof ResizeObserver === 'function' ? new ResizeObserver((entries) => {
  for (const e of entries) e.target._resize(e.contentBoxSize ? e.contentBoxSize[0].inlineSize : e.contentRect.width);
}) : {
  observe(el) { el._resize(el.getBoundingClientRect().width); },
  unobserve() {},
};
const io = typeof IntersectionObserver === 'function' ? new IntersectionObserver((entries) => {
  for (const e of entries) e.target._visible = e.isIntersecting;
}, { rootMargin: '160px' }) : {
  observe(el) { el._visible = true; },
  unobserve() {},
};
if (typeof ResizeObserver !== 'function') {
  window.addEventListener('resize', () => LIVE.forEach((el) => el._resize(el.getBoundingClientRect().width)));
}

/** Screen-size factors [pen, lettering] per level of detail. */
function penScale(detail, w) {
  if (detail === 'mark') { const m = clamp(0.9 + w / 120, 1.1, 1.8); return [m, m]; }
  if (detail === 'figure') { const f = clamp(w / 360, 0.9, 1.25); return [f, f]; }
  return [clamp(w / 600, 1, 1.55), clamp(w / 500, 1, 1.6)];
}

const BASE_ATTRS = ['speed', 'paused', 'time', 'ink', 'accent', 'paper', 'stroke', 'detail', 'labels', 'frame', 'label', 'decorative'];

/** Drafting-sheet border and title strip, for decks and share cards. */
function drawFrame(k, def) {
  const g = k.g(null, 'frame');
  const [x, y, w, hh] = [-10, -10, 500, 420];
  k.path(g, P.rect(x, y, w, hh), 'o');
  k.path(g, P.line(x, 382, x + w, 382), 'f');
  k.path(g, P.line(78, 382, 78, y + hh) + P.line(318, 382, 318, y + hh), 'f');
  // centring marks at the middle of each edge
  const cm = 5;
  k.path(g, P.line(240, y - cm, 240, y + cm) + P.line(240, 382 - cm, 240, 382) +
    P.line(x - cm, 186, x + cm, 186) + P.line(x + w - cm, 186, x + w + cm, 186), 'f');
  k.text(g, -2, 396, `FIG. ${String(def.fig).padStart(2, '0')}`, 't tt');
  k.text(g, 86, 396, def.name, 't');
  k.text(g, 482, 396, 'Astrolabe Schematics', 't ts', 'end');
}

class SchematicElement extends HTMLElement {
  static get observedAttributes() {
    return BASE_ATTRS.concat(Object.keys((this.def && this.def.params) || {}));
  }

  constructor() {
    super();
    const def = this.constructor.def;
    this._def = def;
    this._t = 0;
    this._speed = 1;
    this._strokeMul = 1;
    this._labels = true;
    this._frame = false;
    this._pinned = false;
    this._force = false;
    this._visible = true;
    this._built = false;
    this._w = 0;
    this._detail = null;
    this._pu = 1;
    this._tu = 1;
    this._ps = 1;
    this._P = {};
    for (const [name, p] of Object.entries(def.params || {})) this._P[name] = p.default;
    const root = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = HOST_CSS + BASE_CSS;
    this._ar = document.createElement('style');
    this._ar.textContent = ':host{aspect-ratio:4 / 3}';
    root.append(style, this._ar);
    this._svg = h('svg', { xmlns: NS, part: 'svg', 'aria-hidden': 'true', focusable: 'false' });
    root.append(this._svg);
    try { this._internals = this.attachInternals(); } catch (e) { this._internals = null; }
    this._a11y();
  }

  /* ----- lifecycle ----- */
  connectedCallback() {
    LIVE.add(this);
    ro.observe(this);
    io.observe(this);
    Clock.add(this);
    if (REDUCED.matches && !this._pinned) this._t = this._def.still * this._def.duration;
    if (!this._built) {
      const w = this.getBoundingClientRect().width;
      if (w) this._resize(w);
    }
  }
  disconnectedCallback() {
    LIVE.delete(this);
    ro.unobserve(this);
    io.unobserve(this);
    Clock.remove(this);
  }
  attributeChangedCallback(name, _old, val) {
    const def = this._def;
    switch (name) {
      case 'speed': this._speed = val == null ? 1 : clamp(parseFloat(val) || 0, 0, 16); return;
      case 'paused': if (val == null) this._force = true; return;
      case 'time':
        this._pinned = val != null;
        if (this._pinned) { this._t = mod(parseFloat(val) || 0, def.duration); this._render(); }
        return;
      case 'ink': case 'accent': case 'paper': {
        const prop = name === 'ink' ? '--ink' : name === 'accent' ? '--acc' : '--paper';
        if (val) this._svg.style.setProperty(prop, val); else this._svg.style.removeProperty(prop);
        return;
      }
      case 'stroke': this._strokeMul = val == null ? 1 : clamp(parseFloat(val) || 1, 0.25, 4); break;
      case 'labels': this._labels = !(val === 'off' || val === 'false' || val === 'none'); break;
      case 'frame': this._frame = val != null && val !== 'false'; break;
      case 'detail': break;
      case 'label': case 'decorative': this._a11y(); return;
      default: {
        const p = def.params && def.params[name];
        if (!p) return;
        this._P[name] = val == null ? p.default : p.type === 'range' ? clamp(parseFloat(val), p.min, p.max) : val;
        if (isNaN(this._P[name])) this._P[name] = p.default;
        if (p.live) { this._render(); return; }
      }
    }
    if (this._w) this._resize(this._w, true);
  }

  _a11y() {
    const decorative = this.hasAttribute('decorative');
    const label = this.getAttribute('label') || this._def.name;
    if (this._internals && 'role' in this._internals) {
      this._internals.role = decorative ? 'presentation' : 'img';
      this._internals.ariaLabel = decorative ? null : label;
      this._internals.ariaHidden = decorative ? 'true' : null;
    }
  }

  _motionChanged() {
    if (REDUCED.matches && !this._force && !this._pinned) { this._t = this._def.still * this._def.duration; this._render(); }
  }

  /* ----- level of detail ----- */
  _detailFor(w) {
    const d = this.getAttribute('detail');
    if (d === 'full' || d === 'figure' || d === 'mark') return d;
    return w >= 400 ? 'full' : w >= 150 ? 'figure' : 'mark';
  }
  _vbFor(detail) {
    if (detail === 'mark') return this._def.mark;
    return this._frame ? FRAMED : SHEET;
  }
  _resize(w, force) {
    if (!w) return;
    this._w = w;
    const detail = this._detailFor(w);
    const vb = this._vbFor(detail);
    const [ps, ts] = penScale(detail, w);
    const tu = (vb[2] / w) * ts;
    const pu = (vb[2] / w) * ps * this._strokeMul;
    this._ps = ps * this._strokeMul;
    this._svg.style.setProperty('--pu', String(pu));
    this._svg.style.setProperty('--tu', String(tu));
    this._svg.style.setProperty('--ps', String(this._ps));
    const changed = detail !== this._detail || Math.abs(pu / this._pu - 1) > 0.12 || Math.abs(tu / this._tu - 1) > 0.12;
    this._vb = vb;
    if (force || !this._built || changed) {
      this._detail = detail;
      this._pu = pu;
      this._tu = tu;
      this._build();
    }
  }

  _build() {
    const svg = this._svg;
    svg.textContent = '';
    const vb = this._vb;
    svg.setAttribute('viewBox', vb.join(' '));
    this._ar.textContent = `:host{aspect-ratio:${vb[2]} / ${vb[3]}}`;
    svg.setAttribute('class', this._detail === 'mark' ? 'mk' : '');
    const k = makeKit(svg, { pu: this._pu, tu: this._tu, detail: this._detail, labels: this._labels });
    this._k = k;
    if (this._frame && this._detail !== 'mark') drawFrame(k, this._def);
    let upd = null;
    try { upd = this._def.build(k, this._P); } catch (e) { console.error(`[${this._def.tag}]`, e); }
    this._update = typeof upd === 'function' ? upd : () => {};
    this._built = true;
    this._render();
  }

  _render() {
    if (!this._built) return;
    const D = this._def.duration;
    try {
      this._update(fract(this._t / D), this._t, this._P);
      if (this._k) this._k.afterFrame();
    } catch (e) { console.error(`[${this._def.tag}]`, e); }
  }

  _playing() {
    return this._built && !this._pinned && !this.hasAttribute('paused') && (!REDUCED.matches || this._force);
  }
  _tick(dt) {
    if (!this._playing()) return;
    this._t = mod(this._t + dt * this._speed, this._def.duration);
    if (this._visible) this._render();
  }

  /* ----- public API ----- */
  get definition() { return this._def; }
  get duration() { return this._def.duration; }
  get currentTime() { return this._t; }
  set currentTime(v) { this._t = mod(+v || 0, this._def.duration); this._render(); }
  get playing() { return this._playing(); }
  play() { this._force = true; this.removeAttribute('paused'); }
  pause() { this.setAttribute('paused', ''); }
  seek(t) { this.currentTime = t; }

  /** Colors as concrete values (currentColor resolved) for export. */
  resolvedColors() {
    const cs = getComputedStyle(this._svg);
    const read = (v, fb) => { v = (v || '').trim(); return !v || v === 'currentColor' || v === 'currentcolor' ? fb : v; };
    const color = cs.color || '#000';
    return {
      ink: read(cs.getPropertyValue('--ink'), color),
      accent: read(cs.getPropertyValue('--acc'), '#0466F4'),
      paper: read(cs.getPropertyValue('--paper'), 'transparent'),
    };
  }

  /**
   * Build a detached renderer for exports: fixed pixel size, letterboxed,
   * optional background. svgAt(t) returns a standalone SVG string.
   */
  renderer(opt = {}) {
    const W = Math.round(opt.width || 1920), H = Math.round(opt.height || 1080);
    const pad = opt.padding == null ? Math.round(Math.min(W, H) * 0.06) : opt.padding;
    const colors = Object.assign(this.resolvedColors(), opt.colors || {});
    const inst = document.createElement(this.localName);
    for (const a of this.getAttributeNames()) {
      if (['ink', 'accent', 'paper', 'time', 'paused', 'style', 'class', 'id'].includes(a)) continue;
      inst.setAttribute(a, this.getAttribute(a));
    }
    if (opt.frame != null) { if (opt.frame) inst.setAttribute('frame', ''); else inst.removeAttribute('frame'); }
    if (opt.detail) inst.setAttribute('detail', opt.detail);
    // Fit the sheet into the target frame, then size pens for that width.
    inst._frame = inst.hasAttribute('frame');
    const probe = inst._vbFor(inst._detailFor(1000));
    const s0 = Math.min((W - 2 * pad) / probe[2], (H - 2 * pad) / probe[3]);
    inst._resize(probe[2] * s0 * (opt.scaleHint || 1), true);
    const vb = inst._vb;
    const s = Math.min((W - 2 * pad) / vb[2], (H - 2 * pad) / vb[3]);
    const ow = W / s, oh = H / s;
    const ox = vb[0] - (ow - vb[2]) / 2, oy = vb[1] - (oh - vb[3]) / 2;
    const bg = opt.background && opt.background !== 'transparent' ? opt.background : null;
    const head = `<svg xmlns="${NS}" width="${W}" height="${H}" viewBox="${n2(ox)} ${n2(oy)} ${n2(ow)} ${n2(oh)}">` +
      `<style>svg{--ink:${colors.ink};--acc:${colors.accent};--paper:${colors.paper};--pu:${inst._pu};--tu:${inst._tu};--ps:${inst._ps}}${BASE_CSS}</style>` +
      (bg ? `<rect x="${n2(ox)}" y="${n2(oy)}" width="${n2(ow)}" height="${n2(oh)}" fill="${bg}"/>` : '');
    const ser = new XMLSerializer();
    return {
      width: W, height: H, duration: this._def.duration,
      svgAt(t) {
        inst._t = mod(t, inst._def.duration);
        inst._render();
        let body = '';
        for (const c of inst._svg.childNodes) body += ser.serializeToString(c);
        return head + body + '</svg>';
      },
      /** Same frame with every style inlined as attributes (Figma, Illustrator). */
      designSVGAt(t) {
        const holder = document.createElement('div');
        // off-screen, but not visibility:hidden, which every child would inherit
        holder.style.cssText = 'position:fixed;left:-99999px;top:0;width:10px;height:10px;overflow:hidden;opacity:0;pointer-events:none';
        const host = document.createElement('div');
        holder.append(host);
        document.body.append(holder);
        const shadow = host.attachShadow({ mode: 'open' });
        const st = document.createElement('style');
        st.textContent = `svg{--ink:${colors.ink};--acc:${colors.accent};--paper:${colors.paper};--pu:${inst._pu};--tu:${inst._tu};--ps:${inst._ps}}${BASE_CSS}`;
        inst._t = mod(t, inst._def.duration);
        inst._render();
        const live = inst._svg.cloneNode(true);
        live.removeAttribute('style');
        shadow.append(st, live);
        const out = inlineStyles(live);
        holder.remove();
        out.setAttribute('width', W);
        out.setAttribute('height', H);
        out.setAttribute('viewBox', `${n2(ox)} ${n2(oy)} ${n2(ow)} ${n2(oh)}`);
        if (bg) out.insertBefore(h('rect', { x: n2(ox), y: n2(oy), width: n2(ow), height: n2(oh), fill: bg }), out.firstChild);
        return ser.serializeToString(out);
      },
    };
  }

  /** Current frame as a standalone SVG string. */
  toSVG(opt = {}) {
    const r = this.renderer(Object.assign({ width: 1440, height: 1080, padding: 0 }, opt));
    const t = opt.time == null ? this._t : opt.time;
    return opt.inline === false ? r.svgAt(t) : r.designSVGAt(t);
  }
}

const STYLE_PROPS = ['stroke', 'stroke-width', 'stroke-dasharray', 'stroke-linecap', 'stroke-linejoin', 'stroke-opacity',
  'fill', 'fill-opacity', 'opacity', 'font-family', 'font-size', 'font-weight', 'letter-spacing'];

/** Walk a rendered SVG and bake computed styles into attributes. */
function inlineStyles(svg) {
  const clone = svg.cloneNode(true);
  const src = [svg, ...svg.querySelectorAll('*')];
  const dst = [clone, ...clone.querySelectorAll('*')];
  for (let i = src.length - 1; i >= 0; i--) {
    const s = src[i], d = dst[i];
    const tag = s.tagName.toLowerCase();
    if (['defs', 'clippath', 'mask', 'marker', 'pattern', 'style', 'svg'].includes(tag)) { d.removeAttribute('class'); continue; }
    const cs = getComputedStyle(s);
    if (cs.visibility === 'hidden' || cs.display === 'none' || cs.opacity === '0') { d.remove(); continue; }
    for (const p of STYLE_PROPS) {
      let v = cs.getPropertyValue(p);
      if (!v || v === 'normal' || (p === 'stroke-dasharray' && v === 'none')) continue;
      if (p === 'opacity' && v === '1') continue;
      if (/^(font-|letter-)/.test(p) && tag !== 'text') continue;
      if (/^stroke-/.test(p) && cs.stroke === 'none') continue;
      const numeric = /^(stroke-width|stroke-dasharray|font-size|letter-spacing)$/.test(p);
      d.setAttribute(p, numeric ? v.replace(/px/g, '').replace(/,\s*/g, ' ') : v);
    }
    d.removeAttribute('class');
    d.removeAttribute('style');
  }
  clone.removeAttribute('part');
  clone.removeAttribute('aria-hidden');
  clone.removeAttribute('focusable');
  clone.setAttribute('xmlns', NS);
  return clone;
}

/* ---------- Registry ---------- */
const REGISTRY = [];
function define(def) {
  def.params = def.params || {};
  def.moments = def.moments || [];
  def.keywords = def.keywords || [];
  def.still = def.still == null ? 0.6 : def.still;
  def.mark = def.mark || [60, 0, 360, 360];
  class Figure extends SchematicElement {}
  Figure.def = def;
  if (!customElements.get(def.tag)) customElements.define(def.tag, Figure);
  REGISTRY.push(def);
  REGISTRY.sort((a, b) => a.fig - b.fig);
  return Figure;
}

window.AstrolabeSchematics = {
  version: VERSION,
  figures: REGISTRY,
  define,
  get reducedMotion() { return !!REDUCED.matches; },
};

/* src/figures/00-astro-kit.js */
/* ------------------------------------------------------------------
 * Astrolabe parts shared by figures 01–06. Every line is computed:
 * plates are true stereographic projections for the chosen latitude,
 * the rete carries real stars at their J2000 positions.
 * ------------------------------------------------------------------ */

const OBL = 23.44; // obliquity of the ecliptic, degrees

/** [name, RA hours, Dec degrees, magnitude]. Stars inside the tropic of Capricorn. */
const RETE_STARS = [
  ['Aldebaran', 4.599, 16.51, 0.85], ['Rigel', 5.242, -8.2, 0.13], ['Capella', 5.278, 46.0, 0.08],
  ['Betelgeuse', 5.919, 7.41, 0.45], ['Sirius', 6.752, -16.72, -1.46], ['Procyon', 7.655, 5.22, 0.34],
  ['Pollux', 7.755, 28.03, 1.14], ['Regulus', 10.139, 11.97, 1.35], ['Dubhe', 11.062, 61.75, 1.79],
  ['Spica', 13.42, -11.16, 0.98], ['Arcturus', 14.261, 19.18, -0.05], ['Vega', 18.616, 38.78, 0.03],
  ['Altair', 19.846, 8.87, 0.77], ['Deneb', 20.69, 45.28, 1.25], ['Alpheratz', 0.14, 29.09, 2.06],
  ['Hamal', 2.12, 23.46, 2.0],
];

function astroGeo(Rc, phi) {
  const Req = Rc / Math.tan(((90 + OBL) * D2R) / 2);
  const rCn = stereoR(Req, OBL);
  return {
    Rc, Req, rCn, phi,
    hor: almucantar(Req, phi, 0),
    zen: stereoR(Req, phi),
    eOff: (Rc - rCn) / 2,
    eR: (Rc + rCn) / 2,
  };
}

/** Degrees → "40°43′" */
function dms(deg) {
  const a = Math.abs(deg);
  let d = Math.floor(a), m = Math.round((a - d) * 60);
  if (m === 60) { d += 1; m = 0; }
  return `${d}°${String(m).padStart(2, '0')}′`;
}

/**
 * Latitude plate (tympan), centred on (0,0) of `parent`. Returns named
 * elements so construction figures can draw them on in order.
 * opt.draw: create drawable paths; opt.level: 'full' | 'figure' | 'mark'.
 */
function drawPlate(k, parent, G, opt = {}) {
  const level = opt.level || k.detail;
  const mk = opt.draw ? k.dpath : k.path;
  const g = k.g(parent, 'plate');
  const out = { g, alms: [], azs: [] };
  const plateClip = k.clip(P.circle(0, 0, G.Rc - 0.2));
  const gc = k.g(g, null, { 'clip-path': plateClip.ref });
  const horClip = k.clip(P.circle(0, -G.hor.c, G.hor.r));
  const gAz = k.g(gc, null, { 'clip-path': horClip.ref });

  out.meridian = mk(g, P.line(0, -G.Rc, 0, G.Rc), 'f');
  if (level !== 'mark') out.ew = mk(g, P.line(-G.Rc, 0, G.Rc, 0), 'f');
  out.eq = mk(g, P.circle(0, 0, G.Req), 'm');
  if (level !== 'mark') out.cancer = mk(g, P.circle(0, 0, G.rCn), 'f');
  out.horizon = mk(gc, P.circle(0, -G.hor.c, G.hor.r), 'm');

  const step = level === 'full' ? 5 : level === 'figure' ? 10 : 30;
  for (let hDeg = step; hDeg < 90; hDeg += step) {
    const a = almucantar(G.Req, G.phi, hDeg);
    const cls = hDeg % 30 === 0 ? 'f' : 'h';
    out.alms.push({ h: hDeg, el: mk(gc, P.circle(0, -a.c, a.r), cls) });
  }
  const azStep = level === 'full' ? 15 : level === 'figure' ? 30 : 0;
  if (azStep) {
    for (let A = azStep; A < 180; A += azStep) {
      const c = azimuthCircle(G.Req, G.phi, A);
      out.azs.push({ A, el: mk(gAz, P.circle(c.x, -c.c, c.r), A === 90 ? 'f' : 'h') });
    }
  }
  if (level !== 'mark') {
    const tw = almucantar(G.Req, G.phi, -18);
    out.twilight = k.path(gc, P.circle(0, -tw.c, tw.r), 'hd');
  }
  out.zenith = k.centre(g, 0, -G.zen, 3.2, 'f');
  out.rim = mk(g, P.circle(0, 0, G.Rc), 'o');
  return out;
}

/** Mater limb: the graduated ring around the plate. */
function drawLimb(k, parent, Rin, Rout, opt = {}) {
  const g = k.g(parent, 'limb');
  k.path(g, P.circle(0, 0, Rout), 'o');
  if (k.mark) {
    k.path(g, P.ticks(0, 0, Rout - (Rout - Rin) * 0.55, Rout, 0, TAU, 12), 'm');
    return g;
  }
  k.path(g, P.circle(0, 0, Rin), 'm');
  const mid = Rin + (Rout - Rin) * 0.38;
  k.path(g, P.circle(0, 0, mid), 'f');
  k.path(g, P.ticks(0, 0, Rin, mid, 0, TAU, 72), 'f');
  const numerals = k.L && opt.numerals !== false;
  let d = '';
  for (let i = 0; i < 24; i++) {
    if (numerals && i % 2 === 0) continue;
    const a = -Math.PI / 2 + (i * TAU) / 24;
    d += P.line(...polar(0, 0, Rin, a), ...polar(0, 0, Rout - (Rout - Rin) * 0.18, a));
  }
  k.path(g, d, 'f');
  if (numerals) {
    const rr = mid + (Rout - mid) * 0.5;
    for (let i = 0; i < 12; i++) {
      const [x, y] = polar(0, 0, rr, -Math.PI / 2 + (i * TAU) / 12);
      k.text(g, x, y, String(i * 30), 't ts', 'middle');
    }
  }
  return g;
}

/** Throne (the hanging bracket) and shackle ring above the limb. */
function drawThrone(k, parent, R, opt = {}) {
  const g = k.g(parent, 'throne');
  const a0 = -Math.PI / 2 - 0.2, a1 = -Math.PI / 2 + 0.2;
  const [x0, y0] = polar(0, 0, R, a0);
  const [x1, y1] = polar(0, 0, R, a1);
  const top = -R - 15;
  const d = `M${n2(x0)} ${n2(y0)}C${n2(x0 + 2)} ${n2(y0 - 8)} ${n2(-9)} ${n2(top + 6)} ${n2(-6)} ${n2(top)}` +
    `L6 ${n2(top)}C9 ${n2(top + 6)} ${n2(x1 - 2)} ${n2(y1 - 8)} ${n2(x1)} ${n2(y1)}`;
  k.path(g, d, 'o');
  k.body(g, 0, top + 6.5, 2.6, 'f');
  if (opt.ring !== false) k.body(g, 0, top - 7.5, 7.5, 'm');
  return g;
}

/**
 * Rete: ecliptic band, rim, crossbar and star pointers. Drawn at rotation
 * 0; the caller rotates the group. Returns stars for per-frame updates.
 */
function drawRete(k, parent, G, opt = {}) {
  const g = k.g(parent, 'rete');
  const cls = opt.cls || 'a';
  const band = G.Rc * 0.075;
  const rimIn = G.Rc * 0.955;
  if (k.mark) {
    k.path(g, P.circle(0, G.eOff, G.eR), `m ${cls}`);
    k.path(g, P.line(-rimIn, 0, rimIn, 0), `m ${cls}`);
    const stars = [];
    for (const [name, ra, dec, mag] of RETE_STARS.filter((s) => s[3] < 0.2)) {
      const r = stereoR(G.Req, dec), a = -ra * 15 * D2R;
      const [x, y] = polar(0, 0, r, a);
      const dot = k.body(g, x, y, G.Rc * 0.045, cls === 'a' ? 'fa' : 'fi');
      stars.push({ name, r, a, x, y, ptr: dot, dot });
    }
    return { g, stars };
  }
  k.path(g, P.circle(0, 0, G.Rc * 0.995) + P.circle(0, 0, rimIn), `m ${cls}`);
  // ecliptic band
  k.path(g, P.circle(0, G.eOff, G.eR), `m ${cls}`);
  k.path(g, P.circle(0, G.eOff, G.eR - band), `f ${cls}`);
  {
    let dz = '';
    for (let lam = 0; lam < 360; lam += 10) {
      const L = lam * D2R, e = OBL * D2R;
      const ra = Math.atan2(Math.sin(L) * Math.cos(e), Math.cos(L));
      const dec = Math.asin(Math.sin(e) * Math.sin(L)) * R2D;
      const [x, y] = polar(0, 0, stereoR(G.Req, dec), -ra);
      const vx = 0 - x, vy = G.eOff - y, vl = Math.hypot(vx, vy);
      const len = lam % 30 === 0 ? band : band * 0.45;
      dz += P.line(x, y, x + (vx / vl) * len, y + (vy / vl) * len);
    }
    k.path(g, dz, `f ${cls}`);
  }
  // crossbar along the 0h–12h line, with the centre boss
  const bw = G.Rc * 0.02;
  const boss = G.Rc * 0.07;
  k.path(g, P.line(-rimIn, -bw, -boss, -bw) + P.line(-rimIn, bw, -boss, bw) +
    P.line(boss, -bw, rimIn, -bw) + P.line(boss, bw, rimIn, bw), `f ${cls}`);
  k.path(g, P.circle(0, 0, boss), `m ${cls}`);
  k.path(g, P.circle(0, 0, boss * 0.36), `f ${cls}`);
  // partial equator arc (lower half)
  if (!k.mark) k.path(g, P.arc(0, 0, G.Req, 0.35, Math.PI - 0.35), `f ${cls}`);
  // star pointers
  const stars = [];
  const list = k.mark ? RETE_STARS.filter((s) => s[3] < 0.5) : RETE_STARS;
  for (const [name, ra, dec, mag] of list) {
    const r = stereoR(G.Req, dec);
    const a = -ra * 15 * D2R;
    const [x, y] = polar(0, 0, r, a);
    const ux = -Math.cos(a), uy = -Math.sin(a);
    const L = G.Rc * 0.085, W = G.Rc * 0.016;
    const bx = x + ux * L, by = y + uy * L;
    const ptr = k.path(g, `M${n2(x)} ${n2(y)}L${n2(bx - uy * W)} ${n2(by + ux * W)}L${n2(bx + uy * W)} ${n2(by - ux * W)}Z`, `f ${cls}`);
    const dot = k.star(g, x, y, Math.max(0.2, mag), cls === 'a' ? 'fa' : 'fi');
    stars.push({ name, r, a, x, y, ptr, dot });
  }
  return { g, stars };
}

/** Rotate a rete (group + upright star labels) and grade stars by horizon. */
function reteFrame(k, rete, G, theta, labels) {
  k.rotate(rete.g, theta);
  const hc = -G.hor.c, hr = G.hor.r;
  for (let i = 0; i < rete.stars.length; i++) {
    const s = rete.stars[i];
    const [x, y] = polar(0, 0, s.r, s.a + theta);
    const up = Math.hypot(x, y - hc) < hr;
    const o = up ? 1 : 0.38;
    k.fade(s.dot, o);
    k.fade(s.ptr, o);
    const lb = labels && labels[i];
    if (lb) {
      const ux = x / (s.r || 1), uy = y / (s.r || 1);
      const off = 9 * k.tu;
      lb.setAttribute('x', n2(x + ux * off));
      lb.setAttribute('y', n2(y + uy * off + 2.7 * k.tu));
      lb.setAttribute('text-anchor', ux > 0.25 ? 'start' : ux < -0.25 ? 'end' : 'middle');
      k.fade(lb, up ? 1 : 0.35);
    }
  }
}

/** Alidade/rule: a straight bar through the centre with a fiducial edge. */
function drawRule(k, parent, R, opt = {}) {
  const g = k.g(parent, 'rule');
  const w = R * 0.05;
  const cls = opt.cls || 'm';
  const d = `M${n2(-R)} 0L${n2(-R * 0.12)} ${n2(-w)}L${n2(R * 0.12)} ${n2(-w)}L${n2(R)} 0` +
    `L${n2(R * 0.12)} ${n2(w)}L${n2(-R * 0.12)} ${n2(w)}Z`;
  k.path(g, d, cls);
  k.path(g, P.line(-R, 0, R, 0), 'h');
  k.path(g, P.circle(0, 0, R * 0.05), 'f');
  return g;
}

/* src/figures/01-assembly.js */
/* FIG. 01: Astrolabe, assembly. Oblique exploded view on one axis. The
 * pin rises through the mater, the plate seats flush, the rete and rule
 * settle, the horse locks the stack. It turns as one, then separates. */
define({
  tag: 'astrolabe-assembly',
  fig: 1,
  name: 'Astrolabe, assembly',
  family: 'astrolabe',
  moments: ['ready', 'hero'],
  duration: 14,
  still: 0.94,
  mark: [90, 30, 300, 300],
  description: 'An exploded view of the instrument. Pin, mater, plate, rete, rule, and horse come together on one axis, turn as one unit, then separate again.',
  use: 'Ready: Wrapped report ready',
  keywords: ['astrolabe', 'assembly', 'exploded', 'parts', 'ready', 'complete', 'hero', 'instrument'],
  params: {
    latitude: { type: 'range', min: 0, max: 66, step: 0.1, default: 40.72, label: 'Plate latitude', unit: '°' },
  },
  build(k, o) {
    if (k.mark) return assemblyMark(k, o);
    const s = 0.34;
    const cx = k.L ? 190 : 240;
    const X = k.mark ? 0.46 : 1;
    const Ro = 118, Ri = 104.5, Tm = 12, Tp = 3.2, Rr = 110;
    const G = astroGeo(103, o.latitude);
    const yA = 238; // the mater stays put; everything else comes to it
    const ex = (e, a) => a + (e - a) * X;
    // exploded / assembled heights of each part's top face
    const Y = {
      horse: [ex(30, yA - 10), yA - 10], rule: [ex(76, yA - 5), yA - 5], rete: [ex(126, yA - 1.5), yA - 1.5],
      plate: [ex(180, yA), yA], mater: [yA, yA], pin: [ex(290, yA - 16), yA - 16],
    };
    // [assemble start, end, explode start, end]
    const T = {
      pin: [0.08, 0.28, 0.72, 0.9], plate: [0.1, 0.24, 0.72, 0.86], rete: [0.15, 0.29, 0.68, 0.8],
      rule: [0.2, 0.34, 0.64, 0.76], horse: [0.25, 0.38, 0.6, 0.72],
    };
    const amt = (p, t) => ease.inOut(seg(p, t[0], t[1])) - ease.inOut(seg(p, t[2], t[3]));

    const bounds = [-40, -40, 560, 440];
    const M = { pin: k.mask(bounds), mater: k.mask(bounds), plate: k.mask(bounds), rete: k.mask(bounds) };
    const occ = (m) => h('path', { d: '', fill: '#000' }, m.g);
    const oc = {
      pinM: occ(M.pin), pinP: occ(M.pin), pinR: occ(M.pin),
      materP: occ(M.mater), materR: occ(M.mater), plateR: occ(M.plate), reteR: occ(M.rete),
    };
    const silo = (y, R, t) => {
      const ry = R * s;
      return `M${n2(cx - R)} ${n2(y)}A${n2(R)} ${n2(ry)} 0 0 1 ${n2(cx + R)} ${n2(y)}` +
        `L${n2(cx + R)} ${n2(y + t)}A${n2(R)} ${n2(ry)} 0 0 1 ${n2(cx - R)} ${n2(y + t)}Z`;
    };
    const sq = (parent) => k.g(parent, 'nsg', { transform: `scale(1 ${s})` });

    // layer 0: axis and pin (hidden behind every solid part)
    const L0 = k.g(null, 'pt-pin', { mask: M.pin.ref });
    const pin = k.g(L0);
    k.path(pin, P.line(-1.8, 0, -1.8, 46) + P.line(1.8, 0, 1.8, 46), 'm');
    k.path(pin, P.rect(-1.8, 4, 3.6, 4), 'f');
    k.path(pin, P.ellipse(0, 47, 6.5, 6.5 * s) + P.line(-6.5, 47, -6.5, 50) + P.line(6.5, 47, 6.5, 50) +
      P.earc(0, 50, 6.5, 6.5 * s, 0, Math.PI), 'm');

    // layer 1: mater with limb, throne and body thickness
    const L1 = k.g(null, 'pt-mater', { mask: M.mater.ref });
    const mater = k.g(L1);
    const mSq = sq(mater);
    drawThrone(k, mSq, Ro);
    drawLimb(k, mSq, Ri, Ro, { numerals: false });
    k.path(mater, P.earc(0, 5, Ri, Ri * s, Math.PI, TAU), 'h');
    k.path(mater, P.line(-Ro, 0, -Ro, Tm) + P.line(Ro, 0, Ro, Tm) + P.earc(0, Tm, Ro, Ro * s, 0, Math.PI), 'o');

    // layer 2: latitude plate
    const L2 = k.g(null, 'pt-plate', { mask: M.plate.ref });
    const plate = k.g(L2);
    drawPlate(k, sq(plate), G);
    const plateBand = k.path(plate, '', 'm');

    // layer 3: rete
    const L3 = k.g(null, 'pt-rete', { mask: M.rete.ref });
    const reteG = k.g(L3);
    const rete = drawRete(k, sq(reteG), G);

    // layer 4: rule
    const ruleW = k.g(null, 'pt-rule');
    const ruleG = k.g(ruleW);
    const rule = drawRule(k, sq(ruleG), Rr);

    // layer 5: horse (wedge through the pin)
    const horseW = k.g(null, 'pt-horse');
    const horse = k.g(horseW);
    k.path(horse, 'M-15 -2.5L15 -2.5L12 2.5L-12 2.5Z', 'm');
    k.path(horse, P.line(-15, -2.5, -19, -6) + P.circle(-20.5, -7.5, 2.2), 'f');

    // leaders (full detail): anchored to moving parts, fade as parts seat
    const lead = [];
    let assembled = null;
    if (k.L) {
      const kx = cx + 146;
      const mk = (label, ky) => {
        const g = k.g(null, 'ld');
        const path = k.path(g, '', 'dm');
        const dot = k.dot(g, 0, 0, 1.35);
        k.text(g, kx + 12, ky, label, 't');
        return { g, path, dot, ky };
      };
      lead.push(
        Object.assign(mk('Horse', 30), { part: 'horse' }),
        Object.assign(mk('Rule', 78), { part: 'rule' }),
        Object.assign(mk('Rete', 126), { part: 'rete' }),
        Object.assign(mk('Plate', 184), { part: 'plate' }),
        Object.assign(mk('Mater', 246), { part: 'mater' }),
        Object.assign(mk('Pin', 318), { part: 'pin' }),
      );
      assembled = k.text(null, kx + 12, yA - 30, 'Assembled', 't');
      k.text(null, 14, 346, `PLATE φ ${dms(o.latitude)} N`, 't ts tn');
    }

    return (p) => {
      const a = {};
      for (const key of ['pin', 'plate', 'rete', 'rule', 'horse']) a[key] = amt(p, T[key]);
      a.mater = 0;
      const y = {};
      for (const key in Y) y[key] = lerp(Y[key][0], Y[key][1], a[key]);
      const th = p * TAU;
      const thr = 0.9 - p * TAU;

      k.move(pin, cx, y.pin);
      k.move(mater, cx, y.mater);
      k.move(plate, cx, y.plate);
      k.move(reteG, cx, y.rete);
      k.move(ruleG, cx, y.rule);
      k.move(horse, cx, y.horse - 5);
      reteFrame(k, rete, G, th, null);
      k.rotate(rule, thr);
      const band = Math.min(Tp, Math.max(0, yA - y.plate));
      k.set(plateBand, band > 0.05
        ? P.line(-103, 0, -103, band) + P.line(103, 0, 103, band) + P.earc(0, band, 103, 103 * s, 0, Math.PI)
        : '');

      // occluders
      const plateSil = silo(y.plate, 103, band);
      const hw = Rr * 0.05, c = Math.cos(thr), sn = Math.sin(thr);
      const rpts = [[-Rr, 0], [-Rr * 0.12, -hw], [Rr * 0.12, -hw], [Rr, 0], [Rr * 0.12, hw], [-Rr * 0.12, hw]]
        .map(([u, v]) => [cx + u * c - v * sn, y.rule + (u * sn + v * c) * s]);
      const ruleSil = P.poly(rpts, true);
      const materSil = silo(y.mater, Ro, Tm);
      k.set(oc.pinM, materSil); k.set(oc.pinP, plateSil); k.set(oc.pinR, ruleSil);
      k.set(oc.materP, plateSil); k.set(oc.materR, ruleSil);
      k.set(oc.plateR, ruleSil); k.set(oc.reteR, ruleSil);

      if (lead.length) {
        const anchor = {
          horse: [cx + 15, y.horse - 5], rule: rpts[3], rete: [cx + Rr * 0.995, y.rete],
          plate: [cx + 103, y.plate], mater: [cx + Ro, y.mater + Tm * 0.5], pin: [cx + 1.8, y.pin + 32],
        };
        for (const l of lead) {
          const [ax, ay] = anchor[l.part];
          const kx = cx + 146;
          k.set(l.path, P.poly([[ax, ay], [kx, l.ky], [kx + 9, l.ky]]));
          l.dot.setAttribute('cx', n2(ax));
          l.dot.setAttribute('cy', n2(ay));
          k.fade(l.g, 1 - a[l.part] * (l.part === 'mater' ? 0 : 1));
        }
        const all = Math.min(a.pin, a.plate, a.rete, a.rule, a.horse);
        k.fade(assembled, all);
        const m = lead.find((l) => l.part === 'mater');
        k.fade(m.g, 1 - all);
      }
    };
  },
});

/** Icon-size reduction: the assembled instrument, rete and rule turning. */
function assemblyMark(k, o) {
  const s = 0.62, Ro = 118, Ri = 100, T = 16;
  const G = astroGeo(98, o.latitude);
  const top = k.g(null, null, { transform: 'translate(240 184)' });
  k.path(top, P.line(-Ro, 0, -Ro, T) + P.line(Ro, 0, Ro, T) + P.earc(0, T, Ro, Ro * s, 0, Math.PI), 'o');
  const face = k.g(top, 'nsg', { transform: `scale(1 ${s})` });
  drawThrone(k, face, Ro);
  k.path(face, P.circle(0, 0, Ro), 'o');
  k.path(face, P.circle(0, 0, Ri), 'm');
  const rete = drawRete(k, face, G);
  const rule = k.g(face);
  k.path(rule, P.line(-Ri, 0, Ri, 0), 'o');
  return (p) => {
    reteFrame(k, rete, G, p * TAU, null);
    k.rotate(rule, 0.9 - p * TAU);
  };
}

/* src/figures/02-rete.js */
/* FIG. 02: Rete, turning. The star map turns over a fixed latitude plate:
 * one sidereal day per loop. Stars rise at the eastern horizon (left),
 * culminate on the meridian, and set in the west. */
define({
  tag: 'astrolabe-rete',
  fig: 2,
  name: 'Rete, turning',
  family: 'astrolabe',
  moments: ['enrichment'],
  duration: 30,
  still: 0.18,
  mark: [80, 20, 320, 320],
  description: 'The rete turns once per sidereal day over a plate for your latitude. Stars brighten as they clear the horizon and dim as they set.',
  use: 'Enrichment: list enrichment running',
  keywords: ['astrolabe', 'rete', 'stars', 'rotation', 'loading', 'spinner', 'sidereal', 'plate'],
  params: {
    latitude: { type: 'range', min: 0, max: 66, step: 0.1, default: 40.72, label: 'Plate latitude', unit: '°' },
  },
  build(k, o) {
    const G = astroGeo(124, o.latitude);
    const top = k.g(null, null, { transform: 'translate(240 194)' });
    drawThrone(k, top, 143);
    drawLimb(k, top, 125.5, 143);
    drawPlate(k, top, G);
    const rete = drawRete(k, top, G);
    if (!k.mark) k.centre(top, 0, 0, 4, 'f');

    let labels = null, ecl = null, lst = null;
    if (k.L) {
      labels = rete.stars.map((s) => k.text(top, s.x, s.y, s.name, 't ts tn'));
      const hx = -88;
      const hy = -G.hor.c + Math.sqrt(G.hor.r * G.hor.r - hx * hx);
      k.leader(top, [0, -G.zen], [-172, -112], 'Zenith');
      k.leader(top, [hx, hy], [-172, -30], 'Horizon');
      k.leader(top, [-G.Req * 0.7, G.Req * 0.72], [-172, 52], 'Plate');
      k.leader(top, polar(0, 0, 134, 2.5), [-172, 118], 'Limb');
      k.leader(top, polar(0, 0, 123, -0.72), [172, -108], 'Rete');
      k.leader(top, [0, -164], [60, -166], 'Throne');
      ecl = k.path(top, '', 'dm');
      k.text(top, 184, -58, 'Ecliptic', 't');
      k.text(null, 14, 330, `PLATE φ ${dms(o.latitude)} N`, 't ts tn');
      lst = k.text(null, 14, 346, '', 't ts');
    }

    return (p) => {
      const theta = p * TAU;
      reteFrame(k, rete, G, theta, labels);
      if (ecl) {
        const ec = polar(0, 0, G.eOff, theta + Math.PI / 2);
        const a = Math.atan2(-58 - ec[1], 172 - ec[0]);
        k.set(ecl, P.poly([polar(ec[0], ec[1], G.eR, a), [172, -58], [181, -58]]));
      }
      if (lst) {
        const hrs = mod((theta * R2D + 90) / 15, 24);
        const hh = Math.floor(hrs), mm = Math.floor((hrs - hh) * 60);
        k.retext(lst, `LST ${String(hh).padStart(2, '0')}H ${String(mm).padStart(2, '0')}M`);
      }
    };
  },
});

/* src/figures/03-plate.js */
/* FIG. 03: Plate, construction. The latitude plate is drafted in the order
 * an instrument maker would scribe it: axes, equator and tropics by
 * compass, the horizon from its offset centre, then almucantars and
 * azimuths. Every circle is the true stereographic projection. */
define({
  tag: 'astrolabe-plate',
  fig: 3,
  name: 'Plate, construction',
  family: 'astrolabe',
  moments: ['onboarding', 'upload'],
  duration: 16,
  still: 0.86,
  mark: [90, 30, 300, 300],
  description: 'The latitude plate drafted line by line: axes, then equator and tropics by compass, then the horizon from its offset centre, then altitude and azimuth circles.',
  use: 'Onboarding: first run and setup',
  keywords: ['plate', 'construction', 'compass', 'drawing', 'stereographic', 'onboarding', 'build', 'setup'],
  params: {
    latitude: { type: 'range', min: 5, max: 66, step: 0.1, default: 40.72, label: 'Plate latitude', unit: '°' },
  },
  build(k, o) {
    const G = astroGeo(130, o.latitude);
    const O = [0, 0];
    const top = k.g(null, null, { transform: 'translate(240 180)' });
    // a faint finished plate under the drafting, so the figure never reads blank
    const ghost = k.g(top);
    drawPlate(k, ghost, G);
    k.fade(ghost, k.mark ? 0.35 : 0.34);
    const art = k.g(top);
    const clipPlate = k.clip(P.circle(0, 0, G.Rc + 0.4));
    const clipped = k.g(art, null, { 'clip-path': clipPlate.ref });
    const hc = [0, -G.hor.c];
    const horClip = k.clip(P.circle(hc[0], hc[1], G.hor.r));
    const skyClip = k.g(clipped, null, { 'clip-path': horClip.ref });
    const steps = [];
    const arcStep = (parent, c, r, span, cls, t0, t1, compass) => {
      if (!span) return null;
      const el = k.path(parent, '', cls);
      steps.push({ kind: 'arc', el, c, r, a0: span[0], a1: span[1], t0, t1, compass });
      return el;
    };
    const lineStep = (parent, d, cls, t0, t1) => {
      const el = k.dpath(parent, d, cls);
      steps.push({ kind: 'draw', el, t0, t1 });
      return el;
    };
    const fadeStep = (el, t0, t1) => { steps.push({ kind: 'fade', el, t0, t1 }); return el; };

    // axes
    lineStep(art, P.line(0, -G.Rc - 8, 0, G.Rc + 8), 'f', 0.0, 0.04);
    lineStep(art, P.line(-G.Rc - 8, 0, G.Rc + 8, 0), 'f', 0.03, 0.07);
    // equator, tropic of Cancer, tropic of Capricorn (the plate's edge)
    const full = [-Math.PI / 2, 1.5 * Math.PI];
    arcStep(art, O, G.Req, full, 'm', 0.07, 0.13, true);
    arcStep(art, O, G.rCn, full, 'f', 0.13, 0.18, true);
    arcStep(art, O, G.Rc, full, 'o', 0.18, 0.25, true);
    // horizon from its offset centre
    fadeStep(k.centre(art, hc[0], hc[1], 3.5, 'f a'), 0.24, 0.26);
    arcStep(clipped, hc, G.hor.r, arcInside(hc, G.hor.r, O, G.Rc), 'm', 0.26, 0.33, true);
    // almucantars
    const alts = [];
    const step = k.full ? 5 : k.mark ? 30 : 10;
    for (let hDeg = step; hDeg < 90; hDeg += step) alts.push(hDeg);
    alts.forEach((hDeg, i) => {
      const a = almucantar(G.Req, G.phi, hDeg);
      const c = [0, -a.c];
      const t0 = 0.34 + (i * 0.15) / alts.length;
      arcStep(clipped, c, a.r, arcInside(c, a.r, O, G.Rc), hDeg % 30 ? 'h' : 'f', t0, t0 + 0.05, false);
    });
    // azimuths, drawn outward from the zenith to the horizon
    const Z = [0, -G.zen];
    const azs = [];
    const azStep = k.full ? 15 : k.mark ? 0 : 30;
    if (azStep) for (let A = azStep; A < 180; A += azStep) azs.push(A);
    azs.forEach((A, j) => {
      const ac = azimuthCircle(G.Req, G.phi, A);
      const c = [ac.x, -ac.c];
      const pts = ccx(c, ac.r, hc, G.hor.r);
      if (!pts) return;
      const aZ = Math.atan2(Z[1] - c[1], Z[0] - c[0]);
      const t0 = 0.52 + (j * 0.13) / azs.length;
      for (const pt of pts) {
        const ai = Math.atan2(pt[1] - c[1], pt[0] - c[0]);
        let sw = mod(ai - aZ, TAU);
        const mid = polar(c[0], c[1], ac.r, aZ + sw / 2);
        if (Math.hypot(mid[0] - hc[0], mid[1] - hc[1]) > G.hor.r) sw -= TAU;
        arcStep(skyClip, c, ac.r, [aZ, aZ + sw], A === 90 ? 'f' : 'h', t0, t0 + 0.05, false);
      }
    });
    if (!k.mark) {
      const tw = almucantar(G.Req, G.phi, -18);
      fadeStep(k.path(clipped, P.circle(0, -tw.c, tw.r), 'hd'), 0.68, 0.72);
    }
    fadeStep(k.centre(art, Z[0], Z[1], 3.5, 'f'), 0.5, 0.53);

    // compass: pivot, radius arm, scribing point
    const comp = k.g(top, 'compass');
    const arm = k.path(comp, '', 'f a');
    const pivot = k.ring(comp, 0, 0, 2.6, 'f a');
    const pen = k.dot(comp, 0, 0, 1.9, 'fa');

    if (k.L) {
      const lab = k.g(top);
      k.leader(lab, polar(0, 0, G.Req, 2.6), [-156, 62], 'Equator');
      k.leader(lab, polar(0, 0, G.rCn, 3.55), [-156, 14], 'Cancer');
      k.leader(lab, polar(0, 0, G.Rc, 2.3), [-156, 112], 'Capricorn');
      const hs = arcInside(hc, G.hor.r, O, G.Rc);
      k.leader(lab, polar(hc[0], hc[1], G.hor.r, hs[0] + (hs[1] - hs[0]) * 0.24), [156, 58], 'Horizon');
      k.leader(lab, Z, [156, -100], 'Zenith');
      const a30 = almucantar(G.Req, G.phi, 30);
      k.leader(lab, [a30.r, -a30.c], [156, -52], 'Almucantar');
      k.text(top, -226, -160, `φ ${dms(o.latitude)} N`, 't tn');
      k.text(top, -226, -146, 'Stereographic, south pole', 't ts tn q');
      fadeStep(lab, 0.72, 0.78);
    }

    return (p) => {
      const out = 1 - ease.inOut(seg(p, 0.965, 0.995));
      k.fade(art, out);
      let active = null;
      for (const s of steps) {
        const t = seg(p, s.t0, s.t1);
        if (s.kind === 'arc') {
          const e = ease.inOutSine(t);
          k.set(s.el, t > 0 ? P.arc(s.c[0], s.c[1], s.r, s.a0, s.a0 + (s.a1 - s.a0) * e) : '');
          if (s.compass && t > 0 && t < 1) active = { s, e };
        } else if (s.kind === 'draw') {
          k.draw(s.el, ease.inOut(t));
        } else {
          k.fade(s.el, t * out);
        }
      }
      if (active && !k.mark) {
        const { s, e } = active;
        const a = s.a0 + (s.a1 - s.a0) * e;
        const pt = polar(s.c[0], s.c[1], s.r, a);
        k.set(arm, P.line(s.c[0], s.c[1], pt[0], pt[1]));
        k.move(pivot, s.c[0], s.c[1]);
        k.move(pen, pt[0], pt[1]);
        k.fade(comp, Math.min(1, Math.min(seg(p, s.t0, s.t0 + 0.01), 1 - seg(p, s.t1 - 0.01, s.t1)) * 1));
      } else {
        k.fade(comp, 0);
      }
    };
  },
});

/* src/figures/04-alidade.js */
/* FIG. 04: Alidade, sighting. The back of the instrument, hung from its
 * throne. The alidade swings to sight three stars in turn and the limb
 * gives each altitude. Polaris always reads your latitude. */
define({
  tag: 'astrolabe-alidade',
  fig: 4,
  name: 'Alidade, sighting',
  family: 'astrolabe',
  moments: ['search'],
  duration: 12,
  still: 0.26,
  mark: [58, 44, 300, 300],
  description: 'The back of the instrument. The alidade sights Polaris, Vega, and Arcturus in turn and reads each altitude off the limb. Polaris always reads your latitude.',
  use: 'Search: lookalike search',
  keywords: ['alidade', 'sighting', 'altitude', 'search', 'find', 'lookalike', 'target', 'measure', 'back'],
  params: {
    latitude: { type: 'range', min: 5, max: 66, step: 0.1, default: 40.72, label: 'Observer latitude', unit: '°' },
  },
  build(k, o) {
    const C = [226, 202];
    const R = 124;
    const top = k.g(null, null, { transform: `translate(${C[0]} ${C[1]})` });
    const targets = [
      { name: 'Polaris', alt: o.latitude },
      { name: 'Vega', alt: Math.min(84, o.latitude + 21.3) },
      { name: 'Arcturus', alt: Math.max(8, o.latitude - 17.6) },
    ];
    drawThrone(k, top, R);
    // rings: altitude scale, zodiac, eccentric calendar
    k.path(top, P.circle(0, 0, R), 'o');
    k.path(top, P.circle(0, 0, 111), 'm');
    k.path(top, P.line(-R, 0, R, 0) + P.line(0, -R, 0, R), 'f');
    if (!k.mark) {
      k.path(top, P.ticks(0, 0, R - 4.5, R, -Math.PI, 0, 90), 'h');
      k.path(top, P.ticks(0, 0, R - 8, R, -Math.PI, 0, 18), 'f');
      k.path(top, P.ticks(0, 0, R - 12, R, -Math.PI, 0, 9), 'f');
      k.path(top, P.circle(0, 0, 96), 'f');
      k.path(top, P.ticks(0, 0, 96, 111, 0, TAU, 12), 'f');
      k.path(top, P.ticks(0, 0, 96, 102, 0, TAU, 36), 'h');
      const ec = [-2.4, 1.6];
      k.path(top, P.circle(ec[0], ec[1], 93.5) + P.circle(ec[0], ec[1], 80), 'f');
      const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      let acc = 0, dm = '';
      const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      days.forEach((d, i) => {
        const a = -Math.PI / 2 + (acc / 365) * TAU;
        dm += P.line(...polar(ec[0], ec[1], 80, a), ...polar(ec[0], ec[1], 93.5, a));
        if (k.L) {
          const am = -Math.PI / 2 + ((acc + d / 2) / 365) * TAU;
          const [x, y] = polar(ec[0], ec[1], 86.8, am);
          k.text(top, x, y, month[i], 't ts', 'middle');
        }
        acc += d;
      });
      k.path(top, dm, 'f');
      // shadow square with its graduated edges
      const q = 53;
      k.path(top, P.rect(-q, 0, 2 * q, q), 'f');
      k.path(top, P.line(0, 0, -q, q) + P.line(0, 0, q, q), 'h');
      let sq = '';
      for (let i = 1; i < 12; i++) {
        const u = (i / 12) * q;
        const L = i % 3 === 0 ? 5 : 2.8;
        sq += P.line(-q, u, -q + L, u) + P.line(q, u, q - L, u) + P.line(-q + (2 * q * i) / 12, q, -q + (2 * q * i) / 12, q - L);
      }
      k.path(top, sq, 'h');
      if (k.L) {
        for (let d = 10; d <= 80; d += 10) {
          const [x, y] = polar(0, 0, R + 9 * k.tu, -d * D2R);
          k.text(top, x, y, String(d), 't ts', 'middle');
        }
      }
    }
    // target stars beyond the limb
    const Rs = 176;
    const stars = targets.map((t) => {
      const [x, y] = polar(0, 0, Rs, -t.alt * D2R);
      const g = k.g(top);
      const dot = k.star(g, x, y, 0.2, 'fi');
      const ring = k.ring(g, x, y, 5, 'f a');
      const lb = k.L ? k.text(g, x + 9 * k.tu, y - 7 * k.tu, t.name, 't ts tn') : null;
      return { t, g, dot, ring, lb, x, y };
    });
    // alidade with vanes
    const al = k.g(top);
    const w = 3.6;
    k.path(al, `M${-R + 4} 0L-16 ${-w}L16 ${-w}L${R - 4} 0L16 ${w}L-16 ${w}Z`, 'm a');
    k.path(al, P.line(-R + 4, 0, R - 4, 0), 'h a');
    for (const u of [-74, 74]) {
      k.path(al, P.rect(u - 3, -9, 6, 18), 'm a');
      k.path(al, P.circle(u, 0, 1.3), 'f a');
    }
    k.path(al, P.circle(0, 0, 5.5), 'm a');
    const sight = k.path(al, P.line(80, 0, Rs + 12, 0), 'hd a');
    k.centre(top, 0, 0, 3, 'f');

    let read = null, readName = null, alL = null;
    if (k.L) {
      k.leader(top, polar(0, 0, R - 6, -2.45), [-150, -122], 'Altitude');
      k.leader(top, polar(0, 0, 104, 3.38), [-150, -30], 'Zodiac');
      k.leader(top, polar(-2.4, 1.6, 87, 2.72), [-150, 26], 'Calendar');
      k.leader(top, [-53, 40], [-150, 78], 'Umbra');
      alL = k.leader(top, [0, 0], [-150, 124], 'Alidade');
      read = k.text(null, 466, 314, '', 't tt', 'end');
      readName = k.text(null, 466, 332, '', 't ts', 'end');
      k.text(null, 466, 348, `Observer φ ${dms(o.latitude)} N`, 't ts tn q', 'end');
    }

    const segs = [
      { from: 2, to: 0, m0: 0, m1: 0.18, h1: 0.33 },
      { from: 0, to: 1, m0: 0.33, m1: 0.51, h1: 0.66 },
      { from: 1, to: 2, m0: 0.66, m1: 0.84, h1: 1.0 },
    ];
    return (p) => {
      const sg = segs.find((x) => p < x.h1) || segs[2];
      const alt = lerp(targets[sg.from].alt, targets[sg.to].alt, ease.inOut(seg(p, sg.m0, sg.m1)));
      const cur = sg.to;
      const lock = seg(p, sg.m1 - 0.015, sg.m1 + 0.035);
      k.rotate(al, -alt * D2R);
      if (alL) {
        const an = polar(0, 0, 84, Math.PI - alt * D2R);
        alL.firstChild.setAttribute('d', P.poly([an, [-150, 124], [-150 - 9 * k.tu, 124]]));
        alL.childNodes[1].setAttribute('cx', n2(an[0]));
        alL.childNodes[1].setAttribute('cy', n2(an[1]));
      }
      stars.forEach((s, i) => {
        const on = i === cur ? lock : 0;
        k.fade(s.ring, on * (1 - 0.55 * seg(p, sg.m1 + 0.035, sg.h1)));
        s.ring.setAttribute('r', n2((4 + 6 * ease.out(on)) * k.pu));
        s.dot.setAttribute('class', on > 0.5 ? 'fa' : 'fi');
        if (s.lb) k.fade(s.lb, 0.45 + 0.55 * on);
      });
      k.fade(sight, 0.3 + 0.7 * lock);
      if (read) {
        k.retext(read, `ALT ${dms(alt)}`);
        k.retext(readName, lock >= 1 ? targets[cur].name.toUpperCase() : 'SIGHTING');
        read.setAttribute('class', lock >= 1 ? 't tt ta' : 't tt');
      }
    };
  },
});

/* src/figures/05-limb.js */
/* FIG. 05: Limb, counting. The graduated rim as a progress scale. With no
 * progress attribute it fills and clears (indeterminate). Set
 * progress="0–100" and the index eases to that value. */
define({
  tag: 'astrolabe-limb',
  fig: 5,
  name: 'Limb, counting',
  family: 'astrolabe',
  moments: ['enrichment', 'upload'],
  duration: 6,
  still: 0.45,
  mark: [90, 30, 300, 300],
  description: 'The graduated limb as a progress scale. With no value it fills and clears. Given a progress value, the index eases to it and the passed degrees are marked.',
  use: 'Enrichment: progress with a percentage',
  keywords: ['limb', 'progress', 'percent', 'loader', 'spinner', 'determinate', 'scale', 'degrees'],
  params: {
    progress: { type: 'range', min: 0, max: 100, step: 1, default: -1, label: 'Progress', unit: '%', optional: true, live: true },
  },
  build(k, o) {
    const R = k.mark ? 142 : 138, Ri = k.mark ? 108 : 116;
    const top = k.g(null, null, { transform: 'translate(240 180)' });
    const A0 = -Math.PI / 2;
    k.path(top, P.circle(0, 0, R), 'o');
    k.path(top, P.circle(0, 0, Ri), 'm');
    if (!k.mark) {
      const mid = Ri + (R - Ri) * 0.42;
      k.path(top, P.circle(0, 0, mid), 'f');
      k.path(top, P.ticks(0, 0, Ri, Ri + (mid - Ri) * 0.55, 0, TAU, 360 / 2), 'hh');
      k.path(top, P.ticks(0, 0, Ri, mid, 0, TAU, 72), 'f');
      let hd = '';
      for (let i = 0; i < 24; i++) {
        if (k.L && i % 2 === 0) continue;
        const a = A0 + (i * TAU) / 24;
        hd += P.line(...polar(0, 0, mid, a), ...polar(0, 0, R - (R - mid) * 0.22, a));
      }
      k.path(top, hd, 'f');
      k.path(top, P.circle(0, 0, 36) + P.circle(0, 0, 8), 'f');
      if (k.L) {
        for (let i = 0; i < 12; i++) {
          const [x, y] = polar(0, 0, mid + (R - mid) * 0.52, A0 + (i * TAU) / 12);
          k.text(top, x, y, String(i * 30), 't ts', 'middle');
        }
      }
    } else {
      k.path(top, P.ticks(0, 0, Ri, R, 0, TAU, 24), 'f');
    }
    // progress band along the inner edge, and the index
    const band = k.path(top, '', k.mark ? 'o a' : 'o a');
    const bandW = k.mark ? (R - Ri) * 0.5 : (R - Ri) * 0.36;
    band.style.strokeWidth = `${n2(bandW)}px`;
    band.style.strokeLinecap = 'butt';
    const rb = Ri + bandW / 2 + (k.mark ? (R - Ri) * 0.25 : 1.2);
    const idx = k.g(top);
    k.path(idx, `M0 -${R + 6}L-4.5 -${R + 14}L4.5 -${R + 14}Z`, 'fa');
    k.path(idx, P.line(0, -Ri + 2, 0, k.mark ? -18 : -40), 'm a');
    k.path(idx, P.line(0, 8, 0, 30), 'f a');
    k.centre(top, 0, 0, 4, 'f');
    let read = null;
    if (k.L) {
      read = k.text(top, 0, 60, '', 't tt', 'middle');
      k.leader(top, polar(0, 0, R - 4, 2.45), [-176, 120], 'Limb');
      k.leader(top, polar(0, 0, Ri + 3, -0.5), [176, -122], 'Degrees');
      k.leader(top, [0, 20], [176, 118], 'Index');
    }

    let shown = 0, lastT = null;
    return (p, t) => {
      let a0 = 0, a1;
      const v = o.progress;
      if (v >= 0) {
        const dt = lastT == null ? 1 : Math.min(0.1, Math.max(0, t - lastT));
        lastT = t;
        const target = clamp(v / 100);
        shown = dt >= 1 ? target : shown + (target - shown) * (1 - Math.exp(-dt * 7));
        a1 = shown;
      } else {
        a1 = ease.inOut(seg(p, 0.02, 0.78));
        a0 = ease.inOut(seg(p, 0.8, 0.99));
        shown = a1;
      }
      const s0 = A0 + a0 * TAU, s1 = A0 + a1 * TAU;
      k.set(band, s1 - s0 > 0.002 ? P.arc(0, 0, rb, s0, s1) : '');
      k.rotate(idx, a1 * TAU);
      if (read) k.retext(read, v >= 0 ? `${Math.round(shown * 100)}%` : `${String(Math.round(a1 * 360)).padStart(3, '0')}°`);
    };
  },
});

/* src/figures/06-suspended.js */
/* FIG. 06: Astrolabe, suspended. Hung by its ring, the instrument is
 * nudged and swings as a damped pendulum until its meridian falls on
 * the plumb line. Then it is ready to take a sighting. */
define({
  tag: 'astrolabe-suspended',
  fig: 6,
  name: 'Astrolabe, suspended',
  family: 'astrolabe',
  moments: ['ready'],
  duration: 10,
  still: 0.97,
  mark: [70, 10, 340, 340],
  description: 'Hung by its ring, the instrument swings as a damped pendulum and settles plumb, its meridian on the vertical. Ready to take a sighting.',
  use: 'Ready: settled, success',
  keywords: ['suspended', 'pendulum', 'plumb', 'settle', 'success', 'ready', 'balance', 'swing'],
  params: {
    latitude: { type: 'range', min: 0, max: 66, step: 0.1, default: 40.72, label: 'Plate latitude', unit: '°' },
  },
  build(k, o) {
    const piv = [240, 26];
    const Rl = 112, Ro = 97.5;
    const G = astroGeo(96, o.latitude);
    const dist = Rl + 15 + 15; // ring centre to instrument centre
    // fixed: hook and plumb line
    if (!k.mark) k.path(null, P.line(piv[0], piv[1] + 4, piv[0], 352), 'c');
    k.path(null, `M${piv[0] - 16} ${piv[1] - 12}L${piv[0] + 16} ${piv[1] - 12}`, 'o');
    k.path(null, `M${piv[0]} ${piv[1] - 12}L${piv[0]} ${piv[1] - 3}`, 'm');
    const bob = k.g();
    if (!k.mark) {
      k.path(bob, P.line(piv[0], 338, piv[0] - 4, 346) + P.line(piv[0] - 4, 346, piv[0] + 4, 346) + P.line(piv[0] + 4, 346, piv[0], 338), 'f');
    }
    // swinging body: pivot at the ring centre
    const sw = k.g();
    const body = k.g(sw, null, { transform: `translate(0 ${dist})` });
    drawThrone(k, body, Rl);
    drawLimb(k, body, Ro + 1.5, Rl);
    drawPlate(k, body, G);
    const rete = drawRete(k, body, G);
    reteFrame(k, rete, G, 0.7, null);
    const rule = drawRule(k, body, Ro - 2, { cls: 'm' });
    k.rotate(rule, -0.35);
    const axis = k.path(body, P.line(0, -Rl - 30, 0, Rl + 8), 'f a');

    let ang = null, read = null;
    if (k.L) {
      ang = k.g(null);
      k.leader(null, [piv[0], piv[1] + 2], [piv[0] + 96, piv[1] + 8], 'Ring');
      k.leader(null, [piv[0], 318], [piv[0] + 110, 318], 'Plumb line');
      read = k.text(null, 14, 346, '', 't tt');
    }

    // damped pendulum: nudge, then decay toward plumb
    const A = 13 * D2R, zeta = 0.085, w = TAU / 1.7;
    const angle = (p, D) => {
      const kick = ease.out(seg(p, 0.02, 0.09));
      if (p < 0.09) return A * kick;
      const t = (p - 0.09) * D;
      const wd = w * Math.sqrt(1 - zeta * zeta);
      const env = Math.exp(-zeta * w * t);
      const settle = 1 - seg(p, 0.84, 0.9);
      return A * env * Math.cos(wd * t) * settle;
    };
    return (p) => {
      const th = angle(p, 10);
      sw.setAttribute('transform', `translate(${piv[0]} ${piv[1]}) rotate(${n2(th * R2D)})`);
      if (ang) {
        k.clear(ang);
        const deg = Math.abs(th * R2D);
        if (deg > 0.15) {
          const a0 = Math.PI / 2, a1 = Math.PI / 2 - th;
          k.dimAngle(ang, piv[0], piv[1], 250, Math.min(a0, a1), Math.max(a0, a1), null);
        }
        const plumb = deg < 0.15;
        k.retext(read, plumb ? 'PLUMB' : `SWING ${dms(deg)}`);
        read.setAttribute('class', plumb ? 't tt ta' : 't tt');
        axis.setAttribute('class', plumb ? 'f a' : 'f');
      }
    };
  },
});

/* src/figures/07-orrery.js */
/* FIG. 07: Orrery, inner planets. True eccentric orbits with perihelia in
 * their real directions, and planets moved by Kepler's equation over an
 * 8-year cycle in which Venus makes 13 orbits to Earth's 8. The inset
 * traces Venus as seen from Earth: five retrograde loops in eight years. */
const PLANETS = [
  { name: 'Mercury', a: 0.387, e: 0.2056, peri: 77.46, rev: 33, m0: 0.2, r: 2.2 },
  { name: 'Venus', a: 0.723, e: 0.0068, peri: 131.5, rev: 13, m0: 0.55, r: 3.2 },
  { name: 'Earth', a: 1.0, e: 0.0167, peri: 102.9, rev: 8, m0: 0.0, r: 3.4 },
  { name: 'Mars', a: 1.524, e: 0.0934, peri: 336.0, rev: 4, m0: 0.8, r: 2.8 },
];

define({
  tag: 'astrolabe-orrery',
  fig: 7,
  name: 'Orrery, inner planets',
  family: 'orbits',
  moments: ['hero'],
  duration: 32,
  still: 0.94,
  mark: [90, 30, 300, 300],
  description: 'Mercury, Venus, Earth, and Mars on their true eccentric orbits. The inset traces Venus as seen from Earth, five loops over the 8-year cycle. Periods are rounded so the loop closes.',
  use: 'Hero: marketing, audience overview',
  keywords: ['orrery', 'planets', 'solar system', 'orbit', 'venus', 'rose', 'pentagram', 'overview', 'hero', 'kepler'],
  build(k) {
    const S = k.mark ? 88 : 80;
    const C = k.mark ? [240, 180] : [190, 180];
    const top = k.g(null, null, { transform: `translate(${C[0]} ${C[1]})` });
    const pos = (pl, p) => orbitXY(pl.a * S, pl.e, TAU * (pl.rev * p + pl.m0), -pl.peri * D2R);

    for (const pl of PLANETS) {
      const pts = [];
      for (let i = 0; i <= 160; i++) pts.push(orbitXY(pl.a * S, pl.e, (i / 160) * TAU, -pl.peri * D2R));
      k.path(top, P.poly(pts, true), pl.name === 'Earth' ? 'm' : 'f');
      if (!k.mark) {
        const q = orbitXY(pl.a * S, pl.e, 0, -pl.peri * D2R);
        const L = Math.hypot(...q);
        k.path(top, P.line(q[0] * (1 - 4 / L), q[1] * (1 - 4 / L), q[0] * (1 + 4 / L), q[1] * (1 + 4 / L)), 'f');
      }
    }
    // Earth–Venus chords: a faint background layer, full detail only
    const chords = k.full ? k.path(top, '', 'hh', { style: 'opacity:.14' }) : null;
    const samples = [];
    if (chords) {
      const n = Math.round(8 * 12.2);
      for (let i = 0; i <= n; i++) {
        const E = pos(PLANETS[2], i / n), V = pos(PLANETS[1], i / n);
        samples.push(P.line(E[0], E[1], V[0], V[1]));
      }
    }
    k.body(top, 0, 0, 7, 'm');
    k.dot(top, 0, 0, 1.6);
    if (!k.mark) k.path(top, P.ticks(0, 0, 10, 14, 0, TAU, 12), 'f');
    const bodies = PLANETS.map((pl) => {
      const g = k.g(top);
      k.body(g, 0, 0, pl.r * (k.mark ? 2.4 : 1.25), pl.name === 'Earth' ? 'fa' : 'fi');
      const lb = k.L ? k.text(top, 0, 0, pl.name, 't ts') : null;
      return { pl, g, lb };
    });

    // inset: Venus relative to Earth, the five-loop rose
    let rose = null;
    if (!k.mark) {
      const I = [406, 172], RI = 60, SI = RI / 1.74;
      const geo = (p) => {
        const E = pos(PLANETS[2], p), V = pos(PLANETS[1], p);
        return [I[0] + ((V[0] - E[0]) / S) * SI, I[1] + ((V[1] - E[1]) / S) * SI];
      };
      const N = 480, path = [];
      for (let i = 0; i <= N; i++) path.push(geo(i / N));
      k.path(null, P.circle(I[0], I[1], RI + 8), 'f');
      k.path(null, P.poly(path), 'hh');
      rose = { geo, path, N, line: k.path(null, '', 'm a'), dot: k.dot(null, 0, 0, 2.2, 'fa') };
      k.centre(null, I[0], I[1], 3.5, 'f');
      if (k.L) {
        k.text(null, I[0], I[1] - RI - 20, 'Venus from Earth', 't ts', 'middle');
        k.text(null, I[0], I[1] + RI + 22, '5 loops · 8 years', 't ts tn q', 'middle');
      }
    }
    let yr = null;
    if (k.L) {
      yr = k.text(null, 14, 346, '', 't tt');
      k.text(null, 14, 330, 'Year', 't ts q');
      k.text(null, 466, 346, `Scale 1 AU = ${S}`, 't ts tn q', 'end');
    }
    return (p) => {
      for (const b of bodies) {
        const [x, y] = pos(b.pl, p);
        k.move(b.g, x, y);
        if (b.lb) {
          const L = Math.hypot(x, y) || 1;
          b.lb.setAttribute('x', n2(x + (x / L) * 10 * k.tu));
          b.lb.setAttribute('y', n2(y + (y / L) * 10 * k.tu + 2.7 * k.tu));
          b.lb.setAttribute('text-anchor', x > 8 ? 'start' : x < -8 ? 'end' : 'middle');
        }
      }
      const drawn = seg(p, 0, 0.96);
      if (chords) {
        const n = Math.floor(drawn * (samples.length - 1));
        k.set(chords, samples.slice(0, n + 1).join(''));
      }
      if (rose) {
        const n = Math.floor(drawn * rose.N);
        const head = rose.geo(p);
        k.set(rose.line, P.poly(rose.path.slice(0, n + 1).concat([head])));
        k.move(rose.dot, head[0], head[1]);
        rose.dot.setAttribute('cx', 0);
        rose.dot.setAttribute('cy', 0);
        k.fade(rose.line, 1 - ease.inOut(seg(p, 0.96, 1)));
      }
      if (chords) chords.style.opacity = String(n2(0.14 * (1 - ease.inOut(seg(p, 0.96, 1)))));
      if (yr) k.retext(yr, (p * 8).toFixed(2).padStart(5, '0'));
    };
  },
});

/* src/figures/08-transfer.js */
/* FIG. 08: Transfer, Earth to Mars. A Hohmann transfer with true numbers:
 * depart when Mars leads by 44°, coast 259 days along a half ellipse,
 * arrive at aphelion as Mars does. Burns are heliocentric Δv. */
define({
  tag: 'astrolabe-transfer',
  fig: 8,
  name: 'Transfer, Earth to Mars',
  family: 'orbits',
  moments: ['strategy'],
  duration: 12,
  still: 0.72,
  mark: [70, 20, 320, 320],
  description: 'A Hohmann transfer: depart when Mars leads by 44°, coast 259 days on half an ellipse, and arrive at aphelion together with Mars. The burns are 2.9 and 2.6 km/s.',
  use: 'Strategy: plotting a channel strategy',
  keywords: ['transfer', 'hohmann', 'mars', 'trajectory', 'mission', 'path', 'strategy', 'plan'],
  build(k) {
    const S = 94, r1 = 1.0 * S, r2 = 1.524 * S;
    const C = k.L ? [220, 184] : [240, 180];
    const a = (r1 + r2) / 2, e = (r2 - r1) / (r2 + r1);
    const T = 258.9, TE = 365.25, TM = 687.0, phase = 44.3 * D2R;
    const th0 = 205 * D2R; // departure direction (screen angle, measured CCW as negative)
    const top = k.g(null, null, { transform: `translate(${C[0]} ${C[1]})` });
    const P0 = (r, th) => [r * Math.cos(-th), r * Math.sin(-th)];

    k.path(top, P.circle(0, 0, r1), 'm');
    k.path(top, P.circle(0, 0, r2), 'm');
    k.body(top, 0, 0, 6, 'm');
    k.dot(top, 0, 0, 1.5);
    if (!k.mark) k.path(top, P.ticks(0, 0, 9, 12.5, 0, TAU, 12), 'f');
    // transfer ellipse: perihelion at departure, aphelion opposite
    const craftAt = (tau) => {
      const M = Math.PI * clamp(tau / T);
      const E = kepler(M, e);
      const x = a * (Math.cos(E) - e), y = a * Math.sqrt(1 - e * e) * Math.sin(E);
      const c = Math.cos(th0), s = Math.sin(th0);
      const X = x * c - y * s, Y = x * s + y * c;
      return [X, -Y];
    };
    const future = [];
    for (let i = 0; i <= 96; i++) future.push(craftAt((i / 96) * T));
    k.path(top, P.poly(future), 'dt');
    if (!k.mark) {
      k.path(top, P.line(...P0(r1 + 14, th0), ...P0(-(r2 + 14), th0)), 'c');
    }
    const trail = k.path(top, '', 'm a');
    const earth = k.g(top); k.body(earth, 0, 0, 5, 'fi');
    const mars = k.g(top); k.body(mars, 0, 0, 4.2, 'fi');
    const craft = k.g(top);
    k.path(craft, 'M-4 -3L5 0L-4 3Z', 'fa');
    const burn1 = k.path(top, '', 'm a', { 'marker-end': 'url(#aa)' });
    const burn2 = k.path(top, '', 'm a', { 'marker-end': 'url(#aa)' });
    const phaseG = k.g(top);

    let lbE = null, lbM = null, day = null, b1 = null, b2 = null;
    if (k.L) {
      lbE = k.text(top, 0, 0, 'Earth', 't ts');
      lbM = k.text(top, 0, 0, 'Mars', 't ts');
      const mid = craftAt(T * 0.55);
      k.leader(top, mid, [150, -150], 'Transfer', { side: 'r' });
      b1 = k.text(top, 0, 0, 'Δv₁ 2.9 km/s', 't ts tn ta');
      b2 = k.text(top, 0, 0, 'Δv₂ 2.6 km/s', 't ts tn ta');
      day = k.text(null, 14, 346, '', 't tt');
      k.text(null, 14, 330, 'Mission day', 't ts q');
      k.text(null, 466, 346, 'Hohmann · 259 days', 't ts tn q', 'end');
    }
    // direction of counter-clockwise motion at math angle th, in screen coordinates
    const vel = (th) => [-Math.sin(th), -Math.cos(th)];
    const tangent = (th, len, from) => {
      const d = vel(th);
      return P.line(from[0], from[1], from[0] + d[0] * len, from[1] + d[1] * len);
    };

    return (p) => {
      // mission time: approach, transfer, ride along, fade
      let tau;
      if (p < 0.12) tau = lerp(-40, 0, ease.out(seg(p, 0, 0.12)));
      else if (p < 0.66) tau = T * seg(p, 0.12, 0.66);
      else tau = T + 70 * ease.out(seg(p, 0.66, 0.9));
      const vis = Math.min(ease.inOut(seg(p, 0, 0.05)), 1 - ease.inOut(seg(p, 0.92, 0.99)));
      const thE = th0 + (TAU * tau) / TE;
      const thM = th0 + phase + (TAU * tau) / TM;
      const pe = P0(r1, thE), pm = P0(r2, thM);
      k.move(earth, pe[0], pe[1]);
      k.move(mars, pm[0], pm[1]);
      const flying = tau > 0 && tau < T;
      const cpos = tau <= 0 ? pe : tau >= T ? pm : craftAt(tau);
      const cnext = craftAt(Math.min(T, Math.max(0, tau) + 2));
      const ang = tau >= T || tau <= 0 ? Math.atan2(...(tau <= 0 ? vel(thE) : vel(thM)).reverse()) : Math.atan2(cnext[1] - cpos[1], cnext[0] - cpos[0]);
      k.move(craft, cpos[0], cpos[1], ang);
      const n = Math.max(0, Math.min(96, Math.round((clamp(tau / T)) * 96)));
      k.set(trail, n > 0 ? P.poly(future.slice(0, n + 1).concat(flying ? [cpos] : [])) : '');
      // burns
      const b1a = seg(p, 0.1, 0.13) * (1 - seg(p, 0.2, 0.26));
      const b2a = seg(p, 0.64, 0.67) * (1 - seg(p, 0.78, 0.84));
      const dep = P0(r1, th0), arr = P0(r2, th0 + Math.PI);
      k.set(burn1, tangent(th0, 26, dep));
      k.set(burn2, tangent(th0 + Math.PI, 22, arr));
      k.fade(burn1, b1a * vis);
      k.fade(burn2, b2a * vis);
      if (b1) {
        b1.setAttribute('x', n2(dep[0] - 10)); b1.setAttribute('y', n2(dep[1] + 18));
        b2.setAttribute('x', n2(arr[0] + 6)); b2.setAttribute('y', n2(arr[1] - 12));
        k.fade(b1, b1a * vis); k.fade(b2, b2a * vis);
      }
      // phase angle at departure
      k.clear(phaseG);
      const pa = seg(p, 0.05, 0.1) * (1 - seg(p, 0.16, 0.22));
      if (pa > 0 && !k.mark) {
        const s0 = -(thE + phase), s1 = -thE;
        k.path(phaseG, P.line(0, 0, ...P0(r2 + 8, thM)) + P.line(0, 0, ...P0(r2 + 8, thE)), 'dm');
        k.dimAngle(phaseG, 0, 0, r2 * 0.62, Math.min(s0, s1), Math.max(s0, s1), k.L ? '44°' : null);
        k.fade(phaseG, pa * vis);
      }
      k.fade(earth, vis); k.fade(mars, vis); k.fade(craft, vis); k.fade(trail, vis);
      if (lbE) {
        const L = (q, d) => [q[0] * (1 + d / Math.hypot(...q)), q[1] * (1 + d / Math.hypot(...q))];
        const le = L(pe, 12), lm = L(pm, 12);
        lbE.setAttribute('x', n2(le[0])); lbE.setAttribute('y', n2(le[1] + 3));
        lbM.setAttribute('x', n2(lm[0])); lbM.setAttribute('y', n2(lm[1] + 3));
        lbE.setAttribute('text-anchor', pe[0] >= 0 ? 'start' : 'end');
        lbM.setAttribute('text-anchor', pm[0] >= 0 ? 'start' : 'end');
        k.retext(day, `T${tau < 0 ? '−' : '+'}${String(Math.abs(Math.round(tau))).padStart(3, '0')} D`);
      }
    };
  },
});

/* src/figures/09-phases.js */
/* FIG. 09: Phases, one lunation. Sunlight arrives from the right. The Moon
 * circles Earth with its lit half always facing the Sun; the inset shows
 * the same Moon as seen from Earth. One synodic month per loop. */
const PHASE_NAMES = ['New moon', 'Waxing crescent', 'First quarter', 'Waxing gibbous', 'Full moon',
  'Waning gibbous', 'Last quarter', 'Waning crescent'];

define({
  tag: 'astrolabe-phases',
  fig: 9,
  name: 'Phases, one lunation',
  family: 'orbits',
  moments: ['report'],
  duration: 16,
  still: 0.3,
  mark: [70, 30, 300, 300],
  description: 'Sunlight arrives from the right while the Moon circles Earth, lit half always toward the Sun. The inset shows the phase as seen from Earth. One synodic month per loop.',
  use: 'Report: monthly cycles, retention',
  keywords: ['moon', 'phases', 'lunar', 'cycle', 'month', 'retention', 'recurring', 'terminator'],
  build(k) {
    const E = k.mark ? [220, 180] : [196, 176];
    const Ro = k.mark ? 120 : 112, Re = k.mark ? 26 : 17, Rm = k.mark ? 16 : 9.5;
    const hatch = k.hatch(45, 3.2, 'f');
    // sunlight
    if (!k.mark) {
      for (let i = -2; i <= 2; i++) {
        const y = E[1] + i * 46;
        k.path(null, P.line(472, y, 402, y), 'f', { 'marker-end': 'url(#ar)' });
      }
    }
    k.path(null, P.circle(E[0], E[1], Ro), k.mark ? 'f' : 'f');
    if (!k.mark) {
      k.path(null, P.ticks(E[0], E[1], Ro - 3, Ro + 3, 0, TAU, 8), 'f');
      k.path(null, P.line(E[0] - Ro - 12, E[1], E[0] + Ro + 12, E[1]), 'c');
    }
    // Earth: night side hatched (away from the Sun, on the left)
    const half = (cx, cy, r) => `M${n2(cx)} ${n2(cy - r)}A${n2(r)} ${n2(r)} 0 0 0 ${n2(cx)} ${n2(cy + r)}Z`;
    k.path(null, half(E[0], E[1], Re), 'f', { fill: hatch });
    k.body(null, E[0], E[1], Re, 'm');
    k.path(null, P.line(E[0], E[1] - Re, E[0], E[1] + Re), 'f');
    // Moon
    const moon = k.g();
    k.path(moon, half(0, 0, Rm), 'f', { fill: hatch });
    k.body(moon, 0, 0, Rm, 'm a');
    k.path(moon, P.line(0, -Rm, 0, Rm), 'f a');
    // inset: the Moon as seen from Earth
    let inset = null;
    if (!k.mark) {
      const I = [66, 70], Ri = 30;
      inset = { I, Ri, dark: k.path(null, '', 'f', { fill: hatch }), term: k.path(null, '', 'f') };
      k.body(null, I[0], I[1], Ri, 'm');
      if (k.L) {
        k.text(null, I[0], I[1] - Ri - 12, 'Seen from Earth', 't ts', 'middle');
        inset.name = k.text(null, I[0], I[1] + Ri + 13, '', 't ts', 'middle');
        inset.pct = k.text(null, I[0], I[1] + Ri + 25, '', 't ts tn q', 'middle');
      }
    }
    let day = null;
    if (k.L) {
      k.text(null, 466, 346, 'Synodic month 29.53 d', 't ts tn q', 'end');
      k.text(null, 438, E[1] + 2 * 46 + 13, 'Sunlight', 't ts', 'middle');
      k.leader(null, [E[0] - Re * 0.55, E[1] + Re * 0.5], [118, 298], 'Night side', { side: 'r' });
      day = k.text(null, 14, 346, '', 't tt');
      k.text(null, 14, 330, 'Day', 't ts q');
    }
    return (p) => {
      const al = p * TAU; // elongation from the Sun
      const mx = E[0] + Ro * Math.cos(al), my = E[1] - Ro * Math.sin(al);
      k.move(moon, mx, my);
      if (inset) {
        const { I, Ri } = inset;
        const c = Math.cos(al);
        const waxing = al <= Math.PI;
        const rx = n2(Math.abs(c) * Ri);
        // dark region: the unlit limb plus the part up to the terminator
        const s = c > 0 ? 0 : 1; // crescent vs gibbous
        let d = `M0 ${-Ri}A${Ri} ${Ri} 0 0 0 0 ${Ri}A${rx} ${Ri} 0 0 ${s} 0 ${-Ri}Z`;
        const fx = waxing ? 1 : -1;
        inset.dark.setAttribute('d', d);
        inset.dark.setAttribute('transform', `translate(${I[0]} ${I[1]}) scale(${fx} 1)`);
        inset.term.setAttribute('d', `M0 ${-Ri}A${rx} ${Ri} 0 0 ${s} 0 ${Ri}`);
        inset.term.setAttribute('transform', `translate(${I[0]} ${I[1]}) scale(${fx} 1)`);
        if (inset.name) {
          k.retext(inset.name, PHASE_NAMES[Math.round(p * 8) % 8].toUpperCase());
          k.retext(inset.pct, `${Math.round(((1 - c) / 2) * 100)}% lit`);
        }
      }
      if (day) k.retext(day, (p * 29.53).toFixed(1).padStart(4, '0'));
    };
  },
});

/* src/figures/10-eclipse.js */
/* FIG. 10: Eclipse, shadow geometry. External tangents from Sun to Moon
 * close into the umbra, internal tangents open into the penumbra. As the
 * Moon crosses the line of centres, the shadow sweeps over Earth. Only
 * near perfect alignment does the umbra reach the ground. */
define({
  tag: 'astrolabe-eclipse',
  fig: 10,
  name: 'Eclipse, shadow geometry',
  family: 'orbits',
  moments: ['report'],
  duration: 12,
  still: 0.46,
  mark: [90, 30, 300, 300],
  description: 'Tangent lines from Sun to Moon close into the umbra and open into the penumbra. As the Moon crosses the line of centres, the shadow sweeps across Earth, and only near perfect alignment does the umbra reach the ground.',
  use: 'Report: target vs actual buyers',
  keywords: ['eclipse', 'umbra', 'penumbra', 'shadow', 'alignment', 'match', 'overlap', 'geometry'],
  build(k) {
    const MK = k.mark;
    const Sn = MK ? [150, 180] : [8, 180], rs = MK ? 44 : 84;
    const Ea = MK ? [350, 180] : [404, 180], re = MK ? 26 : 42;
    const mxX = MK ? 262 : 268, rm = MK ? 17 : 25;
    const hatch = k.hatch(-45, 3, 'f');
    const clipR = k.clip(MK ? P.rect(90, 30, 300, 300) : P.rect(8, 8, 464, 344));
    if (!MK) k.path(null, P.rect(8, 8, 464, 344), 'hh');
    const world = k.g(null, null, { 'clip-path': clipR.ref });
    k.path(world, P.circle(Sn[0], Sn[1], rs), 'o');
    if (!k.mark) k.path(world, P.ticks(Sn[0], Sn[1], rs + 5, rs + 11, -1.1, 1.1, 14), 'f');
    k.path(world, P.line(Sn[0], Sn[1], 476, Sn[1]), 'c');
    // earth
    k.body(world, Ea[0], Ea[1], re, 'm');
    if (!k.mark) {
      k.path(world, P.ellipse(Ea[0], Ea[1], re, re * 0.32) + P.line(Ea[0], Ea[1] - re, Ea[0], Ea[1] + re), 'h');
      k.path(world, P.line(mxX, 44, mxX, 316), 'dt');
    }
    const pen = k.path(world, '', 'fs');
    const penL = k.path(world, '', 'f');
    const umb = k.path(world, '', 'f a', { fill: hatch });
    const umbL = k.path(world, '', 'm a');
    const cap = k.path(world, '', 'o a');
    const moon = k.g(world);
    k.body(moon, 0, 0, rm, 'm');
    k.dot(moon, 0, 0, 1.2);
    let read = null;
    const lb = {};
    if (k.L) {
      lb.sun = k.text(null, 22, 40, 'Sun', 't');
      lb.earth = k.text(null, Ea[0], Ea[1] + re + 16, 'Earth', 't', 'middle');
      lb.moon = k.text(null, 0, 0, 'Moon', 't');
      lb.umbra = k.text(null, 0, 0, 'Umbra', 't ts ta');
      lb.pen = k.text(null, 0, 0, 'Penumbra', 't ts');
      read = k.text(null, 466, 346, '', 't tt', 'end');
      k.text(null, 14, 346, 'Not to scale', 't ts tn q');
    }
    const far = MK ? 392 : 520;
    const extend = (a, b, x) => {
      const t = (x - a[0]) / (b[0] - a[0]);
      return [x, a[1] + (b[1] - a[1]) * t];
    };
    return (p) => {
      const my = MK ? lerp(118, 242, ease.inOutSine(seg(p, 0.04, 0.9))) : lerp(58, 302, ease.inOutSine(seg(p, 0.04, 0.9)));
      const vis = Math.min(seg(p, 0, 0.04), 1 - seg(p, 0.9, 0.98));
      const M = [mxX, my];
      k.move(moon, M[0], M[1]);
      const tg = tangents(Sn, rs, M, rm);
      let touch = false;
      // umbra: external tangents converge past the Moon
      const apex = [Sn[0] + (M[0] - Sn[0]) * rs / (rs - rm), Sn[1] + (M[1] - Sn[1]) * rs / (rs - rm)];
      if (tg.ext.length === 2 && tg.int.length === 2) {
        const [e1, e2] = tg.ext;
        k.set(umbL, P.line(e1[0][0], e1[0][1], apex[0], apex[1]) + P.line(e2[0][0], e2[0][1], apex[0], apex[1]));
        k.set(umb, P.poly([e1[1], apex, e2[1]], true));
        const [i1, i2] = tg.int;
        const f1 = extend(i1[0], i1[1], far), f2 = extend(i2[0], i2[1], far);
        k.set(penL, P.line(i1[0][0], i1[0][1], f1[0], f1[1]) + P.line(i2[0][0], i2[0][1], f2[0], f2[1]));
        k.set(pen, P.poly([i1[1], f1, f2, i2[1]], true));
        const ya = extend(i1[0], i1[1], Ea[0] - re * 0.6)[1], yb = extend(i2[0], i2[1], Ea[0] - re * 0.6)[1];
        touch = Math.max(ya, yb) > Ea[1] - re * 0.8 && Math.min(ya, yb) < Ea[1] + re * 0.8;
      }
      // umbral contact: where the apex region meets Earth's surface
      const ax = [apex[0] - M[0], apex[1] - M[1]];
      const L = Math.hypot(...ax);
      const u = [ax[0] / L, ax[1] / L];
      // distance from Earth's centre to the umbra axis
      const w = [Ea[0] - M[0], Ea[1] - M[1]];
      const along = w[0] * u[0] + w[1] * u[1];
      const off = Math.abs(w[0] * u[1] - w[1] * u[0]);
      const hit = off < re && along - Math.sqrt(re * re - off * off) < L;
      if (hit) {
        const d0 = along - Math.sqrt(re * re - off * off);
        const q = [M[0] + u[0] * d0, M[1] + u[1] * d0];
        const a = Math.atan2(q[1] - Ea[1], q[0] - Ea[0]);
        const span = 0.16 * (1 - d0 / L);
        k.set(cap, P.arc(Ea[0], Ea[1], re, a - span, a + span));
      } else k.set(cap, '');
      k.fade(world, 1);
      for (const el of [moon, umb, umbL, pen, penL, cap]) k.fade(el, vis);
      if (read) {
        k.retext(read, hit ? 'TOTALITY' : touch ? 'PARTIAL' : 'NO ECLIPSE');
        read.setAttribute('class', hit ? 't tt ta' : 't tt');
        lb.moon.setAttribute('x', n2(M[0] + rm + 6)); lb.moon.setAttribute('y', n2(M[1] - rm - 2));
        lb.umbra.setAttribute('x', n2(lerp(M[0], apex[0], 0.55))); lb.umbra.setAttribute('y', n2(lerp(M[1], apex[1], 0.55) - 14));
        // ride the lower penumbra edge, clear of Earth
        if (tg.int.length === 2) {
          const low = tg.int[0][1][1] > tg.int[1][1][1] ? tg.int[0] : tg.int[1];
          const px = 318, py = extend(low[0], low[1], px)[1];
          lb.pen.setAttribute('x', px); lb.pen.setAttribute('y', n2(Math.min(326, py + 16)));
          lb.pen.setAttribute('text-anchor', 'start');
        }
        for (const key of ['moon', 'umbra', 'pen']) k.fade(lb[key], vis);
      }
    };
  },
});

/* src/figures/11-kepler.js */
/* FIG. 11: Kepler, equal areas. An orbit of eccentricity 0.6. On the first
 * lap the radius vector sweeps eight sectors in equal times, which are
 * visibly equal in area. On the second lap it wipes them away. The
 * velocity arrow follows the vis-viva equation. */
define({
  tag: 'astrolabe-kepler',
  fig: 11,
  name: 'Kepler, equal areas',
  family: 'orbits',
  moments: ['report'],
  duration: 16,
  still: 0.49,
  mark: [70, 20, 340, 340],
  description: 'An orbit with eccentricity 0.6. The radius vector sweeps eight sectors in equal times, and they are equal in area. The planet speeds up at perihelion and slows at aphelion.',
  use: 'Report: cadence over time',
  keywords: ['kepler', 'second law', 'equal areas', 'ellipse', 'orbit', 'cadence', 'velocity', 'time'],
  build(k) {
    const a = 168, e = 0.6, b = a * Math.sqrt(1 - e * e), c = a * e;
    const O = [236, 184], F = [O[0] + c, O[1]], F2 = [O[0] - c, O[1]];
    const hatchA = k.hatch(45, 3.4, 'f');
    const hatchB = k.hatch(-45, 3.4, 'f');
    const at = (M) => { const q = orbitXY(a, e, M, 0); return [F[0] + q[0], F[1] + q[1]]; };
    const N = 8;
    const sectorPath = (m0, m1) => {
      const pts = [F];
      const n = Math.max(2, Math.ceil(((m1 - m0) / TAU) * 180));
      for (let i = 0; i <= n; i++) pts.push(at(m0 + ((m1 - m0) * i) / n));
      return P.poly(pts, true);
    };
    const secs = [];
    for (let i = 0; i < N; i++) {
      secs.push(k.path(null, '', i % 2 ? 'f q' : 'f q', { fill: i % 2 ? hatchB : hatchA }));
    }
    k.path(null, P.ellipse(O[0], O[1], a, b), 'o');
    if (!k.mark) {
      k.path(null, P.line(O[0] - a - 14, O[1], O[0] + a + 14, O[1]), 'c');
      k.path(null, P.line(O[0], O[1] - b - 12, O[0], O[1] + b + 12), 'c');
      k.centre(null, F2[0], F2[1], 4, 'f');
    }
    k.body(null, F[0], F[1], 6.5, 'm');
    k.dot(null, F[0], F[1], 1.5);
    const rv = k.path(null, '', 'm a');
    const vel = k.path(null, '', 'm a', { 'marker-end': 'url(#aa)' });
    const planet = k.g();
    k.body(planet, 0, 0, k.mark ? 9 : 4.6, 'fa');
    let lA1 = null, lA2 = null, eq = null;
    if (k.L) {
      k.text(null, F[0] + 10, F[1] + 16, 'F', 't');
      k.text(null, F2[0] - 4, F2[1] + 16, 'F′', 't tn', 'end');
      k.text(null, O[0] + a + 8, O[1] - 10, 'Perihelion', 't ts');
      k.text(null, O[0] - a - 8, O[1] - 10, 'Aphelion', 't ts', 'end');
      k.dim(null, [O[0] - a, O[1] - b - 8], [O[0] + a, O[1] - b - 8], 10, '2a', { ext: false, textCls: 't tn' });
      const m0 = at(TAU * 0.5 / N), m4 = at(TAU * 4.5 / N);
      lA1 = k.text(null, lerp(F[0], m0[0], 0.62), lerp(F[1], m0[1], 0.62), 'A₁', 't tt tn', 'middle');
      lA2 = k.text(null, lerp(F[0], m4[0], 0.55), lerp(F[1], m4[1], 0.55) - 8, 'A₂', 't tt tn', 'middle');
      eq = k.text(null, 466, 346, 'A₁ = A₂ · equal areas, equal times', 't ts tn', 'end');
      k.text(null, 14, 346, 'e = 0.60', 't tt tn');
    }
    return (p) => {
      const lap = p < 0.5 ? 0 : 1;
      const q = (p % 0.5) / 0.5;
      const M = TAU * q;
      const pt = at(M);
      k.move(planet, pt[0], pt[1]);
      k.set(rv, P.line(F[0], F[1], pt[0], pt[1]));
      const r = Math.hypot(pt[0] - F[0], pt[1] - F[1]);
      const v = Math.sqrt(2 / r - 1 / a);
      const nx = at(M + 0.004);
      const d = Math.hypot(nx[0] - pt[0], nx[1] - pt[1]) || 1;
      const len = 9 + v * 260;
      k.set(vel, P.line(pt[0], pt[1], pt[0] + ((nx[0] - pt[0]) / d) * len, pt[1] + ((nx[1] - pt[1]) / d) * len));
      const cur = Math.floor(q * N);
      for (let i = 0; i < N; i++) {
        const m0 = (TAU * i) / N, m1 = (TAU * (i + 1)) / N;
        let dd = '';
        if (lap === 0) {
          if (i < cur) dd = sectorPath(m0, m1);
          else if (i === cur) dd = M - m0 > 1e-4 ? sectorPath(m0, M) : '';
        } else {
          if (i > cur) dd = sectorPath(m0, m1);
          else if (i === cur) dd = m1 - M > 1e-4 ? sectorPath(M, m1) : '';
        }
        k.set(secs[i], dd);
      }
      if (lA1) {
        const s0 = lap === 0 ? (cur > 0 ? 1 : 0) : (cur > 0 ? 0 : 1);
        const s4 = lap === 0 ? (cur > 4 ? 1 : 0) : (cur > 4 ? 0 : 1);
        k.fade(lA1, s0);
        k.fade(lA2, s4);
        k.fade(eq, s0 && s4 ? 1 : 0.35);
      }
    };
  },
});

/* src/figures/12-lagrange.js */
/* FIG. 12: Lagrange, balance points. Zero-velocity curves of the
 * restricted three-body problem in the co-rotating frame (mass ratio
 * μ = 0.1, exaggerated so the structure reads). Contours are traced
 * level by level, the five balance points are marked, and a satellite
 * librates on a tadpole path around L4. */
function lagrangeModel(mu) {
  const Om = (x, y) => {
    const r1 = Math.hypot(x + mu, y), r2 = Math.hypot(x - 1 + mu, y);
    return 0.5 * (x * x + y * y) + (1 - mu) / Math.max(r1, 0.04) + mu / Math.max(r2, 0.04);
  };
  const fx = (x) => x - ((1 - mu) * (x + mu)) / Math.abs(x + mu) ** 3 - (mu * (x - 1 + mu)) / Math.abs(x - 1 + mu) ** 3;
  const root = (lo, hi) => {
    let flo = fx(lo);
    for (let i = 0; i < 80; i++) {
      const mid = (lo + hi) / 2, fm = fx(mid);
      if ((fm > 0) === (flo > 0)) { lo = mid; flo = fm; } else hi = mid;
    }
    return (lo + hi) / 2;
  };
  const L1 = [root(-mu + 1e-3, 1 - mu - 1e-3), 0];
  const L2 = [root(1 - mu + 1e-3, 2), 0];
  const L3 = [root(-2, -mu - 1e-3), 0];
  const L4 = [0.5 - mu, Math.sqrt(3) / 2], L5 = [0.5 - mu, -Math.sqrt(3) / 2];
  return { Om, pts: { L1, L2, L3, L4, L5 } };
}

define({
  tag: 'astrolabe-lagrange',
  fig: 12,
  name: 'Lagrange, balance points',
  family: 'orbits',
  moments: ['segmentation'],
  duration: 18,
  still: 0.7,
  mark: [72, 12, 336, 336],
  description: 'Zero-velocity curves of the three-body problem in the rotating frame, traced level by level. The five balance points appear, and a satellite drifts on a tadpole orbit around L4. The mass ratio is exaggerated so the structure is visible.',
  use: 'Segmentation: stable segments',
  keywords: ['lagrange', 'three body', 'equilibrium', 'contours', 'potential', 'balance', 'stable', 'segments'],
  build(k) {
    const mu = 0.1;
    const S = 140, C = [228, 180];
    const toS = ([x, y]) => [C[0] + x * S, C[1] - y * S];
    const { Om, pts } = lagrangeModel(mu);
    const lv = { L1: Om(...pts.L1), L2: Om(...pts.L2), L3: Om(...pts.L3), L4: Om(...pts.L4) };
    const levels = k.mark
      ? [lv.L1, lv.L3, lv.L4 + 0.01]
      : [lv.L1 + 0.9, lv.L1 + 0.3, lv.L1 + 0.1, lv.L1, (lv.L1 + lv.L2) / 2, lv.L2, (lv.L2 + lv.L3) / 2, lv.L3,
        lv.L3 - (lv.L3 - lv.L4) * 0.4, lv.L3 - (lv.L3 - lv.L4) * 0.75, lv.L4 + 0.004];
    const nx = 170, ny = 130;
    const x0 = (-12 - C[0]) / S, x1 = (492 - C[0]) / S, y0 = (C[1] + 12) / S, y1 = (C[1] - 372) / S;
    const dx = (x1 - x0) / (nx - 1), dy = (y1 - y0) / (ny - 1);
    const field = new Float32Array(nx * ny);
    for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) field[j * nx + i] = Om(x0 + i * dx, y0 + j * dy);
    const clipR = k.clip(k.mark ? P.circle(240, 180, 166) : P.rect(8, 8, 464, 344));
    const gC = k.g(null, null, { 'clip-path': clipR.ref });
    k.path(null, k.mark ? P.circle(240, 180, 166) : P.rect(8, 8, 464, 344), k.mark ? 'm' : 'hh');
    const lines = [];
    const traced = [];
    let ghostD = '';
    levels.forEach((L, li) => {
      const ls = isolines(field, nx, ny, x0, y0, dx, dy, L);
      const sep = [lv.L1, lv.L2, lv.L3].includes(L);
      for (const pl of ls) {
        if (pl.length < 4) continue;
        const d = P.poly(pl.map(toS));
        ghostD += d;
        traced.push({ d, cls: sep ? 'm' : li % 2 ? 'h' : 'f', li });
      }
    });
    // the finished field sits faintly under the tracing, so no frame is empty
    k.path(gC, ghostD, 'hh');
    for (const t of traced) lines.push({ el: k.dpath(gC, t.d, t.cls), li: t.li });
    // bodies
    const m1 = toS([-mu, 0]), m2 = toS([1 - mu, 0]);
    k.body(null, m1[0], m1[1], k.mark ? 14 : 10, 'm');
    k.dot(null, m1[0], m1[1], 1.4);
    k.body(null, m2[0], m2[1], k.mark ? 8 : 5, 'fi');
    if (!k.mark) k.path(null, P.line(8, C[1], 472, C[1]), 'c');
    // balance points
    const marks = Object.entries(pts).map(([name, q]) => {
      const s = toS(q);
      const g = k.g();
      k.path(g, P.line(s[0] - 5, s[1] - 5, s[0] + 5, s[1] + 5) + P.line(s[0] - 5, s[1] + 5, s[0] + 5, s[1] - 5), 'm a');
      if (k.L) k.text(g, s[0] + 7, s[1] - 9, name, 't ts');
      return g;
    });
    // tadpole libration around L4
    const L4a = Math.atan2(pts.L4[1], pts.L4[0]), L4r = Math.hypot(...pts.L4);
    const tad = (u) => {
      const ph = L4a + 0.36 * Math.sin(u) + 0.05 * Math.sin(2 * u);
      const r = L4r + 0.028 * Math.cos(u);
      return toS([r * Math.cos(ph), r * Math.sin(ph)]);
    };
    const tp = [];
    for (let i = 0; i <= 90; i++) tp.push(tad((i / 90) * TAU));
    const tpath = k.path(null, P.poly(tp), 'dt a');
    const sat = k.g();
    k.body(sat, 0, 0, k.mark ? 5 : 3, 'fa');
    if (k.L) {
      k.text(null, 14, 330, 'Rotating frame', 't ts q');
      k.text(null, 14, 346, 'μ = 0.10', 't tt tn');
      k.text(null, 466, 346, 'Zero-velocity curves', 't ts tn q', 'end');
      const tl = k.leader(null, tad(Math.PI * 0.5), [370, 36], 'Tadpole', { side: 'r' });
      marks.push(tl);
    }
    const nLev = levels.length;
    return (p) => {
      const out = 1 - ease.inOut(seg(p, 0.92, 0.99));
      for (const l of lines) {
        const t0 = (l.li / nLev) * 0.38;
        k.draw(l.el, ease.inOut(seg(p, t0, t0 + 0.1)));
        k.fade(l.el, out);
      }
      const mk = ease.out(seg(p, 0.42, 0.5)) * out;
      for (const m of marks) k.fade(m, mk);
      const sv = seg(p, 0.48, 0.52) * out;
      k.fade(tpath, sv * 0.9);
      k.fade(sat, sv);
      const s = tad(seg(p, 0.48, 0.92) * TAU * 2);
      k.move(sat, s[0], s[1]);
    };
  },
});

/* src/figures/13-constellation.js */
/* FIG. 13: Constellation, resolving. Scattered points settle into their
 * true places (gnomonic projection of J2000 positions), then the figure
 * is joined line by line. Orion, the Plough, and Cassiopeia in turn. */
const FIGURES = [
  {
    name: 'Orion', ra: 5.6, dec: 0.2, scale: 1.0,
    stars: [
      ['Betelgeuse', 5.919, 7.407, 0.45], ['Rigel', 5.242, -8.202, 0.13], ['Bellatrix', 5.419, 6.35, 1.64],
      ['Saiph', 5.796, -9.67, 2.06], ['Alnitak', 5.679, -1.943, 1.77], ['Alnilam', 5.604, -1.202, 1.69],
      ['Mintaka', 5.533, -0.299, 2.23], ['Meissa', 5.585, 9.934, 3.39], ['', 5.59, -5.91, 2.77],
    ],
    lines: [[7, 0], [7, 2], [0, 4], [2, 6], [4, 5], [5, 6], [4, 3], [6, 1], [5, 8]],
  },
  {
    name: 'The Plough', ra: 12.4, dec: 55.6, scale: 0.95,
    stars: [
      ['Dubhe', 11.062, 61.751, 1.79], ['Merak', 11.031, 56.382, 2.37], ['Phecda', 11.897, 53.695, 2.44],
      ['Megrez', 12.257, 57.033, 3.31], ['Alioth', 12.9, 55.96, 1.77], ['Mizar', 13.399, 54.925, 2.27],
      ['Alkaid', 13.792, 49.313, 1.86],
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 0], [3, 4], [4, 5], [5, 6]],
  },
  {
    name: 'Cassiopeia', ra: 1.0, dec: 60.4, scale: 1.25,
    stars: [
      ['Caph', 0.153, 59.15, 2.28], ['Schedar', 0.675, 56.537, 2.24], ['Navi', 0.945, 60.717, 2.47],
      ['Ruchbah', 1.43, 60.235, 2.68], ['Segin', 1.907, 63.67, 3.37],
    ],
    lines: [[0, 1], [1, 2], [2, 3], [3, 4]],
  },
];

/** Gnomonic projection about (ra0, dec0); x grows east→left like a sky chart. */
function gnomonic(ra0, dec0, ra, dec) {
  const a0 = ra0 * 15 * D2R, d0 = dec0 * D2R, a = ra * 15 * D2R, d = dec * D2R;
  const cc = Math.sin(d0) * Math.sin(d) + Math.cos(d0) * Math.cos(d) * Math.cos(a - a0);
  const x = (Math.cos(d) * Math.sin(a - a0)) / cc;
  const y = (Math.cos(d0) * Math.sin(d) - Math.sin(d0) * Math.cos(d) * Math.cos(a - a0)) / cc;
  return [-x, -y];
}

define({
  tag: 'astrolabe-constellation',
  fig: 13,
  name: 'Constellation, resolving',
  family: 'sky',
  moments: ['segmentation'],
  duration: 21,
  still: 0.25,
  mark: [90, 30, 300, 300],
  description: 'Scattered points settle into their true positions and are joined into a figure: Orion, then the Plough, then Cassiopeia. Points resolving into a recognisable shape.',
  use: 'Segmentation: personas forming',
  keywords: ['constellation', 'stars', 'orion', 'segmentation', 'cluster', 'persona', 'pattern', 'connect'],
  build(k) {
    const C = [240, 176];
    if (!k.mark) {
      k.path(null, P.rect(10, 10, 460, 340), 'f');
      let tk = '';
      for (let x = 10; x <= 470; x += 23) tk += P.line(x, 10, x, 14) + P.line(x, 346, x, 350);
      for (let y = 10; y <= 350; y += 17) tk += P.line(10, y, 14, y) + P.line(466, y, 470, y);
      k.path(null, tk, 'hh');
    }
    // one persistent field of faint stars, so the plate never empties between figures
    const fr = rng(97);
    const nField = k.mark ? 0 : k.full ? 150 : 90;
    for (let i = 0; i < nField; i++) {
      k.star(null, 16 + fr() * 448, 16 + fr() * 328, 3.2 + Math.pow(fr(), 0.5) * 2.8, 'fi');
    }
    const scenes = FIGURES.map((F, fi) => {
      const g = k.g();
      const r = rng(97 + fi * 31);
      const proj = F.stars.map((s) => gnomonic(F.ra, F.dec, s[1], s[2]));
      const xs = proj.map((q) => q[0]), ys = proj.map((q) => q[1]);
      const w = Math.max(...xs) - Math.min(...xs), hgt = Math.max(...ys) - Math.min(...ys);
      const S = Math.min(300 / w, 240 / hgt) * F.scale * (k.mark ? 1.05 : 1);
      const mx = (Math.max(...xs) + Math.min(...xs)) / 2, my = (Math.max(...ys) + Math.min(...ys)) / 2;
      const pts = proj.map(([x, y]) => [C[0] + (x - mx) * S, C[1] + (y - my) * S]);
      const lines = F.lines.map(([i, j]) => k.dpath(g, P.line(...pts[i], ...pts[j]), 'm a'));
      const stars = F.stars.map((s, i) => {
        const sg = k.g(g);
        k.star(sg, 0, 0, s[3] - 0.6, 'fi');
        const ring = k.ring(sg, 0, 0, 3.6 + Math.max(0, 2 - s[3]) * 1.2, 'f a');
        const ang = r() * TAU, dist = 40 + r() * 70;
        return { sg, ring, from: [pts[i][0] + Math.cos(ang) * dist, pts[i][1] + Math.sin(ang) * dist], to: pts[i] };
      });
      const labels = k.g(g);
      if (k.L) {
        const top = Math.min(...pts.map((q) => q[1]));
        k.text(labels, C[0], Math.max(30, top - 22), F.name, 't tt', 'middle');
        F.stars.forEach((s, i) => {
          if (!s[0] || s[3] > 2.3) return;
          k.text(labels, pts[i][0] + 8, pts[i][1] + 9, s[0], 't ts tn');
        });
        k.text(labels, 22, 336, `RA ${Math.floor(F.ra)}h ${String(Math.round((F.ra % 1) * 60)).padStart(2, '0')}m  ·  Dec ${F.dec >= 0 ? '+' : '−'}${Math.abs(F.dec).toFixed(0)}°`, 't ts tn q');
      }
      return { g, lines, stars, labels };
    });
    return (p) => {
      const n = scenes.length;
      scenes.forEach((sc, i) => {
        const q = p * n - i;
        const on = q >= 0 && q < 1;
        k.fade(sc.g, on ? Math.min(ease.inOut(seg(q, 0, 0.08)), 1 - ease.inOut(seg(q, 0.9, 1))) : 0);
        if (!on) return;
        const settle = ease.inOut(seg(q, 0.06, 0.32));
        sc.stars.forEach((s, j) => {
          const x = lerp(s.from[0], s.to[0], settle), y = lerp(s.from[1], s.to[1], settle);
          k.move(s.sg, x, y);
          k.fade(s.ring, seg(q, 0.28, 0.34) * (1 - seg(q, 0.6, 0.7)));
          void j;
        });
        sc.lines.forEach((el, j) => {
          const t0 = 0.34 + (j / sc.lines.length) * 0.24;
          k.draw(el, ease.inOut(seg(q, t0, t0 + 0.08)));
        });
        k.fade(sc.labels, seg(q, 0.56, 0.66));
      });
    };
  },
});

/* src/figures/14-sphere.js */
/* FIG. 14: Celestial sphere, turning. An orthographic sphere tilted
 * toward the north pole, turning once per sidereal day. Hour circles,
 * parallels and the ecliptic are split at the limb into visible (solid)
 * and hidden (dashed) runs, with the vernal point γ marked. */
define({
  tag: 'astrolabe-sphere',
  fig: 14,
  name: 'Celestial sphere, turning',
  family: 'sky',
  moments: ['hero', 'enrichment'],
  duration: 24,
  still: 0.12,
  mark: [90, 30, 300, 300],
  description: 'A celestial sphere turning once per sidereal day. Hour circles, parallels, and the ecliptic are drawn solid in front and dashed behind, with the vernal point γ marked.',
  use: 'Hero: the full picture',
  keywords: ['sphere', 'globe', 'celestial', 'rotation', 'ecliptic', 'equator', 'overview', 'wireframe'],
  build(k) {
    const R = k.mark ? 142 : 128, C = [240, 182], tilt = 22 * D2R, eps = OBL * D2R;
    k.path(null, P.circle(C[0], C[1], R), 'o');
    const circles = [];
    const add = (pts, front, back, key) => {
      circles.push({ pts, front: k.path(null, '', front), back: back ? k.path(null, '', back) : null, key });
    };
    const Z = [0, 0, 1];
    const hours = k.mark ? 4 : k.full ? 12 : 8;
    for (let i = 0; i < hours; i++) {
      const a = (i / hours) * Math.PI;
      add(circle3([0, 0, 0], [Math.cos(a), Math.sin(a), 0], Z, 1, 96), 'h', k.mark ? null : 'hh');
    }
    const decs = k.mark ? [] : [-60, -30, 30, 60];
    for (const d of decs) {
      const r = Math.cos(d * D2R), zc = Math.sin(d * D2R);
      add(circle3([0, 0, zc], [1, 0, 0], [0, 1, 0], r, 96), 'f', 'hd');
    }
    add(circle3([0, 0, 0], [1, 0, 0], [0, 1, 0], 1, 128), 'm', k.mark ? null : 'hd', 'eq');
    add(circle3([0, 0, 0], [1, 0, 0], [0, Math.cos(eps), Math.sin(eps)], 1, 128), 'm a', k.mark ? null : 'hd a', 'ecl');
    // polar axis
    const axis = k.path(null, '', 'c');
    const poleN = k.dot(null, 0, 0, 2.2);
    const gam = k.g();
    k.ring(gam, 0, 0, 3.2, 'f a');
    k.dot(gam, 0, 0, 1.3, 'fa');
    let gl = null, eclL = null, nL = null;
    if (k.L) {
      k.text(null, C[0] + R + 8, C[1] + 4, 'Equator', 't ts');
      eclL = k.text(null, 0, 0, 'Ecliptic', 't ts ta');
      gl = k.text(null, 0, 0, 'γ', 't tt tn ta');
      nL = k.text(null, 0, 0, 'NCP', 't ts');
      k.text(null, 14, 346, 'ε = 23.44°', 't tt tn');
      k.text(null, 466, 346, 'Orthographic · hidden lines dashed', 't ts tn q', 'end');
    }
    const view = (v, spin) => {
      const x = v[0] * Math.cos(spin) - v[1] * Math.sin(spin);
      const y = v[0] * Math.sin(spin) + v[1] * Math.cos(spin);
      const z = v[2];
      const y2 = y * Math.cos(tilt) - z * Math.sin(tilt);
      const z2 = y * Math.sin(tilt) + z * Math.cos(tilt);
      return [C[0] + x * R, C[1] - z2 * R, -y2];
    };
    return (p) => {
      const spin = -p * TAU;
      let eclBest = null;
      for (const c of circles) {
        const pts = c.pts.map((v) => view(v, spin));
        const runs = splitRuns(pts, (q) => q[2] >= 0);
        let f = '', b = '';
        for (const run of runs) {
          const d = P.poly(run.pts);
          if (run.front) f += d; else b += d;
        }
        k.set(c.front, f);
        if (c.back) k.set(c.back, b);
        if (c.key === 'ecl' && eclL) {
          for (const q of pts) if (q[2] > 0.2 && (!eclBest || q[0] > eclBest[0])) eclBest = q;
        }
      }
      const n = view([0, 0, 1.22], spin), s = view([0, 0, -1.22], spin);
      k.set(axis, P.line(n[0], n[1], s[0], s[1]));
      const np = view([0, 0, 1], spin);
      k.move(poleN, np[0], np[1]);
      const g = view([1, 0, 0], spin);
      k.move(gam, g[0], g[1]);
      k.fade(gam, g[2] >= 0 ? 1 : 0.3);
      if (gl) {
        gl.setAttribute('x', n2(g[0] + 7)); gl.setAttribute('y', n2(g[1] - 6));
        k.fade(gl, g[2] >= 0 ? 1 : 0);
        if (eclBest) { eclL.setAttribute('x', n2(eclBest[0] + 8)); eclL.setAttribute('y', n2(eclBest[1] + 3)); }
        nL.setAttribute('x', n2(n[0] + 8)); nL.setAttribute('y', n2(n[1] + 3));
      }
    };
  },
});

/* src/figures/15-precession.js */
/* FIG. 15: Precession, 25,772 years. A chart centred on the ecliptic pole.
 * The celestial pole walks the 23.4° precession circle from Polaris
 * (today) past Deneb and Vega, then back through Thuban (2800 BC),
 * while the current equatorial grid travels with it. */
const PREC_STARS = [
  ['Polaris', 2.53, 89.264, 1.98], ['Kochab', 14.845, 74.155, 2.08], ['Pherkad', 15.346, 71.834, 3.0],
  ['Yildun', 17.537, 86.586, 4.35], ['', 16.766, 82.037, 4.2], ['', 15.734, 77.795, 4.3], ['', 16.292, 75.755, 4.9],
  ['Thuban', 14.073, 64.376, 3.65], ['Vega', 18.616, 38.784, 0.03], ['Deneb', 20.69, 45.28, 1.25],
  ['Alderamin', 21.31, 62.585, 2.45], ['Errai', 23.655, 77.632, 3.2], ['Eltanin', 17.943, 51.489, 2.23],
  ['Rastaban', 17.507, 52.301, 2.79], ['Caph', 0.153, 59.15, 2.28], ['Schedar', 0.675, 56.537, 2.24],
  ['Dubhe', 11.062, 61.751, 1.79], ['Merak', 11.031, 56.382, 2.37],
];
const PREC_LINES = [[0, 3], [3, 4], [4, 5], [5, 6], [6, 2], [2, 1], [1, 5]];

define({
  tag: 'astrolabe-precession',
  fig: 15,
  name: 'Precession, 25,772 years',
  family: 'sky',
  moments: ['report'],
  duration: 26,
  still: 0.02,
  mark: [90, 30, 300, 300],
  description: 'A chart centred on the ecliptic pole. The celestial pole walks its 23.4° circle, from Polaris today past Deneb and Vega and back through Thuban, while the equatorial grid travels with it.',
  use: 'Report: long-term trends',
  keywords: ['precession', 'pole star', 'polaris', 'vega', 'drift', 'trend', 'long term', 'time'],
  build(k) {
    const C = [240, 176], K = 470, e = OBL * D2R;
    const toEcl = (raH, dec) => {
      const a = raH * 15 * D2R, d = dec * D2R;
      const sb = Math.sin(d) * Math.cos(e) - Math.cos(d) * Math.sin(e) * Math.sin(a);
      const b = Math.asin(sb);
      const l = Math.atan2(Math.sin(d) * Math.sin(e) + Math.cos(d) * Math.cos(e) * Math.sin(a), Math.cos(d) * Math.cos(a));
      return [l, b];
    };
    const chart = (l, b) => {
      const r = K * Math.tan((Math.PI / 2 - b) / 2);
      return [C[0] + r * Math.cos(-l), C[1] + r * Math.sin(-l)];
    };
    // ecliptic longitude grid
    const rc = K * Math.tan(e / 2);
    if (!k.mark) {
      k.path(null, P.circle(C[0], C[1], rc * 0.5) + P.circle(C[0], C[1], rc * 1.5), 'hh');
      let sp = '';
      for (let i = 0; i < 12; i++) sp += P.line(...polar(C[0], C[1], 14, (i * TAU) / 12), ...polar(C[0], C[1], rc * 1.62, (i * TAU) / 12));
      k.path(null, sp, 'hh');
      k.centre(null, C[0], C[1], 5, 'f');
    }
    k.path(null, P.circle(C[0], C[1], rc), 'm');
    // year ticks every 1,000 years along the circle
    const lonAt = (year) => Math.PI / 2 - ((year - 2000) / 25772) * TAU;
    if (!k.mark) {
      let tk = '';
      for (let y = -12000; y < 13772; y += 1000) {
        const a = -lonAt(y);
        tk += P.line(...polar(C[0], C[1], rc - (y % 4000 ? 3 : 6), a), ...polar(C[0], C[1], rc + (y % 4000 ? 3 : 6), a));
      }
      k.path(null, tk, 'f');
    }
    // stars
    const S = PREC_STARS.map((s) => { const [l, b] = toEcl(s[1], s[2]); return { s, q: chart(l, b) }; });
    if (!k.mark) k.path(null, PREC_LINES.map(([i, j]) => P.line(...S[i].q, ...S[j].q)).join(''), 'h');
    for (const { s, q } of S) {
      if (k.mark && !['Polaris', 'Vega', 'Thuban'].includes(s[0])) continue;
      k.star(null, q[0], q[1], k.mark ? -1.2 : s[3] - 0.4, 'fi');
      if (k.L && s[0] && s[3] < 3.3) k.text(null, q[0] + 7, q[1] - 7, s[0], 't ts tn');
    }
    // moving pole, its trail, and the equatorial grid around it
    const trail = k.path(null, '', 'm a');
    const grid = k.mark ? [] : [70, 80].map(() => k.path(null, '', 'h a'));
    const pole = k.g();
    k.ring(pole, 0, 0, 5, 'm a');
    k.path(pole, P.line(-9, 0, -3, 0) + P.line(3, 0, 9, 0) + P.line(0, -9, 0, -3) + P.line(0, 3, 0, 9), 'f a');
    let yr = null;
    if (k.L) {
      k.text(null, C[0] + 8, C[1] + 14, 'Ecliptic pole', 't ts');
      yr = k.text(null, 14, 346, '', 't tt');
      k.text(null, 14, 330, 'North celestial pole', 't ts q');
      k.text(null, 466, 346, 'Precession circle 23.4°', 't ts tn q', 'end');
    }
    const fmt = (y) => (y < 0 ? `${Math.round(-y).toLocaleString('en-US')} BC` : `AD ${Math.round(y).toLocaleString('en-US')}`);
    return (p) => {
      let year = 2026 + p * 25772;
      if (year > 14800) year -= 25772;
      const lp = lonAt(2026 + p * 25772);
      const pc = chart(lp, Math.PI / 2 - e);
      k.move(pole, pc[0], pc[1]);
      const tr = [];
      for (let i = 0; i <= 40; i++) tr.push(chart(lp + (i / 40) * 0.9, Math.PI / 2 - e));
      k.set(trail, P.poly(tr));
      // declination circles around the moving pole
      const pv = [Math.cos(Math.PI / 2 - e) * Math.cos(lp), Math.cos(Math.PI / 2 - e) * Math.sin(lp), Math.sin(Math.PI / 2 - e)];
      const u0 = [-Math.sin(lp), Math.cos(lp), 0];
      const v0 = [pv[1] * u0[2] - pv[2] * u0[1], pv[2] * u0[0] - pv[0] * u0[2], pv[0] * u0[1] - pv[1] * u0[0]];
      [70, 80].forEach((dec, gi) => {
        if (!grid[gi]) return;
        const rho = (90 - dec) * D2R;
        const pts = [];
        for (let i = 0; i <= 72; i++) {
          const t = (i / 72) * TAU;
          const c = Math.cos(rho), s = Math.sin(rho);
          const v = [
            pv[0] * c + (u0[0] * Math.cos(t) + v0[0] * Math.sin(t)) * s,
            pv[1] * c + (u0[1] * Math.cos(t) + v0[1] * Math.sin(t)) * s,
            pv[2] * c + (u0[2] * Math.cos(t) + v0[2] * Math.sin(t)) * s,
          ];
          const b = Math.asin(clamp(v[2], -1, 1)), l = Math.atan2(v[1], v[0]);
          pts.push(chart(l, b));
        }
        k.set(grid[gi], P.poly(pts));
        k.fade(grid[gi], 1);
      });
      if (yr) k.retext(yr, fmt(year));
    };
  },
});

/* src/figures/16-startrails.js */
/* FIG. 16: Star trails, long exposure. Facing north, the shutter opens
 * and circumpolar stars draw concentric arcs around the celestial pole
 * at 15° an hour, above a graduated horizon and an observatory dome. */
define({
  tag: 'astrolabe-startrails',
  fig: 16,
  name: 'Star trails, exposure',
  family: 'sky',
  moments: ['upload'],
  duration: 14,
  still: 0.8,
  mark: [110, 0, 260, 260],
  description: 'Facing north with the shutter open. Circumpolar stars draw arcs around the celestial pole at 15° an hour until the exposure closes.',
  use: 'Upload: long-running processing',
  keywords: ['star trails', 'exposure', 'long', 'history', 'time', 'circumpolar', 'night', 'processing'],
  build(k) {
    const Pc = [240, 118], Hy = 300;
    const clipR = k.clip(P.rect(8, 8, 464, Hy - 8));
    const sky = k.g(null, null, { 'clip-path': clipR.ref });
    if (!k.mark) k.path(null, P.rect(8, 8, 464, Hy - 8), 'hh');
    const r = rng(7);
    const n = k.mark ? 6 : k.full ? 84 : 52;
    const stars = [];
    for (let i = 0; i < n; i++) {
      const rad = k.mark ? 26 + i * 19 : 8 + Math.pow(r(), 0.8) * 330;
      const a = k.mark ? i * 2.3 : r() * TAU;
      const mag = k.mark ? 1.5 : 1 + r() * 4.5;
      const cls = mag < 2.2 ? 'm' : mag < 3.8 ? 'f' : 'h';
      stars.push({ rad, a, mag, arc: k.path(sky, '', cls), head: k.star(sky, 0, 0, mag, 'fi') });
    }
    // Polaris, 0.7° from the pole
    const pol = { rad: 3.2, a: 0.6, arc: k.path(sky, '', 'm a'), head: k.star(sky, 0, 0, 1.2, 'fa') };
    k.centre(null, Pc[0], Pc[1], 5, 'f a');
    // horizon, azimuth scale, observatory dome
    k.path(null, P.line(8, Hy, 472, Hy), 'o');
    if (!k.mark) {
      let tk = '';
      for (let az = -60; az <= 60; az += 5) {
        const x = Pc[0] + az * 3.9;
        tk += P.line(x, Hy, x, Hy + (az % 15 ? 4 : 8));
      }
      k.path(null, tk, 'f');
      const dx = 392, dw = 44;
      k.path(null, P.rect(dx - dw / 2, Hy - 18, dw, 18), 'm');
      k.path(null, `M${dx - dw / 2} ${Hy - 18}A${dw / 2} ${dw / 2} 0 0 1 ${dx + dw / 2} ${Hy - 18}`, 'm');
      k.path(null, P.line(dx - 3, Hy - 18 - dw / 2 + 1, dx - 3, Hy - 18) + P.line(dx + 3, Hy - 18 - dw / 2 + 1, dx + 3, Hy - 18), 'f');
      k.path(null, P.line(dx, Hy - 30, dx + 22, Hy - 52), 'f');
      k.path(null, P.rect(dx - 8, Hy - 9, 7, 9), 'h');
    }
    let exp = null;
    if (k.L) {
      k.text(null, Pc[0], Hy + 20, 'N', 't', 'middle');
      k.text(null, Pc[0] - 45 * 3.9, Hy + 20, 'NW', 't ts', 'middle');
      k.text(null, Pc[0] + 45 * 3.9, Hy + 20, 'NE', 't ts', 'middle');
      k.leader(null, [Pc[0] + 3, Pc[1] - 3], [Pc[0] + 60, Pc[1] - 58], 'Celestial pole', { side: 'r' });
      exp = k.text(null, 14, 346, '', 't tt');
      k.text(null, 14, 330, 'Exposure', 't ts q');
      k.text(null, 466, 346, '15° per hour', 't ts tn q', 'end');
    }
    const draw = (s, L) => {
      // counter-clockwise on screen: the head advances to smaller angles
      const a1 = s.a - L;
      k.set(s.arc, L > 0.002 ? P.arc(Pc[0], Pc[1], s.rad, a1, s.a) : '');
      const [x, y] = polar(Pc[0], Pc[1], s.rad, a1);
      s.head.setAttribute('cx', n2(x));
      s.head.setAttribute('cy', n2(y));
    };
    return (p) => {
      const hours = 3.4 * ease.inOutSine(seg(p, 0.03, 0.84));
      const L = (hours / 24) * TAU;
      const vis = 1 - ease.inOut(seg(p, 0.88, 0.98));
      for (const s of stars) { draw(s, L); k.fade(s.arc, vis); k.fade(s.head, Math.max(vis, 0.35)); }
      draw(pol, L);
      k.fade(pol.arc, vis);
      if (exp) {
        const hh = Math.floor(hours), mm = Math.floor((hours - hh) * 60);
        k.retext(exp, `${String(hh).padStart(2, '0')}H ${String(mm).padStart(2, '0')}M`);
      }
    };
  },
});

/* src/figures/17-analemma.js */
/* FIG. 17: Analemma, one year. The Sun's position at the same clock time
 * each day: declination against the equation of time. Its figure of
 * eight comes from axial tilt plus orbital eccentricity. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_START = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

function solar(n) {
  const B = (TAU * (n - 81)) / 364;
  const eot = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
  const dec = 23.44 * Math.sin((TAU * (284 + n)) / 365);
  return [eot, dec];
}

define({
  tag: 'astrolabe-analemma',
  fig: 17,
  name: 'Analemma, one year',
  family: 'sky',
  moments: ['report'],
  duration: 16,
  still: 0.9,
  mark: [108, 22, 316, 316],
  description: "The Sun's position at the same clock time on every day of the year: declination against the equation of time. Axial tilt and orbital eccentricity make the figure of eight.",
  use: 'Report: seasonality',
  keywords: ['analemma', 'sun', 'seasons', 'year', 'seasonality', 'equation of time', 'solstice', 'pattern'],
  build(k) {
    const C = [258, 172], kx = 8.2, ky = 5.9;
    const at = (n) => { const [e, d] = solar(n); return [C[0] + e * kx, C[1] - d * ky]; };
    const pts = [];
    for (let n = 1; n <= 366; n++) pts.push(at(((n - 1) % 365) + 1));
    if (!k.mark) {
      const L = 72, B = 318, T = 26, Rr = 440;
      let grid = '';
      for (let m = -15; m <= 15; m += 5) grid += P.line(C[0] + m * kx, T, C[0] + m * kx, B);
      for (let d = -20; d <= 20; d += 10) grid += P.line(L, C[1] - d * ky, Rr, C[1] - d * ky);
      k.path(null, grid, 'hh');
      k.path(null, P.line(L, B, Rr, B) + P.line(L, T, L, B), 'f');
      let tk = '';
      for (let m = -15; m <= 15; m += 1) tk += P.line(C[0] + m * kx, B, C[0] + m * kx, B + (m % 5 ? 3 : 6));
      for (let d = -25; d <= 25; d += 5) tk += P.line(L, C[1] - d * ky, L - (d % 10 ? 3 : 6), C[1] - d * ky);
      k.path(null, tk, 'f');
      k.path(null, P.line(C[0], T - 6, C[0], B) + P.line(L, C[1], Rr + 6, C[1]), 'c');
      if (k.L) {
        for (let m = -15; m <= 15; m += 5) k.text(null, C[0] + m * kx, B + 15, (m > 0 ? '+' : '') + m, 't ts', 'middle');
        for (let d = -20; d <= 20; d += 10) k.text(null, L - 9, C[1] - d * ky, (d > 0 ? '+' : '') + d + '°', 't ts', 'end');
        k.text(null, Rr + 4, B + 15, 'Min', 't ts q', 'start');
        k.text(null, L + 6, T + 4, 'Declination', 't ts q');
        k.text(null, Rr, B + 29, 'Sundial fast →', 't ts tn q', 'end');
      }
    }
    k.path(null, P.poly(pts), 'hh');
    const curve = k.path(null, '', 'm');
    const trail = k.path(null, '', 'o a');
    const ticks = k.g();
    // month labels sit outside their own lobe, pushed away from its centroid
    const lobe = (hi) => {
      const q = pts.filter((pt) => (C[1] - pt[1]) / ky > 9.6 === hi);
      return [q.reduce((s0, pt) => s0 + pt[0], 0) / q.length, q.reduce((s0, pt) => s0 + pt[1], 0) / q.length];
    };
    const lobes = { hi: lobe(true), lo: lobe(false) };
    MONTH_START.forEach((d0, i) => {
      const a = at(d0 + 1), b = at(d0 + 3);
      const dx = b[0] - a[0], dy = b[1] - a[1], l = Math.hypot(dx, dy) || 1;
      const nx = -dy / l, ny = dx / l;
      k.path(ticks, P.line(a[0] - nx * 4, a[1] - ny * 4, a[0] + nx * 4, a[1] + ny * 4), 'f');
      if (k.L) {
        const c = (C[1] - a[1]) / ky > 9.6 ? lobes.hi : lobes.lo;
        let ox = a[0] - c[0], oy = a[1] - c[1];
        const ol = Math.hypot(ox, oy) || 1;
        ox /= ol; oy /= ol;
        const lx = a[0] + ox * 15, ly = a[1] + oy * 12;
        k.text(ticks, lx, ly, MONTHS[i], 't ts', ox > 0.35 ? 'start' : ox < -0.35 ? 'end' : 'middle');
      }
    });
    const sun = k.g();
    k.body(sun, 0, 0, k.mark ? 9 : 5.5, 'fa');
    if (!k.mark) k.path(sun, P.ticks(0, 0, 8, 12, 0, TAU, 8), 'f a');
    let read = null, read2 = null;
    if (k.L) {
      read = k.text(null, 466, 22, '', 't tt', 'end');
      read2 = k.text(null, 466, 38, '', 't ts tn', 'end');
      k.text(null, 14, 346, 'Same clock time, every day', 't ts tn q');
    }
    return (p) => {
      const q = seg(p, 0, 0.86);
      const n = 1 + q * 364.999;
      const idx = Math.floor(n) - 1;
      const head = at(n);
      k.move(sun, head[0], head[1]);
      k.set(curve, P.poly(pts.slice(0, idx + 1).concat([head])));
      k.set(trail, P.poly(pts.slice(Math.max(0, idx - 10), idx + 1).concat([head])));
      const vis = 1 - ease.inOut(seg(p, 0.9, 0.99));
      k.fade(curve, vis);
      k.fade(trail, vis);
      k.fade(ticks, 0.25 + 0.75 * vis);
      if (read) {
        const day = Math.floor(n);
        let mi = 11;
        while (mi > 0 && MONTH_START[mi] >= day) mi--;
        const [e, d] = solar(n);
        k.retext(read, `${MONTHS[mi].toUpperCase()} ${String(day - MONTH_START[mi]).padStart(2, '0')}`);
        k.retext(read2, `EoT ${e >= 0 ? '+' : '−'}${Math.abs(e).toFixed(1)} min  ·  δ ${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(1)}°`);
      }
    };
  },
});

/* src/figures/18-finder.js */
/* FIG. 18: Finder chart, locating. An equatorial chart of Orion, Taurus and
 * Andromeda with real bright-star positions. A reticle slews to the Orion
 * Nebula, the Pleiades and the Andromeda Galaxy in turn, locks on, and
 * reads out the coordinates. */
const FINDER_STARS = [
  [4.599, 16.51, 0.85], [5.242, -8.2, 0.13], [5.919, 7.41, 0.45], [5.419, 6.35, 1.64], [5.679, -1.94, 1.77],
  [5.604, -1.2, 1.69], [5.533, -0.3, 2.23], [5.796, -9.67, 2.06], [5.585, 9.93, 3.39], [2.12, 23.46, 2.0],
  [0.14, 29.09, 2.06], [1.162, 35.62, 2.05], [2.065, 42.33, 2.1], [3.038, 4.09, 2.54], [3.136, 40.96, 2.1],
  [5.438, 28.61, 1.65], [1.911, 20.81, 2.64], [0.656, 30.86, 3.27], [4.477, 15.87, 3.4], [4.33, 15.63, 3.6],
  [5.627, 21.14, 3.0], [3.413, 9.03, 3.47], [2.72, 3.24, 3.47], [0.855, 38.5, 3.87], [1.891, 19.29, 3.9],
  [5.108, -5.09, 2.79], [4.83, 6.96, 3.19], [4.853, 5.6, 3.69],
  [3.791, 24.105, 2.87], [3.819, 24.053, 3.62], [3.747, 24.113, 3.7], [3.764, 24.368, 3.87], [3.772, 23.948, 4.18],
  [3.755, 24.467, 4.3], [3.819, 24.137, 5.05],
];
const FINDER_TARGETS = [
  { id: 'M42', name: 'Orion Nebula', ra: 5.588, dec: -5.39, shape: 'neb' },
  { id: 'M45', name: 'Pleiades', ra: 3.791, dec: 24.12, shape: 'none' },
  { id: 'M31', name: 'Andromeda Galaxy', ra: 0.712, dec: 41.27, shape: 'gal' },
];

define({
  tag: 'astrolabe-finder',
  fig: 18,
  name: 'Finder chart, locating',
  family: 'sky',
  moments: ['search', 'empty'],
  duration: 15,
  still: 0.3,
  mark: [70, 20, 320, 320],
  description: 'A chart of Orion, Taurus, and Andromeda with real star positions. The reticle slews to the Orion Nebula, the Pleiades, and the Andromeda Galaxy, locks on each, and reads out its coordinates.',
  use: 'Search: lookup and no results',
  keywords: ['finder', 'chart', 'search', 'locate', 'reticle', 'target', 'lookup', 'empty', 'coordinates'],
  build(k) {
    const X0 = 24, X1 = 456, Y0 = 30, Y1 = 318, RAL = 6.4, RAR = -0.2;
    const kx = (X1 - X0) / ((RAL - RAR) * 15);
    const xy = (ra, dec) => [X0 + (RAL - ra) * 15 * kx, 174 - (dec - 17) * kx];
    // at mark size the chart becomes an eyepiece: the sky moves, the reticle stays
    const EC = [230, 180], ER = 142;
    const view = k.g(null, null, k.mark ? { 'clip-path': k.clip(P.circle(EC[0], EC[1], ER)).ref } : null);
    const sky = k.g(view, k.mark ? 'nsg' : null);
    if (k.mark) k.path(null, P.circle(EC[0], EC[1], ER), 'o');
    if (!k.mark) {
      k.path(sky, P.rect(X0, Y0, X1 - X0, Y1 - Y0), 'f');
      let g = '';
      for (let h = 0; h <= 6; h++) { const [x] = xy(h, 0); g += P.line(x, Y0, x, Y1); }
      for (let d = -10; d <= 50; d += 10) { const [, y] = xy(0, d); if (y > Y0 && y < Y1) g += P.line(X0, y, X1, y); }
      k.path(sky, g, 'hh');
      if (k.L) {
        for (let h = 0; h <= 6; h++) { const [x] = xy(h, 0); k.text(null, x, Y0 - 10, `${h}h`, 't ts', 'middle'); }
        for (let d = -10; d <= 40; d += 10) {
          const [, y] = xy(0, d);
          if (y > Y0 + 8 && y < Y1 - 8) k.text(null, X0 + 5, y - 6, `${d > 0 ? '+' : d < 0 ? '−' : ''}${Math.abs(d)}°`, 't ts tn');
        }
      }
    }
    const field = k.g(sky);
    const r = rng(1234);
    if (!k.mark) {
      for (let i = 0; i < (k.full ? 120 : 70); i++) {
        k.star(field, X0 + 4 + r() * (X1 - X0 - 8), Y0 + 4 + r() * (Y1 - Y0 - 8), 4.4 + r() * 1.4, 'fi');
      }
    }
    for (const [ra, dec, mag] of FINDER_STARS) {
      const [x, y] = xy(ra, dec);
      if (k.mark) k.body(sky, x, y, (Math.max(0.6, 2.5 - 0.42 * mag) * k.pu) / 2.2, 'fi');
      else k.star(sky, x, y, mag - 0.4, 'fi');
    }
    for (const t of FINDER_TARGETS) {
      const [x, y] = xy(t.ra, t.dec);
      if (t.shape === 'neb') k.path(sky, P.ellipse(x, y, 6, 8), k.mark ? 'm' : 'hd');
      if (t.shape === 'gal') k.path(sky, P.ellipse(0, 0, 14, 4.2), k.mark ? 'm' : 'hd', { transform: `translate(${n2(x)} ${n2(y)}) rotate(-38)` });
      if (k.L) k.text(null, x - 10, y + 16, t.id, 't ts', 'end');
    }
    const path = k.path(null, '', 'dt a');
    const ret = k.g();
    const rr = k.mark ? [14, 40, 72] : [5, 14, 27];
    k.path(ret, P.circle(0, 0, rr[0]) + P.circle(0, 0, rr[1]), 'm a');
    k.path(ret, P.circle(0, 0, rr[2]), 'f a');
    const t1 = rr[2] + 3, t2 = rr[2] + (k.mark ? 12 : 9);
    k.path(ret, P.line(-t2, 0, -t1, 0) + P.line(t1, 0, t2, 0) + P.line(0, -t2, 0, -t1) + P.line(0, t1, 0, t2), 'm a');
    const pulse = k.ring(ret, 0, 0, 10, 'f a');
    let read = null, read2 = null;
    if (k.L) {
      read = k.text(null, 456, 336, '', 't tt', 'end');
      read2 = k.text(null, 456, 350, '', 't ts tn', 'end');
      k.text(null, 24, 344, 'Field stars schematic', 't ts tn q');
    }
    const pos = FINDER_TARGETS.map((t) => xy(t.ra, t.dec));
    const fmt = (t) => {
      const h = Math.floor(t.ra), m = Math.round((t.ra - h) * 60);
      const dd = Math.floor(Math.abs(t.dec)), dm = Math.round((Math.abs(t.dec) - dd) * 60);
      return `RA ${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m  ·  Dec ${t.dec >= 0 ? '+' : '−'}${dd}°${String(dm).padStart(2, '0')}′`;
    };
    return (p) => {
      const n = FINDER_TARGETS.length;
      const q = p * n, i = Math.floor(q) % n, f = q - Math.floor(q);
      const from = pos[(i + n - 1) % n], to = pos[i];
      const m = ease.inOut(seg(f, 0, 0.42));
      // slight overshoot and settle, like a slewing mount
      const settle = f > 0.42 ? Math.exp(-(f - 0.42) * 30) * Math.sin((f - 0.42) * 60) * 2.2 : 0;
      const x = lerp(from[0], to[0], m) + settle, y = lerp(from[1], to[1], m) + settle * 0.4;
      if (k.mark) {
        sky.setAttribute('transform', `translate(${EC[0]} ${EC[1]}) scale(2.2) translate(${n2(-x)} ${n2(-y)})`);
        k.move(ret, EC[0], EC[1]);
      } else {
        k.move(ret, x, y);
        k.set(path, m > 0 && m < 1 ? P.line(from[0], from[1], x, y) : '');
      }
      const lock = seg(f, 0.48, 0.54);
      k.fade(pulse, lock * (1 - seg(f, 0.54, 0.8)));
      pulse.setAttribute('r', n2(lerp(rr[2] * 0.6, rr[2] * 1.6, seg(f, 0.48, 0.8))));
      if (read) {
        const t = FINDER_TARGETS[i];
        k.retext(read, lock > 0 ? `${t.id} · ${t.name.toUpperCase()}` : 'SLEWING');
        k.retext(read2, lock > 0 ? fmt(t) : '');
        read.setAttribute('class', lock > 0 ? 't tt ta' : 't tt');
      }
    };
  },
});

/* src/figures/19-armillary.js */
/* FIG. 19: Armillary sphere. The fixed meridian and horizon rings hold an
 * inner cage (equator, tropics, colures and the ecliptic band) that turns
 * about a polar axis raised to your latitude. Rings behind the centre
 * are dashed. */
define({
  tag: 'astrolabe-armillary',
  fig: 19,
  name: 'Armillary sphere',
  family: 'instruments',
  moments: ['hero', 'enrichment'],
  duration: 24,
  still: 0.1,
  mark: [90, 20, 300, 300],
  description: 'Fixed meridian and horizon rings hold an inner cage of equator, tropics, colures, and the ecliptic band, which turns about a polar axis set to your latitude. Rings behind the centre are dashed.',
  use: 'Hero: model building',
  keywords: ['armillary', 'sphere', 'rings', 'instrument', 'model', 'rotation', 'loading', 'hero'],
  params: {
    latitude: { type: 'range', min: 0, max: 80, step: 0.1, default: 40.72, label: 'Axis latitude', unit: '°' },
  },
  build(k, o) {
    const R = k.mark ? 96 : 104, C = [240, k.mark ? 140 : 150];
    const phi = o.latitude * D2R, eps = OBL * D2R;
    const az = -32 * D2R, el = 18 * D2R;
    const view = (v) => {
      const x = v[0] * Math.cos(az) - v[1] * Math.sin(az);
      const y = v[0] * Math.sin(az) + v[1] * Math.cos(az);
      const z = v[2];
      const y2 = y * Math.cos(el) - z * Math.sin(el);
      const z2 = y * Math.sin(el) + z * Math.cos(el);
      return [C[0] + x * R, C[1] - z2 * R, -y2];
    };
    const rings = [];
    const ring = (front, back, fixedBasis) => {
      const r = { f: k.path(null, '', front), b: back ? k.path(null, '', back) : null, basis: fixedBasis };
      rings.push(r);
      return r;
    };
    const drawRing = (r, c, u, v, rad) => {
      const pts = circle3(c, u, v, rad, 120).map(view);
      let f = '', b = '';
      for (const run of splitRuns(pts, (q) => q[2] >= -0.02)) {
        const d = P.poly(run.pts);
        if (run.front) f += d; else b += d;
      }
      k.set(r.f, f);
      if (r.b) k.set(r.b, b);
    };
    // stand
    if (k.mark) {
      const b = view([0, 0, -1.62]), c1 = view([0, 0, -1.0]);
      k.path(null, P.line(b[0], b[1], c1[0], c1[1]), 'm');
      k.path(null, P.poly(circle3([0, 0, -1.62], [1, 0, 0], [0, 1, 0], 0.36, 60).map(view)), 'm');
    } else {
      const base = circle3([0, 0, -1.62], [1, 0, 0], [0, 1, 0], 0.38, 80).map(view);
      k.path(null, P.poly(base), 'm');
      const b2 = circle3([0, 0, -1.7], [1, 0, 0], [0, 1, 0], 0.38, 80).map(view);
      k.path(null, P.poly(b2.filter((q) => q[2] >= 0)), 'f');
      const c0 = view([0, 0, -1.62]), c1 = view([0, 0, -1.0]);
      k.path(null, P.line(c0[0] - 5, c0[1], c1[0] - 5, c1[1]) + P.line(c0[0] + 5, c0[1], c1[0] + 5, c1[1]), 'm');
    }
    // fixed rings, as bands
    const merid = k.mark ? [ring('o', null)] : [ring('o', 'hd'), ring('f', 'hh')];
    const horiz = k.mark ? [ring('o', null)] : [ring('o', 'hd'), ring('f', 'hh')];
    const X = [1, 0, 0], Y = [0, 1, 0], Zv = [0, 0, 1];
    drawRing(merid[0], [0, 0, 0], Y, Zv, 1);
    if (merid[1]) drawRing(merid[1], [0, 0, 0], Y, Zv, 0.93);
    drawRing(horiz[0], [0, 0, 0], X, Y, 1.02);
    if (horiz[1]) drawRing(horiz[1], [0, 0, 0], X, Y, 0.95);
    if (!k.mark) {
      let tk = '';
      for (let i = 0; i < 36; i++) {
        const a = (i / 36) * TAU;
        const p1 = view([Math.cos(a) * 0.95, Math.sin(a) * 0.95, 0]), p2 = view([Math.cos(a) * 1.02, Math.sin(a) * 1.02, 0]);
        if (p1[2] >= 0) tk += P.line(p1[0], p1[1], p2[0], p2[1]);
      }
      k.path(null, tk, 'f');
    }
    // rotating cage
    const axis = [0, Math.cos(phi), Math.sin(phi)];
    const u0 = [1, 0, 0];
    const v0 = [axis[1] * u0[2] - axis[2] * u0[1], axis[2] * u0[0] - axis[0] * u0[2], axis[0] * u0[1] - axis[1] * u0[0]];
    const eq = ring('m a', 'hd a');
    const trop = k.mark ? [] : [ring('f a', 'hh'), ring('f a', 'hh')];
    const col = k.mark ? [] : [ring('f', 'hh'), ring('f', 'hh')];
    const ecl = k.mark ? [ring('m a', null)] : [ring('m a', 'hd a'), ring('f a', null)];
    const axisL = k.path(null, '', 'c');
    const earth = k.mark ? null : k.body(null, C[0], C[1], R * 0.14, 'm');
    const earthEq = k.mark ? null : k.path(null, '', 'f');
    let labs = null;
    if (k.L) {
      // each ring gets its own leader to its own knee, so the two never stack
      const dyn = (label, knee) => {
        const g = k.leader(null, [0, 0], knee, label, { side: 'r', textCls: 't ta' });
        return { g, knee };
      };
      labs = { eq: dyn('Equator', [C[0] + 146, C[1] + 104]), ecl: dyn('Ecliptic', [C[0] + 146, C[1] - 38]) };
      const mt = view([0, -0.2, 1]);
      k.leader(null, [mt[0], mt[1]], [C[0] + 120, 26], 'Meridian ring', { side: 'r' });
      const hz = view([-1.02, 0, 0]);
      k.leader(null, [hz[0], hz[1]], [34, C[1] + 70], 'Horizon', { side: 'r' });
      k.text(null, 14, 346, `Axis φ ${dms(o.latitude)}`, 't tt tn');
    }
    const add = (a, b, s) => [a[0] + b[0] * s, a[1] + b[1] * s, a[2] + b[2] * s];
    return (p) => {
      const th = p * TAU;
      const u = add(add([0, 0, 0], u0, Math.cos(th)), v0, Math.sin(th));
      const v = add(add([0, 0, 0], u0, -Math.sin(th)), v0, Math.cos(th));
      const r = 0.82;
      drawRing(eq, [0, 0, 0], u, v, r);
      const se = Math.sin(eps), ce = Math.cos(eps);
      if (trop.length) {
        drawRing(trop[0], add([0, 0, 0], axis, r * se), u, v, r * ce);
        drawRing(trop[1], add([0, 0, 0], axis, -r * se), u, v, r * ce);
        drawRing(col[0], [0, 0, 0], u, axis, r);
        drawRing(col[1], [0, 0, 0], v, axis, r);
      }
      const ev = add(add([0, 0, 0], v, ce), axis, se);
      drawRing(ecl[0], [0, 0, 0], u, ev, r * 1.02);
      if (ecl[1]) drawRing(ecl[1], [0, 0, 0], u, ev, r * 0.94);
      const a1 = view(add([0, 0, 0], axis, 1.22)), a2 = view(add([0, 0, 0], axis, -1.22));
      k.set(axisL, P.line(a1[0], a1[1], a2[0], a2[1]));
      if (earthEq) k.set(earthEq, P.poly(circle3([0, 0, 0], u, v, 0.14, 40).map(view).filter((q) => q[2] >= 0)));
      void earth;
      if (labs) {
        let best = null;
        for (let i = 0; i < 60; i++) {
          const a = (i / 60) * TAU;
          const q = view(add(add([0, 0, 0], u, Math.cos(a) * r), v, Math.sin(a) * r));
          if (q[2] > 0 && (!best || q[1] > best[1])) best = q;
        }
        const pin = (L, q) => {
          if (!q) return;
          L.g.firstChild.setAttribute('d', P.poly([[q[0], q[1]], L.knee, [L.knee[0] + 9 * k.tu, L.knee[1]]]));
          L.g.childNodes[1].setAttribute('cx', n2(q[0]));
          L.g.childNodes[1].setAttribute('cy', n2(q[1]));
        };
        pin(labs.eq, best);
        let be = null;
        for (let i = 0; i < 60; i++) {
          const a = (i / 60) * TAU;
          const q = view(add(add([0, 0, 0], u, Math.cos(a) * r), ev, Math.sin(a) * r));
          if (q[2] > 0 && (!be || q[1] < be[1])) be = q;
        }
        pin(labs.ecl, be);
      }
    };
  },
});

/* src/figures/20-sextant.js */
/* FIG. 20: Sextant, bringing the Sun down. The index arm turns the index
 * mirror until the Sun's twice-reflected image sits on the horizon seen
 * through the horizon glass. The arc reads double the mirror angle. */
define({
  tag: 'astrolabe-sextant',
  fig: 20,
  name: 'Sextant, sighting the Sun',
  family: 'instruments',
  moments: ['search'],
  duration: 12,
  still: 0.62,
  mark: [150, 0, 300, 300],
  description: "The index arm turns the index mirror until the Sun's doubly reflected image sits on the horizon seen through the horizon glass. The arc reads twice the mirror angle.",
  use: 'Search: measuring a match',
  keywords: ['sextant', 'navigation', 'measure', 'sun', 'mirror', 'optics', 'angle', 'fix'],
  params: {
    altitude: { type: 'range', min: 10, max: 60, step: 0.5, default: 35, label: 'Sun altitude', unit: '°' },
  },
  build(k, o) {
    const I = [300, 96], H = [272, 214], Rarc = 208;
    const A = o.altitude * D2R;
    const d1 = (() => { const dx = H[0] - I[0], dy = H[1] - I[1], l = Math.hypot(dx, dy); return [dx / l, dy / l]; })();
    const nH = (() => { const x = d1[0] + 1, y = d1[1], l = Math.hypot(x, y); return [x / l, y / l]; })();
    const inc = [-Math.cos(A), Math.sin(A)];
    const nStar = (() => { const x = inc[0] - d1[0], y = inc[1] - d1[1], l = Math.hypot(x, y); return [x / l, y / l]; })();
    let aStar = Math.atan2(nStar[1], nStar[0]) - Math.atan2(nH[1], nH[0]);
    while (aStar > Math.PI / 2) aStar -= Math.PI;
    while (aStar < -Math.PI / 2) aStar += Math.PI;
    const zero = 120 * D2R; // arm angle (screen) at a reading of 0
    const rot = (v, a) => [v[0] * Math.cos(a) - v[1] * Math.sin(a), v[0] * Math.sin(a) + v[1] * Math.cos(a)];
    // frame
    const aL = 60 * D2R, aR = 120 * D2R;
    k.path(null, P.arc(I[0], I[1], Rarc, aL, aR) + P.arc(I[0], I[1], Rarc - 16, aL, aR), 'o');
    k.path(null, P.line(I[0], I[1], ...polar(I[0], I[1], Rarc, aL)) + P.line(I[0], I[1], ...polar(I[0], I[1], Rarc, aR)), 'm');
    k.path(null, P.line(I[0], I[1], ...polar(I[0], I[1], Rarc - 16, 90 * D2R)), 'f');
    if (!k.mark) {
      let tk = '';
      for (let v = 0; v <= 120; v += 1) {
        const a = zero - (v / 2) * D2R;
        const L = v % 10 === 0 ? 9 : v % 5 === 0 ? 6 : 3.5;
        tk += P.line(...polar(I[0], I[1], Rarc - 16, a), ...polar(I[0], I[1], Rarc - 16 + L, a));
      }
      k.path(null, tk, 'f');
      if (k.L) {
        for (let v = 0; v <= 120; v += 20) {
          const [x, y] = polar(I[0], I[1], Rarc + 10, zero - (v / 2) * D2R);
          k.text(null, x, y + 4, String(v), 't ts', 'middle');
        }
      }
    }
    // telescope and eye, horizon glass
    k.path(null, P.rect(58, H[1] - 7, 92, 14), 'm');
    k.path(null, P.rect(44, H[1] - 5, 14, 10), 'f');
    const hs = [nH[1], -nH[0]];
    k.path(null, P.line(H[0] - hs[0] * 13, H[1] - hs[1] * 13, H[0] + hs[0] * 13, H[1] + hs[1] * 13), 'o');
    k.path(null, P.line(H[0], H[1], H[0] + hs[0] * 13, H[1] + hs[1] * 13) + P.line(H[0] + nH[0] * 2.5, H[1] + nH[1] * 2.5, H[0] + hs[0] * 13 + nH[0] * 2.5, H[1] + hs[1] * 13 + nH[1] * 2.5), 'f');
    // sea horizon, sun
    if (!k.mark) {
      k.path(null, P.line(380, H[1], 474, H[1]), 'm');
      let wv = '';
      for (let x = 386; x < 470; x += 12) wv += `M${x} ${H[1] + 7}q3 -3 6 0t6 0`;
      k.path(null, wv, 'h');
    }
    const S = polar(I[0], I[1], 158, -A);
    k.body(null, S[0], S[1], 9, 'm');
    if (!k.mark) k.path(null, P.ticks(S[0], S[1], 12, 17, 0, TAU, 12), 'f');
    // rays
    const direct = k.path(null, P.line(474, H[1], 44, H[1]), 'hd');
    const rayIn = k.path(null, P.line(S[0], S[1], I[0], I[1]), 'f a', { 'marker-end': 'url(#aa)' });
    const rayOut = k.path(null, '', 'f a');
    const rayEye = k.path(null, P.line(H[0], H[1], 50, H[1]), 'm a', { 'marker-end': 'url(#aa)' });
    // index arm with mirror
    const arm = k.g();
    k.path(arm, `M0 -4L${Rarc - 4} -2L${Rarc + 6} 0L${Rarc - 4} 2L0 4Z`, 'm a');
    k.path(arm, P.rect(Rarc - 22, -7, 18, 14), 'f a');
    const mir = k.g();
    k.path(mir, P.line(-14, 0, 14, 0), 'o a');
    k.path(mir, P.line(-14, 3, 14, 3), 'f a');
    k.centre(null, I[0], I[1], 4, 'f');
    let read = null, note = null;
    if (k.L) {
      k.leader(null, [H[0] + hs[0] * 8, H[1] + hs[1] * 8], [210, 150], 'Horizon glass', { side: 'l' });
      k.leader(null, [I[0] + 6, I[1] - 2], [372, 64], 'Index mirror', { side: 'r' });
      k.leader(null, [100, H[1] + 7], [100, 262], 'Telescope', { side: 'r' });
      k.text(null, 474, H[1] + 22, 'Horizon', 't ts', 'end');
      read = k.text(null, 14, 346, '', 't tt');
      note = k.text(null, 14, 330, '', 't ts');
    }
    return (p) => {
      const sweep = ease.inOut(seg(p, 0.1, 0.5)) * (1 - ease.inOut(seg(p, 0.82, 0.97)));
      const a = aStar * sweep;
      const armAng = zero + a;
      arm.setAttribute('transform', `translate(${I[0]} ${I[1]}) rotate(${n2(armAng * R2D)})`);
      const n = rot(nH, a);
      mir.setAttribute('transform', `translate(${I[0]} ${I[1]}) rotate(${n2(Math.atan2(n[1], n[0]) * R2D + 90)})`);
      const dot = inc[0] * n[0] + inc[1] * n[1];
      const r = [inc[0] - 2 * dot * n[0], inc[1] - 2 * dot * n[1]];
      const L = Math.hypot(H[0] - I[0], H[1] - I[1]);
      k.set(rayOut, P.line(I[0], I[1], I[0] + r[0] * L * 1.25, I[1] + r[1] * L * 1.25));
      const lock = seg(sweep, 0.97, 1);
      const vis = Math.min(seg(p, 0, 0.08), 1 - seg(p, 0.9, 0.98));
      k.fade(rayIn, vis);
      k.fade(rayOut, vis * (1 - lock * 0.2));
      k.fade(rayEye, lock);
      k.fade(direct, 0.6);
      if (read) {
        k.retext(read, `ALT ${dms(Math.abs(a) * 2 * R2D)}`);
        k.retext(note, lock > 0.5 ? 'SUN ON THE HORIZON' : 'INDEX ARM');
        read.setAttribute('class', lock > 0.5 ? 't tt ta' : 't tt');
      }
    };
  },
});

/* src/figures/21-telescope.js */
/* FIG. 21: Telescope, Newtonian optics. Parallel light enters the tube,
 * reflects off the parabolic primary toward its focus, is turned 90° by
 * the flat diagonal, and comes to focus at the eyepiece. Pulses of light
 * travel the true ray paths. */
define({
  tag: 'astrolabe-telescope',
  fig: 21,
  name: 'Telescope, Newtonian optics',
  family: 'instruments',
  moments: ['enrichment'],
  duration: 6,
  still: 0.4,
  mark: [90, 30, 300, 300],
  description: 'Parallel light enters the tube, reflects off the parabolic primary toward its focus, turns 90° at the flat diagonal, and comes to focus at the eyepiece. Pulses of light follow the true ray paths.',
  use: 'Enrichment: fetching data',
  keywords: ['telescope', 'optics', 'newtonian', 'mirror', 'light', 'rays', 'loading', 'fetch', 'collect'],
  build(k) {
    if (k.mark) return telescopeMark(k);
    const ax = 186, x0 = 56, f = 280, F = [x0 + f, ax];
    const top = ax - 44, bot = ax + 44, xOpen = 404;
    const dX = 296; // diagonal centre on the axis
    const fp = [dX, ax - (F[0] - dX)]; // folded focus
    // tube, spider, focuser, eyepiece
    k.path(null, P.line(x0 - 12, top, xOpen, top) + P.line(x0 - 12, bot, xOpen, bot) + P.line(x0 - 12, top, x0 - 12, bot), 'o');
    k.path(null, P.line(xOpen, top - 4, xOpen, top + 4) + P.line(xOpen, bot - 4, xOpen, bot + 4), 'm');
    k.path(null, P.rect(dX - 13, top - 30, 26, 30), 'm');
    k.path(null, P.rect(dX - 9, top - 58, 18, 28), 'm');
    k.path(null, P.line(dX - 9, top - 50, dX + 9, top - 50), 'f');
    if (!k.mark) {
      k.path(null, P.line(x0 - 24, ax, xOpen + 44, ax), 'c');
      k.path(null, P.line(dX, top, dX, ax - 8) + P.line(dX, ax + 8, dX, bot), 'h');
      k.path(null, P.rect(x0 - 12, top + 8, 10, bot - top - 16), 'h');
    }
    // parabolic primary: x = x0 + y²/4f
    const para = [];
    for (let y = -40; y <= 40; y += 2) para.push([x0 + (y * y) / (4 * f), ax + y]);
    k.path(null, P.poly(para), 'o');
    k.path(null, P.poly(para.map(([x, y]) => [x - 5, y])), 'f');
    // diagonal flat at 45°
    k.path(null, P.line(dX - 8, ax + 8, dX + 8, ax - 8), 'o');
    // rays
    const hs = k.mark ? [-30, -18, 18, 30] : [-38, -28, -18, 18, 28, 38];
    const paths = hs.map((hh) => {
      const xp = x0 + (hh * hh) / (4 * f);
      const s = [xp, ax + hh];
      const dx = F[0] - s[0], dy = F[1] - s[1];
      const t = (dX + ax - s[0] - s[1]) / (dx + dy);
      const m = [s[0] + dx * t, s[1] + dy * t];
      const out = [-dy, -dx];
      const tf = (fp[1] - m[1]) / out[1];
      const fpt = [m[0] + out[0] * tf, m[1] + out[1] * tf];
      const ey = top - 58;
      const te = (ey - fpt[1]) / out[1];
      const ept = [fpt[0] + out[0] * te, ey];
      return [[478, ax + hh], s, m, fpt, ept];
    });
    for (const pts of paths) k.path(null, P.poly(pts), 'hh');
    const pulses = paths.map((pts) => k.dpath(null, P.poly(pts), 'm a'));
    let fl = null;
    if (k.L) {
      k.leader(null, [x0 + 2, ax + 30], [40, 300], 'Primary', { side: 'r' });
      k.leader(null, [dX + 4, ax - 4], [352, 300], 'Diagonal', { side: 'r' });
      k.leader(null, [fp[0] + 1, fp[1] + 1], [352, 58], 'Focus', { side: 'r' });
      k.leader(null, [dX + 9, top - 44], [352, 26], 'Eyepiece', { side: 'r' });
      k.dim(null, [x0, bot], [F[0], bot], 22, 'f', { textCls: 't tn' });
      k.text(null, 470, ax - 52, 'Starlight', 't ts', 'end');
      fl = k.text(null, 14, 346, 'Parabolic primary · flat diagonal', 't ts tn q');
    }
    void fl;
    return (p) => {
      for (let i = 0; i < pulses.length; i++) {
        const el = pulses[i];
        const ph = fract(p + i * 0.11);
        el.style.strokeDasharray = '0.06 0.94';
        el.style.strokeDashoffset = String(n2(0.06 - ph * 1.06));
      }
    };
  },
});

/** Icon-size reduction: tube, mirror, eyepiece and two travelling rays. */
function telescopeMark(k) {
  const y0 = 152, y1 = 212, x0 = 114, x1 = 360, ex = 300;
  k.path(null, P.line(x0, y0, x1, y0) + P.line(x0, y1, x1, y1) + P.line(x0, y0, x0, y1), 'o');
  k.path(null, `M${x0 + 8} ${y0 + 6}Q${x0 + 22} ${(y0 + y1) / 2} ${x0 + 8} ${y1 - 6}`, 'm');
  k.path(null, P.rect(ex - 11, y0 - 34, 22, 34), 'o');
  k.path(null, P.line(ex - 9, 190, ex + 9, 172), 'm');
  const rays = [168, 196].map((y) => k.dpath(null, P.poly([[392, y], [x0 + 14, y], [ex, 181], [ex, y0 - 34]]), 'm a'));
  return (p) => {
    rays.forEach((el, i) => {
      const ph = fract(p + i * 0.5);
      el.style.strokeDasharray = '0.12 0.88';
      el.style.strokeDashoffset = String(n2(0.12 - ph * 1.12));
    });
  };
}

/* src/figures/22-satellite.js */
/* FIG. 22: Satellite, deploying. An orthographic view of a bus with two
 * accordion solar arrays and a folded high-gain antenna. The arrays
 * unfold panel by panel, the dish swings up, and it transmits. Then
 * everything stows again. */
define({
  tag: 'astrolabe-satellite',
  fig: 22,
  name: 'Satellite, deploying',
  family: 'instruments',
  moments: ['ready', 'onboarding'],
  duration: 12,
  still: 0.62,
  mark: [90, 30, 300, 300],
  description: 'A satellite bus with two accordion solar arrays and a folded antenna. The arrays unfold panel by panel, the dish swings up and transmits, then everything stows again.',
  use: 'Ready: delivered, onboarding complete',
  keywords: ['satellite', 'deploy', 'solar panels', 'unfold', 'success', 'launch', 'complete', 'antenna'],
  build(k) {
    const S = k.mark ? 0.92 : 1, C = [240, k.mark ? 190 : 196];
    const az = -38 * D2R, el = 24 * D2R;
    const view = (v) => {
      const x = v[0] * Math.cos(az) - v[1] * Math.sin(az);
      const y = v[0] * Math.sin(az) + v[1] * Math.cos(az);
      const z = v[2];
      const y2 = y * Math.cos(el) - z * Math.sin(el);
      const z2 = y * Math.sin(el) + z * Math.cos(el);
      return [C[0] + x * S, C[1] - z2 * S, -y2];
    };
    const B = 30; // half-size of the bus
    // bus: visible faces solid, hidden edges dashed
    const V = [];
    for (const x of [-B, B]) for (const y of [-B, B]) for (const z of [-B, B]) V.push([x, y, z]);
    const E = [[0, 1], [2, 3], [4, 5], [6, 7], [0, 2], [1, 3], [4, 6], [5, 7], [0, 4], [1, 5], [2, 6], [3, 7]];
    const Pv = V.map(view);
    // the hidden vertex is the one farthest from the viewer
    let hid = 0;
    Pv.forEach((q, i) => { if (q[2] < Pv[hid][2]) hid = i; });
    let vis = '', hidd = '';
    for (const [a, b] of E) {
      const d = P.line(Pv[a][0], Pv[a][1], Pv[b][0], Pv[b][1]);
      if (a === hid || b === hid) hidd += d; else vis += d;
    }
    k.path(null, hidd, 'hd');
    k.path(null, vis, 'o');
    if (!k.mark) {
      // face detail: inset panel on the front faces, thruster below
      const inset = (pts) => P.poly(pts.map(view), true);
      const i = B * 0.62;
      k.path(null, inset([[-i, -B, -i], [i, -B, -i], [i, -B, i], [-i, -B, i]]), 'h');
      k.path(null, inset([[B, -i, -i], [B, i, -i], [B, i, i], [B, -i, i]]), 'h');
      const n0 = view([0, 0, -B]), n1 = view([0, 0, -B - 14]);
      k.path(null, P.line(n0[0] - 7, n0[1], n1[0] - 11, n1[1]) + P.line(n0[0] + 7, n0[1], n1[0] + 11, n1[1]), 'f');
      k.path(null, P.poly(circle3([0, 0, -B - 14], [1, 0, 0], [0, 1, 0], 11, 30).map(view)), 'f');
    }
    // solar wings: three panels each, accordion hinged about vertical lines
    const W = 46, Hh = 34, NP = k.mark ? 2 : 3;
    const wings = [-1, 1].map(() => Array.from({ length: NP }, () => ({ q: k.path(null, '', 'f', { style: 'fill:var(--ink);fill-opacity:.05' }), c: k.path(null, '', 'hh') })));
    const yoke = [-1, 1].map(() => k.path(null, '', 'm'));
    // antenna on a hinge at the top face
    const dish = k.path(null, '', 'm a');
    const feed = k.path(null, '', 'f a');
    const waves = [0, 1, 2].map(() => k.path(null, '', 'f a'));
    let read = null;
    if (k.L) {
      k.leader(null, view([0, -B, -8]).slice(0, 2), [300, 300], 'Bus', { side: 'r' });
      k.text(null, 14, 330, 'Solar arrays', 't ts q');
      read = k.text(null, 14, 346, '', 't tt');
      k.text(null, 466, 346, 'Orthographic · hidden edges dashed', 't ts tn q', 'end');
    }
    const add = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
    const mul = (a, s) => [a[0] * s, a[1] * s, a[2] * s];
    return (p) => {
      const dep = ease.inOut(seg(p, 0.06, 0.4)) * (1 - ease.inOut(seg(p, 0.8, 0.97)));
      const dishUp = ease.inOut(seg(p, 0.32, 0.46)) * (1 - ease.inOut(seg(p, 0.76, 0.86)));
      [-1, 1].forEach((side, wi) => {
        let hinge = [side * (B + 10), 0, 0];
        const root = [side * B, 0, 0];
        k.set(yoke[wi], P.line(...view(root).slice(0, 2), ...view(hinge).slice(0, 2)));
        for (let i = 0; i < NP; i++) {
          const fold = (1 - dep) * (Math.PI / 2 - 0.08);
          const ang = i % 2 === 0 ? fold : -fold;
          const dir = [side * Math.cos(ang), Math.sin(ang) * (i === 0 ? 1 : 1), 0];
          const end = add(hinge, mul(dir, W));
          const quad = [add(hinge, [0, 0, -Hh]), add(end, [0, 0, -Hh]), add(end, [0, 0, Hh]), add(hinge, [0, 0, Hh])].map(view);
          k.set(wings[wi][i].q, P.poly(quad, true));
          let cells = '';
          for (let c = 1; c < 3; c++) {
            const a = add(hinge, mul(dir, (W * c) / 3));
            cells += P.line(...view(add(a, [0, 0, -Hh])).slice(0, 2), ...view(add(a, [0, 0, Hh])).slice(0, 2));
          }
          cells += P.line(...view(hinge).slice(0, 2), ...view(end).slice(0, 2));
          k.set(wings[wi][i].c, cells);
          hinge = end;
        }
      });
      // dish: hinge at the top-front edge, normal swings from up to up-and-out
      const hingeP = [0, -B * 0.3, B];
      const tilt = lerp(0, 1.05, dishUp);
      const nrm = [0, -Math.sin(tilt), Math.cos(tilt)];
      const uu = [1, 0, 0], vv = [0, Math.cos(tilt), Math.sin(tilt)];
      const ctr = add(hingeP, add(mul(vv, 20), mul(nrm, 4)));
      const rim = circle3(ctr, uu, vv, 20, 48).map(view);
      k.set(dish, P.poly(rim));
      const tip = view(add(ctr, mul(nrm, 18)));
      const c0 = view(ctr);
      let fd = P.line(c0[0], c0[1], tip[0], tip[1]);
      for (const a of [0, TAU / 3, (2 * TAU) / 3]) {
        const e = view(add(ctr, add(mul(uu, Math.cos(a) * 20), mul(vv, Math.sin(a) * 20))));
        fd += P.line(e[0], e[1], tip[0], tip[1]);
      }
      k.set(feed, fd);
      waves.forEach((wv, i) => {
        const ph = fract(p * 6 + i / 3);
        const on = seg(p, 0.46, 0.5) * (1 - seg(p, 0.74, 0.78));
        const r0 = 24 + ph * 60;
        const arc = circle3(add(ctr, mul(nrm, 18 + ph * 70)), uu, vv, r0 * 0.5, 30, -0.9, 0.9).map(view);
        const arc2 = circle3(add(ctr, mul(nrm, 18 + ph * 70)), uu, vv, r0 * 0.5, 30, Math.PI - 0.9, Math.PI + 0.9).map(view);
        k.set(wv, P.poly(arc) + P.poly(arc2));
        k.fade(wv, on * (1 - ph));
      });
      if (read) k.retext(read, dep >= 0.999 && dishUp >= 0.999 ? 'DEPLOYED' : `${String(Math.round(dep * 100)).padStart(3, '0')}%`);
      if (read) read.setAttribute('class', dep >= 0.999 && dishUp >= 0.999 ? 't tt ta' : 't tt');
    };
  },
});

/* src/figures/23-radio.js */
/* FIG. 23: Radio dish, drift scan. A parabolic dish on an alt-azimuth
 * mount tracks a source across the sky. Plane wavefronts arrive,
 * reflect to the feed at the focus, and the receiver trace rises as the
 * source crosses the beam. */
define({
  tag: 'astrolabe-radio',
  fig: 23,
  name: 'Radio dish, listening',
  family: 'instruments',
  moments: ['upload', 'enrichment'],
  duration: 14,
  still: 0.5,
  mark: [40, 30, 300, 300],
  description: 'A parabolic dish on an alt-azimuth mount tracks a source across the sky. Wavefronts reflect to the feed at the focus, and the receiver trace rises as the source crosses the beam.',
  use: 'Upload: listening for data',
  keywords: ['radio', 'dish', 'antenna', 'signal', 'waves', 'listening', 'processing', 'receiver'],
  build(k) {
    const piv = [178, 222], Rd = 78, fdish = 58;
    // mount and ground
    k.path(null, P.line(60, 330, 300, 330), 'o');
    if (!k.mark) {
      let hat = '';
      for (let x = 64; x < 300; x += 8) hat += P.line(x, 330, x - 6, 338);
      k.path(null, hat, 'h');
    }
    k.path(null, `M${piv[0] - 36} 330L${piv[0] - 12} ${piv[1] + 8}L${piv[0] + 12} ${piv[1] + 8}L${piv[0] + 36} 330`, 'm');
    k.path(null, P.rect(piv[0] - 30, 318, 60, 12), 'f');
    k.body(null, piv[0], piv[1], 8, 'm');
    k.centre(null, piv[0], piv[1], 3, 'f');
    // dish assembly, drawn pointing along +x then rotated to the source
    const dish = k.g();
    const prof = [];
    const V0 = 14;
    for (let y = -Rd; y <= Rd; y += 3) prof.push([(y * y) / (4 * fdish) + V0, y]);
    k.path(dish, P.poly(prof), 'o');
    k.path(dish, P.poly(prof.map(([x, y]) => [x - 4, y])), 'f');
    const fx = V0 + fdish;
    k.path(dish, P.line(prof[0][0], prof[0][1], fx, 0) + P.line(prof[prof.length - 1][0], prof[prof.length - 1][1], fx, 0), 'f');
    k.path(dish, P.rect(fx - 3, -6, 10, 12), 'm');
    k.path(dish, P.line(0, 0, V0 - 4, 0) + P.line(0, 0, V0 + 3, -42) + P.line(0, 0, V0 + 3, 42) + P.line(V0 + 3, -42, V0 + 3, 42), 'f');
    // rays and wavefronts inside the dish frame
    const rays = k.path(dish, '', 'h a');
    const fronts = [0, 1, 2].map(() => k.path(dish, '', 'f a'));
    // source track across the sky
    const SC = [178, 222], SR = 196;
    if (!k.mark) k.path(null, P.arc(SC[0], SC[1], SR, -2.6, -0.5), 'dt');
    const src = k.g();
    k.ring(src, 0, 0, 5, 'f a');
    k.dot(src, 0, 0, 1.8, 'fa');
    // receiver trace
    let trace = null, tFrame = null;
    if (!k.mark) {
      tFrame = [322, 196, 142, 112];
      const [x, y, w, hh] = tFrame;
      k.path(null, P.rect(x, y, w, hh), 'f');
      let g = '';
      for (let i = 1; i < 4; i++) g += P.line(x + (w * i) / 4, y, x + (w * i) / 4, y + hh);
      for (let i = 1; i < 3; i++) g += P.line(x, y + (hh * i) / 3, x + w, y + (hh * i) / 3);
      k.path(null, g, 'hh');
      trace = k.path(null, '', 'm a');
      if (k.L) {
        k.text(null, x, y - 10, 'Receiver', 't ts');
        k.text(null, x + w, y + hh + 12, 'Time', 't ts q', 'end');
        k.text(null, x - 6, y + 6, 'P', 't ts tn q', 'end');
      }
    }
    let read = null;
    if (k.L) {
      k.text(null, 14, 40, '', 't tt');
      read = k.text(null, 14, 22, '', 't ts');
      k.text(null, 466, 346, 'Alt-azimuth mount', 't ts tn q', 'end');
    }
    const noise = rng(5);
    const N = 160;
    const nz = Array.from({ length: N + 1 }, () => noise() - 0.5);
    return (p) => {
      const q = seg(p, 0.02, 0.92);
      const ang = lerp(-2.52, -0.62, q);
      const s = polar(SC[0], SC[1], SR, ang);
      k.move(src, s[0], s[1]);
      // the dish lags the source slightly, then catches it
      const lag = 0.1 * Math.sin(q * Math.PI) * (1 - seg(q, 0.3, 0.5));
      const pointing = ang + lag;
      dish.setAttribute('transform', `translate(${piv[0]} ${piv[1]}) rotate(${n2(pointing * R2D)})`);
      // rays: parallel from the source, reflected to the focus
      let rd = '';
      for (const y of [-58, -30, 30, 58]) {
        const xp = (y * y) / (4 * fdish) + V0;
        rd += P.line(176, y, xp, y) + P.line(xp, y, fx, 0);
      }
      k.set(rays, rd);
      fronts.forEach((el, i) => {
        const ph = fract(p * 8 + i / 3);
        const x = lerp(176, 24, ph);
        k.set(el, P.line(x, -70, x, 70));
        k.fade(el, Math.min(1, ph * 4) * (1 - ph) * 1.2);
      });
      if (trace) {
        const [x, y, w, hh] = tFrame;
        const n = Math.floor(q * N);
        const pts = [];
        for (let i = 0; i <= n; i++) {
          const t = i / N;
          const beam = Math.exp(-Math.pow((t - 0.5) / 0.1, 2));
          const v = 0.12 + 0.72 * beam + nz[i] * 0.08;
          pts.push([x + t * w, y + hh - v * hh]);
        }
        k.set(trace, pts.length > 1 ? P.poly(pts) : '');
        k.fade(trace, 1 - seg(p, 0.93, 0.99));
      }
      if (read) {
        const onBeam = Math.abs(q - 0.5) < 0.12;
        k.retext(read, onBeam ? 'SOURCE IN BEAM' : 'TRACKING');
        read.setAttribute('class', onBeam ? 't ts ta' : 't ts');
        k.retext(read.previousSibling, `EL ${String(Math.round(90 - Math.abs(-ang * R2D - 90))).padStart(2, "0")}°`);
      }
    };
  },
});

/* src/figures/24-flyby.js */
/* FIG. 24: Flyby, gravity assist. A probe on a hyperbolic path (e = 1.35)
 * swings past a planet, timed by the hyperbolic Kepler equation. It is
 * turned about 96° between its asymptotes. The inset adds the planet's
 * velocity: the same v∞ in and out, but a faster heliocentric exit. */
define({
  tag: 'astrolabe-flyby',
  fig: 24,
  name: 'Flyby, gravity assist',
  family: 'instruments',
  moments: ['strategy'],
  duration: 10,
  still: 0.78,
  mark: [74, 96, 300, 300],
  description: "A probe on a hyperbolic path swings past a planet and turns about 96° between its asymptotes. The inset adds the planet's velocity: the same speed relative to the planet, but a faster exit relative to the Sun.",
  use: 'Strategy: growth and momentum',
  keywords: ['flyby', 'gravity assist', 'slingshot', 'probe', 'growth', 'momentum', 'boost', 'hyperbola'],
  build(k) {
    const Pl = [224, 244], Rp = 28;
    const e = 1.35, rp = 48, a = rp / (e - 1), b = a * Math.sqrt(e * e - 1);
    const rotA = -Math.PI / 2;
    const rot = ([x, y]) => [Pl[0] + x * Math.cos(rotA) - y * Math.sin(rotA), Pl[1] + x * Math.sin(rotA) + y * Math.cos(rotA)];
    const at = (F) => rot([a * (e - Math.cosh(F)), b * Math.sinh(F)]);
    const Fmax = 2.05;
    const Mmax = e * Math.sinh(Fmax) - Fmax;
    const solveF = (M) => {
      let F = Math.asinh(M / e);
      for (let i = 0; i < 30; i++) F -= (e * Math.sinh(F) - F - M) / (e * Math.cosh(F) - 1);
      return F;
    };
    // planet with bands
    const pc = k.clip(P.circle(Pl[0], Pl[1], Rp));
    k.body(null, Pl[0], Pl[1], Rp, 'o');
    if (!k.mark) {
      let bands = '';
      for (const y of [-18, -9, -2, 7, 15, 22]) bands += P.line(Pl[0] - Rp, Pl[1] + y, Pl[0] + Rp, Pl[1] + y);
      k.path(null, bands, 'h', { 'clip-path': pc.ref });
    }
    // asymptotes through the hyperbola's centre
    const Ch = rot([a * e, 0]);
    const dirIn = [-1, -Math.sqrt(e * e - 1)], dirOut = [-1, Math.sqrt(e * e - 1)];
    const rv = ([x, y]) => { const l = Math.hypot(x, y); return [(x * Math.cos(rotA) - y * Math.sin(rotA)) / l, (x * Math.sin(rotA) + y * Math.cos(rotA)) / l]; };
    const ui = rv(dirIn), uo = rv(dirOut);
    if (!k.mark) {
      k.path(null, P.line(Ch[0], Ch[1], Ch[0] + ui[0] * 330, Ch[1] + ui[1] * 330) + P.line(Ch[0], Ch[1], Ch[0] + uo[0] * 330, Ch[1] + uo[1] * 330), 'c');
      k.path(null, P.line(Ch[0], Ch[1], Ch[0] - ui[0] * 70, Ch[1] - ui[1] * 70), 'c');
    }
    // the whole path, dotted, then the flown part solid
    const full = [];
    for (let i = 0; i <= 120; i++) full.push(at(-Fmax + (2 * Fmax * i) / 120));
    k.path(null, P.poly(full), 'dt');
    const trail = k.path(null, '', 'm a');
    const probe = k.g();
    k.path(probe, 'M-5 -3.5L6 0L-5 3.5Z', 'fa');
    const peri = at(0);
    if (!k.mark) k.path(null, P.line(Pl[0], Pl[1], peri[0], peri[1]), 'dm');
    // inset: heliocentric velocity sum
    let inset = null;
    if (!k.mark) {
      const O = [346, 84];
      const vin = [ui[0] * -38, ui[1] * -38], vout = [uo[0] * 38, uo[1] * 38];
      // planet velocity leaning toward the change in v∞: the assist adds speed
      const dv = [vout[0] - vin[0], vout[1] - vin[1]];
      const ang = Math.atan2(dv[1], dv[0]) + 1.05;
      const vp = [Math.cos(ang) * 62, Math.sin(ang) * 62];
      inset = k.g();
      const arrow = (p0, v, cls, marker) => k.path(inset, P.line(p0[0], p0[1], p0[0] + v[0], p0[1] + v[1]), cls, { 'marker-end': marker });
      arrow(O, vp, 'f', 'url(#ar)');
      const tipP = [O[0] + vp[0], O[1] + vp[1]];
      arrow(tipP, vin, 'hd', 'url(#ar)');
      arrow(tipP, vout, 'm a', 'url(#aa)');
      arrow(O, [vp[0] + vin[0], vp[1] + vin[1]], 'f', 'url(#ar)');
      arrow(O, [vp[0] + vout[0], vp[1] + vout[1]], 'o a', 'url(#aa)');
      if (k.L) {
        k.text(inset, O[0] - 4, O[1] - 44, 'Heliocentric', 't ts');
        const g = Math.hypot(vp[0] + vout[0], vp[1] + vout[1]) / Math.hypot(vp[0] + vin[0], vp[1] + vin[1]);
        k.text(inset, O[0] - 4, O[1] - 31, `Exit speed ×${g.toFixed(2)}`, 't ts tn q');
      }
    }
    let lab = null;
    if (k.L) {
      const a0 = Math.atan2(-ui[1], -ui[0]);
      let a1 = Math.atan2(uo[1], uo[0]);
      while (a1 - a0 > Math.PI) a1 -= TAU;
      while (a1 - a0 < -Math.PI) a1 += TAU;
      k.dimAngle(null, Ch[0], Ch[1], 46, Math.min(a0, a1), Math.max(a0, a1), `δ ${(2 * Math.asin(1 / e) * R2D).toFixed(1)}°`, { textCls: 't ts tn' });
      k.leader(null, peri, [peri[0] - 70, peri[1] - 40], 'Periapsis', { side: 'l' });
      k.text(null, Pl[0], Pl[1] + Rp + 16, 'Planet', 't ts', 'middle');
      lab = { vi: k.text(null, 0, 0, 'v∞ in', 't ts tn'), vo: k.text(null, 0, 0, 'v∞ out', 't ts tn ta') };
      const s0 = at(-1.35), s1 = at(1.35);
      lab.vi.setAttribute('x', n2(clamp(s0[0] + 12, 20, 440))); lab.vi.setAttribute('y', n2(clamp(s0[1], 20, 330)));
      lab.vo.setAttribute('x', n2(clamp(s1[0] + 12, 20, 440))); lab.vo.setAttribute('y', n2(clamp(s1[1], 20, 330)));
      k.text(null, 14, 346, `e = ${e.toFixed(2)}`, 't tt tn');
    }
    return (p) => {
      const q = seg(p, 0.04, 0.86);
      const M = lerp(-Mmax, Mmax, q);
      const F = solveF(M);
      const pos = at(F), nxt = at(F + 0.01);
      k.move(probe, pos[0], pos[1], Math.atan2(nxt[1] - pos[1], nxt[0] - pos[0]));
      const pts = [];
      for (let i = 0; i <= 80; i++) {
        const f = -Fmax + ((F + Fmax) * i) / 80;
        pts.push(at(f));
      }
      k.set(trail, P.poly(pts));
      const vis = Math.min(seg(p, 0, 0.04), 1 - seg(p, 0.9, 0.98));
      k.fade(trail, vis);
      k.fade(probe, vis);
      if (inset) k.fade(inset, seg(p, 0.55, 0.65) * (1 - seg(p, 0.9, 0.98)));
      void lab;
    };
  },
});

})();
